// The in-memory API's chat routes (M2-06): spaces, channels, topics, messages, DMs, reactions,
// pins, bookmarks, read state, the caller's channel settings and follows, agent rooms (take over
// and release), a channel's agent rules, and the few work and agents routes the chat screens
// read (a board list, task cards, approvals, runs, presence). The rules are the contracts' (04,
// 06): a topic name is unique per channel (creating an existing one returns it), `client_id` is
// idempotent forever, edits close after 15 minutes, a delete is a tombstone, read positions
// never move backwards, a DM is one channel per member set, and agents stop posting while a
// person has taken their room over. Acme carries the demo: a recruiting team with two digital
// employees (Riley, Sam) in its channels, an agent room, live task and approval cards.
import {
  bodyTextOf,
  categoryOf,
  deRingState,
  dmClosed,
  dmQuiet,
  firstLine,
  markdownToBlocks,
  oneSentence,
} from "@companyos/domain-work/chat";
import {
  cleanFileName,
  contentDisposition,
  previewOf,
  servedType,
  sniffMime,
  thumbable,
} from "@companyos/domain-work/files";
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import type { Ctx, Handler, Principal } from "./fake";
import { NO_EXECUTION } from "./fake-approval-execution";
import {
  COMPANY_IDS,
  crewMembershipIdOf,
  DE_IDS,
  type FakeDe,
  SCREEN_ASK_ID,
  uid,
} from "./fake-data";
import { inboxMatches, textMatches } from "./fake-merge";
import { FIRST_ATTEMPT } from "./fake-runs";
import { applyShares, type FakeShare, shareProblem } from "./fake-shares";
import type { WorkBridge } from "./fake-work";
import type { MeResponse } from "./types";

type Space = OperationOutput<"spaces.get">;
type Channel = OperationOutput<"channels.get">;
/** A stored topic: the row without what the routes add per caller (CHAT-TOPICS-SEPARATE). */
type Topic = Omit<
  OperationOutput<"topics.get">,
  "thread_id" | "is_main" | "is_member" | "is_opener"
>;
type TopicMemberRow = OperationOutput<"topics.members">["data"][number];
/** CHAT-TOPICS-SEPARATE: a channel's own conversation (and a DM's one conversation). */
const MAIN_TOPIC = "(no topic)";
const MAIN_FIXED = "the channel's own conversation is not a topic to change";
type Thread = OperationOutput<"threads.get">;
/** A stored message: the row without the wake results the routes add (AGENT-MENTION). */
type Message = Omit<OperationOutput<"messages.create">, "agent_wakes" | "attachments">;
type AgentWake = OperationOutput<"messages.wake">;
type Readiness = OperationOutput<"threads.agents">["data"][number];
type Reaction = OperationOutput<"messages.react">;
type ChannelMember = OperationOutput<"channels.members">["data"][number];
type ChannelPrefs = OperationOutput<"channels.setPrefs">;
type AgentRules = OperationOutput<"channels.agentRules">;
type Follow = OperationOutput<"follows.put">;
type Bookmark = OperationOutput<"bookmarks.list">["data"][number];
type Board = OperationOutput<"boards.get">;
type TaskDetail = OperationOutput<"tasks.get">;
type Approval = OperationOutput<"approvals.approve">;
type Run = OperationOutput<"runs.list">["data"][number];
type Peer = OperationOutput<"presence.get">["peers"][number];
/** CHAT-FILES: a chat file (its view, its bytes, and whether its message took it away). */
type ChatFileView = OperationOutput<"attachments.get">;
type ChatFileRow = { view: ChatFileView; bytes: Uint8Array; gone: boolean };
type ChannelFile = OperationOutput<"channels.files">["data"][number];
type InboxItem = OperationOutput<"inbox.list">["data"][number];
type RoomView = OperationOutput<"agentRooms.get">;
type Room = RoomView["room"];
type ParticipantView = RoomView["participants"][number];
type Participant = Omit<
  ParticipantView,
  "display_name" | "member_kind" | "badge" | "is_turn" | "run"
>;
type StatusAsk = OperationOutput<"statusAsks.answer">;
type RoomTurn = OperationOutput<"agentRooms.turn">;
type SearchHit = OperationOutput<"search.query">["data"][number];
type DePresence = OperationOutput<"dePresence.list">["data"][number];

/** CHAT-PRESENCE: what the screens twin tells the DE rings (fake-screens.ts `presenceOf`). */
export type ScreensPresence = {
  asks: {
    agent_membership_id: string;
    owner_membership_id: string | null;
    lease_id: string;
    why: string;
    reason: string | null;
    created_at: string;
  }[];
  leases: { id: string; holder_membership_id: string }[];
};

/** A person's crew as chat sees it (M4-03): who posts, for whom, and where `#brief` is. */
export type CrewChatRef = {
  companyId: string;
  ownerId: string;
  ownerName: string;
  /** The crew's `personal_agent` membership (the author) and its lead crew agent. */
  crewId: string;
  crewAgentId: string;
  crewName: string;
  spaceId: string;
  channelId: string;
};

/** What the crews twin writes through the chat twin (posts are the crew's, as its agent). */
export type CrewChatBridge = {
  ensureBrief(c: CrewChatRef): void;
  /** The day's brief in `#brief` under `topicName`; a `clientId` already used returns that one. */
  postBrief(
    c: CrewChatRef,
    topicName: string,
    markdown: string,
    clientId: string,
  ): { message_id: string; thread_id: string; channel_id: string };
  /** The thread of an inbox item's subject (a topic's), or null. */
  threadOf(companyId: string, subjectKind: string, subjectId: string): string | null;
  /** A drafted reply posted in `threadId`; null when there is no such thread. */
  postReply(c: CrewChatRef, threadId: string, markdown: string, clientId: string): string | null;
};

/** MEETINGS: what the cadence twin (fake-cadence.ts) reads and writes through the chat twin. */
export type MeetingsChatBridge = {
  /** The channel a team meeting's summary posts to by default. */
  teamChannel(companyId: string): { id: string; name: string } | null;
  channel(companyId: string, channelId: string): { id: string; name: string } | null;
  /** A person's message in the channel (an issue raised from chat points at it). */
  firstMessage(companyId: string, channelId: string): { id: string; text: string } | null;
  /** A system message in the channel's topic `topicName` (made when missing). */
  postSummary(
    companyId: string,
    channelId: string,
    topicName: string,
    markdown: string,
    at?: string,
  ): { message_id: string; topic_id: string; channel_id: string } | null;
};

/** Chat routes the fake serves (the contract's `feature: chat`; fake-contract.test.ts checks). */
export const FAKE_CHAT_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  // CHAT-FILES (the people's routes; the agent tools are the API's alone)
  "attachments.upload",
  "attachments.discard",
  "attachments.get",
  "attachments.content",
  "attachments.save",
  "channels.list",
  "channels.create",
  // MULTI-SPACE-ACCESS
  "channels.shares.list",
  "channels.shares.set",
  "channels.get",
  // CHAT-SWITCH-A: the channel's Files tab.
  "channels.files",
  "channels.update",
  "channels.archive",
  "channels.unarchive",
  "channels.delete",
  "channels.updateMember",
  "threads.mine",
  "threads.readAll",
  "threads.read",
  "threads.follow",
  "channels.topics",
  "channels.createTopic",
  "channels.members",
  "channels.addMember",
  "channels.removeMember",
  "channels.takeOver",
  "channels.release",
  "channels.read",
  "channels.join",
  "channels.leave",
  "channels.subscription",
  "topics.subscription",
  "channels.myPrefs",
  "channels.setPrefs",
  "channels.agentRules",
  "channels.setAgentRules",
  "topics.get",
  "topics.rename",
  "topics.resolve",
  "topics.pin",
  "topics.archive",
  "topics.restore",
  "topics.delete",
  "topics.move",
  "topics.takeOver",
  "topics.release",
  // CHAT-TOPICS-SEPARATE: a topic's members (join, leave, add, remove) and its privacy.
  "topics.members",
  "topics.updateMembers",
  "topics.privacy",
  "threads.get",
  "messages.list",
  "messages.create",
  // AGENT-MENTION: ask again for an agent a message named; the agents a mention reaches here.
  "messages.wake",
  "threads.agents",
  // CHAT-THREAD-AGENTS: "Leave thread" on an agent's chip.
  "threads.leaveAgent",
  "messages.update",
  "messages.delete",
  "messages.react",
  "messages.unreact",
  "messages.pin",
  "messages.makeTask",
  "messages.bookmark",
  // W1-FIXES: served by fake-palette.ts beside the person's own reply style
  "replyStyle.setFromMessage",
  "bookmarks.list",
  "dms.open",
  "topics.split",
  // CHAT-SWITCH-A: reminders ("Remind me about this", `/remind`).
  "chatReminders.list",
  "chatReminders.create",
  "chatReminders.dismiss",
  // CHAT-SWITCH-A: presence, custom status, time zone, do-not-disturb.
  "chatStatus.list",
  "chatStatus.set",
  // CHAT-SWITCH-B: message links, link cards (none here), Send later, Ask (refused), webhooks.
  "messages.locate",
  "messages.previews",
  "scheduledMessages.list",
  "scheduledMessages.create",
  "scheduledMessages.update",
  "scheduledMessages.cancel",
  "chatAsks.create",
  "chatAsks.get",
  "chatWebhooks.list",
  "chatWebhooks.create",
  "chatWebhooks.listForDe",
  "chatWebhooks.createForDe",
  "chatWebhooks.rotate",
  "chatWebhooks.revoke",
  // CHAT-PRESENCE: the DE rings, closing DMs.
  "dePresence.list",
  "dePresence.ack",
  "dms.close",
  "dms.cleanup",
]);

/** Routes behind the `agents` add-on that chat reads (approval cards, cost chips, status line). */
export const FAKE_AGENTS_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "approvals.get",
  "approvals.approve",
  "approvals.reject",
  "approvals.edit",
  "runs.list",
  "runs.get",
  "runs.cancel",
  // M4-05: agent rooms (participants, turns, badges) and status asks.
  "agentRooms.list",
  "agentRooms.create",
  "agentRooms.get",
  "agentRooms.update",
  "agentRooms.delete",
  "agentRooms.addParticipant",
  "agentRooms.removeParticipant",
  "agentRooms.join",
  "agentRooms.leave",
  "agentRooms.turn",
  "agentRooms.pass",
  "statusAsks.list",
  "statusAsks.create",
  "statusAsks.answer",
  "statusAsks.decline",
]);

/** The base-plan (`core`) routes of this file: spaces, follows, boards, tasks, presence. */
export const FAKE_CHAT_CORE_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "spaces.list",
  "spaces.get",
  "spaces.create",
  "spaces.update",
  "spaces.archive",
  "spaces.unarchive",
  "spaces.sidebar",
  "spaces.addMember",
  "spaces.removeMember",
  "inbox.list",
  "inbox.read",
  "inbox.done",
  "inbox.readAll",
  "inbox.snooze",
  "search.query",
  // SEARCH-NOTIF: served by fake-palette.ts (wired in fake.ts), over this twin's routes.
  "search.palette",
  "notifications.prefs",
  "notifications.setPrefs",
  "replyStyle.get",
  "replyStyle.set",
  "follows.put",
  "follows.delete",
  "follows.mine",
  "boards.list",
  "tasks.get",
  "tasks.create",
  "tasks.setStatus",
  "presence.get",
]);

/** How long a message stays editable (06 §Messages: 15 minutes, configurable later). */
export const EDIT_WINDOW_MS = 15 * 60_000;

type Deps = {
  now: () => Date;
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: never, title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  page: <T>(items: T[], url: URL) => { data: T[]; next_cursor: string | null; prev_cursor: null };
  desOf: (companyId: string) => FakeDe[];
  /**
   * AGENT-MENTION: whether the company's Agents add-on would admit a run (on, not in its grace),
   * as the API's wake-up consumers decide. Absent: on.
   */
  agentsOn?: (companyId: string) => boolean;
  /**
   * The Work twin (fake-work.ts `WorkBridge`), when there is one: the company's spaces are its
   * array (this twin's seed reuses a space of the same name rather than adding a second), and a
   * space's sidebar lists its boards and the channels it holds too.
   */
  work?: () => WorkBridge | null;
  /** M7-02: a space's live pages in sibling order (the pages twin, fake-pages.ts). */
  pagesOf?: (
    companyId: string,
    spaceId: string,
  ) => { id: string; title: string; parent_page_id: string | null }[];
  /** CHAT-FILES "Save to task": the files twin's independent copy (fake-files.ts). */
  saveChatCopy?: (
    p: Principal,
    taskId: string,
    input: { bytes: Uint8Array; name: string; mime: string; folderPath: string | null },
    provenance: {
      kind: "chat_message";
      source_attachment_id: string;
      message_id: string;
      thread_id: string;
      sender_membership_id: string;
      sender_name: string;
      sent_at: string;
      saved_by_membership_id: string;
      saved_at: string;
    },
  ) => Response;
  /** CHAT-PRESENCE: the screens twin's open asks and live screens (fake-screens.ts). */
  screens?: (companyId: string) => ScreensPresence;
};

type Store = {
  spaces: Space[];
  channels: Channel[];
  topics: Topic[];
  /** CHAT-TOPICS-SEPARATE: topic members (the API's `topic_member`); never the main topic's. */
  topicMembers: TopicMemberRow[];
  threads: Thread[];
  messages: Message[];
  reactions: Reaction[];
  members: ChannelMember[];
  prefs: Map<string, ChannelPrefs>;
  reads: Map<string, string>;
  /** THREAD-READS: `${member}|${root}` → read position and follow state in a reply thread. */
  threadReads: Map<string, ThreadReadRow>;
  follows: Follow[];
  bookmarks: Map<string, string>;
  rules: Map<string, AgentRules>;
  boards: Board[];
  tasks: TaskDetail[];
  approvals: Approval[];
  runs: Run[];
  clientIds: Map<string, string>;
  chatFiles: ChatFileRow[];
  /** Inbox item state by item id (the items themselves derive from mentions and replies). */
  inbox: Map<
    string,
    { read_at: string | null; done_at: string | null; snoozed_until: string | null }
  >;
  /** M4-05: agent rooms, their participants, status asks, and whose crew is whose. */
  rooms: Room[];
  participants: Participant[];
  asks: StatusAsk[];
  crewOwners: Map<string, string>;
  /** AGENT-MENTION: each message's wake results (the API's `wakeup_request` rows of it). */
  wakes: Map<string, AgentWake[]>;
  /** CHAT-THREAD-AGENTS: `${root}|${agent}` → when the agent was muted in that reply thread. */
  mutedAgents: Map<string, string>;
  /**
   * WF-FIX (2026-10-26): `${space}|${membership}` → the relation `spaces.addMember` granted
   * (the API's space tuples); an owner manages the space's channels.
   */
  spaceRelations: Map<string, "viewer" | "editor" | "owner">;
  /** CHAT-PRESENCE: `${member}|${channel}` → when the member closed that DM (`dm_closure`). */
  closures: Map<string, string>;
  /** CHAT-PRESENCE: failed runs someone acknowledged (`run_failure_ack`). */
  acks: Set<string>;
  /** MULTI-SPACE-ACCESS: channels shared into more spaces (`space_share`). */
  shares: FakeShare[];
};

/** THREAD-READS: a member's state in one reply thread (the API's `thread_read` row). */
type ThreadReadRow = { at: string | null; reply: string | null; following: boolean | null };

const SYSTEM = { actor_kind: "system" as const, actor_membership_id: null };
const NO_ACTOR = {
  actor_seat_id: null,
  actor_key_id: null,
  actor_run_id: null,
  actor_acting_for_membership_id: null,
  actor_identity_mode: null,
  actor_crew_agent_id: null,
  actor_policy_snapshot_id: null,
  actor_auth_version: null,
};

/** Acme's people in the demo (Maya is the owner account; Jordan leads recruiting). */
export const CHAT_PEOPLE = {
  maya: "0193a0a0-0000-7000-8000-000000000101",
  jordan: uid(0x1000, 0xa11),
} as const;
const RILEY_SEAT = uid(0x2000, 0xa01);
const JORDAN_SEAT = uid(0x2000, 0xa11);

const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/** The caller's membership in the company (agency operators act without one). */
export function membershipOf(me: MeResponse, companyId: string): string | null {
  return me.memberships.find((m) => m.company_id === companyId)?.membership_id ?? null;
}

/** A key per caller: the membership, or the agency user for operators. */
const isDmKind = (kind: string) => kind === "dm" || kind === "group_dm";

const whoKey = (me: MeResponse, companyId: string) =>
  membershipOf(me, companyId) ?? `agency:${me.user.id}`;

const asUuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

/** Plain text of a BlockNote document (mentions as "@Name"), as the server's `body_text`. */
export function blocksText(blocks: unknown): string {
  if (!Array.isArray(blocks)) return "";
  const lines: string[] = [];
  for (const block of blocks) {
    const content = (block as { content?: unknown }).content;
    if (!Array.isArray(content)) continue;
    lines.push(
      content
        .map((n: { type?: string; text?: string; props?: { name?: string } }) =>
          n.type === "mention" ? `@${n.props?.name ?? ""}` : (n.text ?? ""),
        )
        .join(""),
    );
  }
  return lines.join("\n").trim();
}

/** A one-paragraph BlockNote document of `text` (what `body_markdown` converts to here). */
export function textBlocks(text: string): Record<string, unknown>[] {
  return text.split("\n").map((line, i) => ({
    id: `b${i}`,
    type: "paragraph",
    props: {},
    content: line ? [{ type: "text", text: line, styles: {} }] : [],
    children: [],
  }));
}

export function createChatFake(deps: Deps) {
  const stores = new Map<string, Store>();
  /** CHAT-SWITCH-A: `${company}|${membership}` → the custom status and time zone set. */
  const statuses = new Map<
    string,
    {
      emoji: string | null;
      text: string | null;
      expires_at: string | null;
      time_zone: string | null;
    }
  >();
  let seq = 0x100;
  const at = (minutesAgo: number) =>
    new Date(deps.now().getTime() - minutesAgo * 60_000).toISOString();
  const stamp = () => deps.now().toISOString();
  const nextId = (group: number) => uid(group, ++seq + 0x10000);
  const forbidden = (detail: string) =>
    deps.problem(403, "forbidden" as never, "Forbidden", detail);
  const conflict = (detail: string) => deps.problem(409, "conflict" as never, "Conflict", detail);

  // Seed ---------------------------------------------------------------------------------------

  function emptyStore(): Store {
    return {
      spaces: [],
      channels: [],
      topics: [],
      topicMembers: [],
      threads: [],
      messages: [],
      reactions: [],
      members: [],
      prefs: new Map(),
      reads: new Map(),
      threadReads: new Map(),
      follows: [],
      bookmarks: new Map(),
      rules: new Map(),
      spaceRelations: new Map(),
      boards: [],
      tasks: [],
      approvals: [],
      runs: [],
      clientIds: new Map(),
      chatFiles: [],
      inbox: new Map(),
      rooms: [],
      participants: [],
      asks: [],
      crewOwners: new Map(),
      wakes: new Map(),
      mutedAgents: new Map(),
      closures: new Map(),
      acks: new Set(),
      shares: [],
    };
  }

  function seed(companyId: string): Store {
    const s = emptyStore();
    const shared = deps.work?.()?.spacesOf(companyId);
    if (shared) s.spaces = shared;
    const n = companyNo(companyId) * 0x100;
    const id = (group: number, i: number) => uid(group, n + i);
    const created = at(60 * 24 * 30);

    const space = (i: number, name: string, kind: Space["kind"], description: string): Space => {
      const same = s.spaces.find((x) => x.name === name);
      if (same) return same;
      const row: Space = {
        company_id: companyId,
        id: id(0xc100, i),
        name,
        kind,
        team_id: null,
        owner_membership_id: null,
        icon: null,
        description,
        is_private: false,
        // MEMBER-UX (migration 0044): a shared space lets members edit.
        settings: kind === "team" ? {} : { member_access: "editor" },
        // BOARD-ORDER: after Work's demo spaces, in the order made.
        position: 100 + i,
        archived_at: null,
        ...SYSTEM,
        created_at: created,
        updated_at: created,
      };
      s.spaces.push(row);
      return row;
    };
    const channel = (
      i: number,
      spaceRow: Space | null,
      name: string,
      opts: Partial<Channel> = {},
    ): Channel => {
      const row: Channel = {
        company_id: companyId,
        id: id(0xc110, i),
        space_id: spaceRow?.id ?? null,
        kind: "space",
        name,
        description: null,
        board_id: null,
        team_id: null,
        posting_policy: "everyone",
        topic_policy: "everyone",
        broadcast_policy: "everyone",
        default_notify_level: "all",
        is_private: false,
        is_agent_room: false,
        is_default: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: null,
        archived_at: null,
        ...SYSTEM,
        created_at: created,
        updated_at: created,
        ...opts,
      };
      s.channels.push(row);
      return row;
    };
    let topicNo = 0;
    const topic = (ch: Channel, name: string, opts: Partial<Topic> = {}): Topic => {
      topicNo += 1;
      const row: Topic = {
        company_id: companyId,
        id: id(0xc120, topicNo),
        channel_id: ch.id,
        name,
        status: "open",
        is_agent_room: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        pinned_at: null,
        archived_at: null,
        last_message_at: null,
        message_count: 0,
        is_private: false,
        ...SYSTEM,
        created_at: created,
        updated_at: created,
        ...opts,
      };
      s.topics.push(row);
      s.threads.push({
        company_id: companyId,
        id: id(0xc130, topicNo),
        kind: "topic",
        subject_id: row.id,
        last_message_at: null,
        message_count: 0,
        created_at: created,
        updated_at: created,
      });
      return row;
    };
    let messageNo = 0;
    const say = (
      t: Topic,
      minutesAgo: number,
      author: string | null,
      text: string,
      opts: Partial<Message> & { de?: boolean } = {},
    ): Message => {
      messageNo += 1;
      const { de, ...rest } = opts;
      const row: Message = {
        company_id: companyId,
        created_at: at(minutesAgo),
        id: id(0xc140, messageNo),
        thread_id: threadOfTopic(s, t.id)?.id ?? "",
        author_membership_id: author,
        body: textBlocks(text),
        body_text: text,
        author_kind: de ? "agent" : author ? "person" : "system",
        mentions: [],
        card_refs: [],
        parent_message_id: null,
        client_id: null,
        edited_at: null,
        deleted_at: null,
        pinned_at: null,
        ...NO_ACTOR,
        actor_kind: author === null ? "system" : de ? "digital_employee" : "person",
        actor_membership_id: author,
        ...rest,
      };
      s.messages.push(row);
      touchThread(s, row);
      return row;
    };

    // The company's default channels (CHAT-UX): every person is in them without browsing.
    const general = space(1, "General", "project", "Company-wide news and conversation.");
    const generalCh = channel(1, general, "general", {
      description: "Anything for everyone at the company.",
      is_default: true,
    });
    const announcements = channel(2, general, "announcements", {
      description: "Decisions and news, posted by managers.",
      is_default: true,
      // CHAT-CONTROLS: the admin-post-only channel of the demo.
      posting_policy: "admins",
    });
    const welcome = topic(generalCh, "Welcome");
    say(welcome, 60 * 24 * 6, null, "This channel was created with the company.");

    if (companyId !== COMPANY_IDS.acme) return s;

    // Acme: a recruiting agency with two digital employees ---------------------------------
    const riley = DE_IDS.riley;
    const sam = DE_IDS.sam;
    const { maya, jordan } = CHAT_PEOPLE;
    const recruiting = space(2, "Recruiting", "team", "The recruiting team: sourcing to offer.");
    const northwind = space(3, "Client: Northwind", "client", "Northwind Foods' searches.");
    const recruitingCh = channel(3, recruiting, "recruiting", {
      kind: "team",
      description: "Day-to-day recruiting: shortlists, screens and offers.",
    });
    const sourcing = channel(4, recruiting, "sourcing", {
      is_private: true,
      description: "Search strings and sourcing notes (private).",
    });
    const rileyDesk = channel(5, recruiting, "riley-desk", {
      kind: "agent_room",
      is_agent_room: true,
      description: "Riley works the Northwind controller search here.",
    });
    const pipelineCh = channel(6, recruiting, "pipeline", { kind: "board_home" });
    const clientCh = channel(7, northwind, "client-northwind", {
      description: "Shared with Northwind's hiring manager.",
    });
    const dmJordan = channel(8, null, "Jordan Lee", {
      kind: "dm",
      dm_key: [maya, jordan].sort().join(","),
    });
    const dmRiley = channel(9, null, "Riley", {
      kind: "dm",
      dm_key: [maya, riley].sort().join(","),
    });
    for (const [ch, who] of [
      [sourcing, [maya, jordan, sam]],
      [dmJordan, [maya, jordan]],
      [dmRiley, [maya, riley]],
    ] as const)
      for (const m of who) s.members.push(member(companyId, ch.id, m, created));

    // The board behind #pipeline, and two tasks the conversation refers to.
    const board: Board = {
      company_id: companyId,
      id: id(0xc150, 1),
      space_id: recruiting.id,
      name: "Pipeline",
      home_channel_id: pipelineCh.id,
      status_set: [
        { key: "todo", name: "To do", done: false },
        { key: "in_progress", name: "In progress", done: false, started: true },
        { key: "review", name: "Review", done: false, started: true },
        { key: "done", name: "Done", done: true },
      ],
      settings: { live_cards: true },
      digest_interval: "daily",
      position: 0,
      color: null,
      lead_seat_id: null,
      page_id: null,
      archived_at: null,
      version: 1,
      ...SYSTEM,
      created_at: created,
      updated_at: created,
    };
    pipelineCh.board_id = board.id;
    s.boards.push(board);
    const screen = task(s, board, 12, "Screen the five shortlisted candidates", "in_progress", {
      seat: RILEY_SEAT,
      due: at(-60 * 24 * 2),
      checklist: [
        ["Chen Wu", true],
        ["Ana Ortiz", true],
        ["Bo Park", false],
        ["Dee Rao", false],
        ["Eli Novak", false],
      ],
    });
    task(s, board, 13, "Send the offer to Chen Wu", "todo", { seat: JORDAN_SEAT });

    // Runs: two finished turns with their cost, one awaiting approval, one working now.
    const run = (
      i: number,
      state: Run["state"],
      minutesAgo: number,
      ref: { kind: "message" | "topic"; id: string },
      cost: number,
      tokens: [number, number],
      summary: string | null,
    ): Run => {
      const row: Run = {
        company_id: companyId,
        id: id(0xc180, i),
        membership_id: riley,
        crew_agent_id: null,
        seat_id: RILEY_SEAT,
        agent_profile_id: null,
        owner_membership_id: null,
        identity_mode: "as_agent",
        trigger_kind: ref.kind === "message" ? "mention" : "heartbeat",
        trigger_ref: ref,
        wakeup_ids: [],
        state,
        state_reason: state === "awaiting_approval" ? "offer email needs Jordan's approval" : null,
        started_at: at(minutesAgo),
        ended_at: state === "completed" ? at(minutesAgo - 4) : null,
        parent_run_id: null,
        delegation_depth: 0,
        budget_reserved_micros: 250_000,
        cost_micros: cost,
        tokens_in: tokens[0],
        tokens_out: tokens[1],
        model: "claude-sonnet",
        sandbox_kind: null,
        skill_pack_versions: {},
        summary,
        hand_back: null,
        lease_owner: null,
        lease_until: null,
        verdict: null,
        verdict_evidence_ref: null,
        output_ref: null,
        model_path: "gateway",
        model_path_fallback_used: false,
        model_credential_id: null,
        context_refs: null,
        bundle_sha: null,
        ...FIRST_ATTEMPT,
        task_id: null,
        actor_kind: "digital_employee",
        actor_membership_id: riley,
        created_at: at(minutesAgo),
        updated_at: at(Math.max(0, minutesAgo - 4)),
      };
      s.runs.push(row);
      return row;
    };

    // #recruiting › W43 shortlist: a mention wakes Riley, who answers with a live card.
    const shortlist = topic(recruitingCh, "W43 shortlist");
    const ask = say(
      shortlist,
      130,
      jordan,
      "@Riley can you pull the five strongest candidates for the Northwind controller role?",
      { mentions: [`de:${riley}`] },
    );
    const r1 = run(
      1,
      "completed",
      128,
      { kind: "message", id: ask.id },
      42_300,
      [8_120, 1_240],
      "Ranked the shortlist.",
    );
    s.wakes.set(ask.id, [
      {
        membership_id: riley,
        name: deps.desOf(companyId).find((d) => d.membership_id === riley)?.display_name ?? "Riley",
        agent_kind: "digital_employee",
        state: "done",
        reason: "Riley finished.",
        run_id: r1.id,
        requested_at: ask.created_at,
      },
    ]);
    const answer = say(
      shortlist,
      122,
      riley,
      "Ranked by fit: Chen Wu, Ana Ortiz, Bo Park, Dee Rao and Eli Novak. Notes are on each card, and screening is on the board.",
      {
        de: true,
        card_refs: [`task:${screen.id}`],
        actor_run_id: r1.id,
        actor_seat_id: RILEY_SEAT,
      },
    );
    s.reactions.push(reaction(companyId, answer.id, jordan, "👍", at(120)));
    s.reactions.push(reaction(companyId, answer.id, maya, "👍", at(119)));
    s.reactions.push(reaction(companyId, answer.id, maya, "✅", at(119)));
    say(shortlist, 100, maya, "Start with Chen, the client asked about her twice.", {
      parent_message_id: answer.id,
    });
    const r2 = run(
      2,
      "completed",
      97,
      { kind: "message", id: answer.id },
      9_800,
      [2_400, 310],
      "Booked the screen.",
    );
    say(shortlist, 95, riley, "Booked Chen for Thursday at 10:00 with Jordan.", {
      de: true,
      // DE-REPLY-STYLE: the reply links its task (a chip), never names it in the text.
      card_refs: [`task_link:${screen.id}`],
      parent_message_id: answer.id,
      actor_run_id: r2.id,
      actor_seat_id: RILEY_SEAT,
    });
    say(shortlist, 90, maya, "Good. Once the screen is done, let's get Chen an offer.", {
      pinned_at: at(89),
    });
    // CHAT-CONTROLS: a reply after Maya's read position: her Threads view shows it unread.
    say(shortlist, 20, jordan, "Chen confirmed for Thursday; sending the brief tonight.", {
      parent_message_id: answer.id,
    });
    const r3 = run(
      3,
      "awaiting_approval",
      30,
      { kind: "topic", id: shortlist.id },
      18_600,
      [4_050, 820],
      null,
    );
    const approval: Approval = {
      company_id: companyId,
      id: id(0xc170, 1),
      kind: "external_action",
      subject_kind: "message",
      subject_id: id(0xc140, 99),
      requested_by_seat_id: RILEY_SEAT,
      requested_by_membership_id: riley,
      approver_seat_id: JORDAN_SEAT,
      run_id: r3.id,
      proposal: {
        title: "Send the offer email to Chen Wu",
        summary: "Base $96,000, start 1 December, the standard controller offer template.",
        change: { to: "chen.wu@example.com", subject: "Your offer from Northwind Foods" },
      },
      state: "pending",
      approved_payload: null,
      decided_at: null,
      decided_by_membership_id: null,
      reason: null,
      thread_id: null,
      expires_at: at(-60 * 24),
      executed_at: null,
      ...NO_EXECUTION,
      actor_kind: "digital_employee",
      actor_membership_id: riley,
      created_at: at(25),
      updated_at: at(25),
    };
    s.approvals.push(approval);
    say(
      shortlist,
      25,
      riley,
      "I drafted the offer email to Chen. It goes out once it is approved.",
      {
        de: true,
        card_refs: [`approval:${approval.id}`],
        actor_run_id: r3.id,
        actor_seat_id: RILEY_SEAT,
      },
    );
    say(shortlist, 12, jordan, "I'll check it against the comp band first.");
    say(shortlist, 6, maya, "Thanks. Flag anything above the band.");

    // #recruiting › Interview loop: Sam (the sourcing DE), a reply, a deleted message.
    const loop = topic(recruitingCh, "Interview loop", { pinned_at: created });
    say(
      loop,
      60 * 26,
      sam,
      "Interview slots for next week are on the shared calendar: 14 in total.",
      {
        de: true,
        actor_seat_id: uid(0x2000, 0xa02),
      },
    );
    const slots = s.messages.at(-1) as Message;
    // CHAT-CONTROLS: a thread under Sam's message (the Threads view lists it for Maya).
    say(loop, 60 * 25, jordan, "Thanks Sam. Keep Friday afternoons free for client calls.", {
      parent_message_id: slots.id,
    });
    say(loop, 60 * 25 + 5, maya, "And no slots before 9:00, please.", {
      parent_message_id: slots.id,
    });
    say(loop, 60 * 25 + 8, sam, "Done: Fridays after 13:00 and early mornings are blocked.", {
      de: true,
      parent_message_id: slots.id,
      actor_seat_id: uid(0x2000, 0xa02),
    });
    say(loop, 60 * 24, maya, "", { body: [], body_text: "", deleted_at: at(60 * 24 - 2) });
    const bonus = topic(recruitingCh, "Referral bonus", { status: "resolved" });
    say(bonus, 60 * 24 * 4, maya, "Referral bonus is now $1,500 for any placed controller.");
    say(bonus, 60 * 24 * 4 - 30, jordan, "Shared it with the team.");

    // #sourcing (private), #riley-desk (agent room, Riley at work), #pipeline (digest).
    const strings = topic(sourcing, "Search strings");
    say(
      strings,
      60 * 30,
      sam,
      'Controller, Chicago: ("financial controller" OR "group controller") AND CPA, 8+ years.',
      {
        de: true,
      },
    );
    const search = topic(rileyDesk, "Northwind controller search", { is_agent_room: true });
    say(
      search,
      60 * 3,
      riley,
      "Starting the Northwind controller search: 42 profiles in the first pass.",
      {
        de: true,
      },
    );
    say(
      search,
      60 * 2,
      riley,
      "Narrowed to 11 who match the CPA and the eight years; reaching out to the top five.",
      {
        de: true,
      },
    );
    say(
      search,
      20,
      riley,
      "Three replies so far. Chen Wu and Ana Ortiz are keen; Bo Park wants a call first.",
      {
        de: true,
      },
    );
    // CHAT-PRESENCE: the live run works the screening task (Riley's ring: working on it).
    run(4, "running", 1, { kind: "topic", id: search.id }, 3_100, [1_900, 120], null).task_id =
      screen.id;
    const digest = topic(pipelineCh, "Digest");
    say(digest, 60 * 9, null, "Yesterday on Pipeline: 4 tasks moved to Done, 1 overdue, 2 new.");

    // #general, #announcements, the client channel, DMs.
    const hours = topic(generalCh, "Office hours");
    const moved = say(hours, 60 * 22, maya, "Office hours move to Thursdays 15:00 from next week.");
    say(hours, 60 * 21, jordan, "Works for me.", { parent_message_id: moved.id });
    say(hours, 60 * 20, riley, "Noted. I'll keep Thursday afternoons clear of screens.", {
      de: true,
      parent_message_id: moved.id,
      actor_seat_id: RILEY_SEAT,
    });
    // CHAT-CONTROLS: an archived channel (restorable from Archived channels).
    const offsite = channel(10, general, "offsite-2025", {
      description: "Planning for the 2025 offsite.",
      archived_at: at(60 * 24 * 20),
    });
    const venue = topic(offsite, "Venue");
    say(venue, 60 * 24 * 30, jordan, "Booked the lakeside lodge for the 14th and 15th.");
    say(venue, 60 * 24 * 29, maya, "Great. Closing this channel once the photos are up.");
    const plan = topic(announcements, "Q4 hiring plan");
    say(
      plan,
      60 * 48,
      maya,
      "Q4 plan: two controllers and one AP lead for Northwind, plus our own sourcer.",
      {
        pinned_at: at(60 * 47),
      },
    );
    const kickoff = topic(clientCh, "Kickoff");
    say(
      kickoff,
      60 * 50,
      jordan,
      "Kickoff notes: Northwind wants a controller in seat by mid-January.",
    );
    const dmJ = topic(dmJordan, "(no topic)");
    say(dmJ, 30, jordan, "Got a minute before the Northwind call?");
    const dmR = topic(dmRiley, "(no topic)");
    say(dmR, 60 * 5, maya, "Riley, what's on your plate today?");
    say(dmR, 60 * 5 - 1, riley, "Three screens and the Northwind offer draft.", { de: true });

    // Maya's read positions: the shortlist's last three, the agent room's last two and
    // Jordan's DM are unread; everything else is read.
    const lastBefore = (ch: Channel, minutesAgo: number) =>
      s.reads.set(`${maya}|${ch.id}`, at(minutesAgo));
    for (const ch of s.channels) lastBefore(ch, 0);
    lastBefore(recruitingCh, 26);
    lastBefore(rileyDesk, 60 * 2 + 1);
    lastBefore(dmJordan, 31);

    // Maya's settings: everything joined but #client-northwind; #announcements muted.
    for (const ch of s.channels) {
      if (ch.id === clientCh.id) continue;
      s.prefs.set(`${maya}|${ch.id}`, {
        channel_id: ch.id,
        joined: true,
        muted: ch.id === announcements.id,
        notify: ch.id === recruitingCh.id ? "all" : "mentions",
        updated_at: created,
      });
    }
    s.follows.push(follow(companyId, maya, "topic", shortlist.id, "all", created));
    s.bookmarks.set(`${maya}|${answer.id}`, at(118));
    // THREAD-READS: the seeded replies as posts would leave them (each author read up to its own
    // reply), so Maya has new replies in the shortlist and office-hours threads.
    for (const m of [...s.messages].sort((a, b) => a.created_at.localeCompare(b.created_at)))
      if (m.parent_message_id || m.mentions.length) noteThreadPost(s, m);
    // She has read the interview-slots thread to the end: it sits under "Earlier".
    const slotsLast = liveReplies(s, slots.id).at(-1);
    if (slotsLast) moveThreadRead(s, maya, slots.id, slotsLast);
    s.rules.set(recruitingCh.id, {
      channel_id: recruitingCh.id,
      wake: "mentions",
      initiate: "allowed",
      instructions:
        "Quote candidate names as on the board. Never share salary bands outside #recruiting.",
      updated_at: created,
    });
    s.rules.set(rileyDesk.id, {
      channel_id: rileyDesk.id,
      wake: "topic",
      initiate: "allowed",
      instructions: null,
      updated_at: created,
    });
    // M4-05: #riley-desk is a room with turns: Riley (DE) and Jordan's agent (crew); Riley's
    // turn. Riley asked Maya's agent for a task's status; her sharing policy sent it to her.
    const jordanCrew = crewMembershipIdOf(companyId, jordan);
    const mayaCrew = crewMembershipIdOf(companyId, maya);
    s.crewOwners.set(jordanCrew, jordan);
    s.crewOwners.set(mayaCrew, maya);
    const room: Room = {
      company_id: companyId,
      id: id(0xc1b0, 1),
      channel_id: rileyDesk.id,
      topic_id: null,
      turn_policy: "round_robin",
      window_size: 20,
      max_agent_turns: 8,
      current_turn_membership_id: riley,
      turn_seq: 3,
      turn_started_at: at(20),
      agent_turns_since_human: 2,
      stalled_at: null,
      ...SYSTEM,
      created_at: created,
      updated_at: at(20),
    };
    s.rooms.push(room);
    s.participants.push(
      participant(room, riley, "agent", 0, created),
      participant(room, jordanCrew, "agent", 1, created),
    );
    const askedTask = s.tasks[0];
    if (askedTask)
      s.asks.push({
        company_id: companyId,
        id: id(0xc1b1, 1),
        room_id: room.id,
        asker_membership_id: riley,
        asker_run_id: null,
        target_membership_id: mayaCrew,
        subject_kind: "task",
        subject_id: askedTask.id,
        question: "Is the Northwind offer letter out yet?",
        rule: "never",
        state: "escalated",
        escalation: "refused",
        escalated_to_membership_id: maya,
        escalated_at: at(15),
        answer: null,
        answered_by_membership_id: null,
        answered_at: null,
        deadline_at: at(-15),
        actor_kind: "digital_employee",
        actor_membership_id: riley,
        created_at: at(15),
        updated_at: at(15),
      });
    // CHAT-TOPICS-SEPARATE: messages posted to the channels themselves (their own conversation,
    // apart from their topics), read already.
    const generalMain = topic(generalCh, MAIN_TOPIC);
    say(generalMain, 60 * 24 * 3, jordan, "Reminder: the office is closed on Friday afternoon.");
    say(generalMain, 60 * 24 * 3 - 5, maya, "Thanks Jordan, noted.");
    // Maya watches Riley's search: a member of the room's topic, from her read position there.
    s.topicMembers.push({
      company_id: companyId,
      topic_id: search.id,
      membership_id: maya,
      via: "added",
      last_read_message_id: null,
      last_read_at: s.reads.get(`${maya}|${rileyDesk.id}`) ?? null,
      ...SYSTEM,
      created_at: created,
      updated_at: created,
    });
    const recruitingMain = topic(recruitingCh, MAIN_TOPIC);
    say(
      recruitingMain,
      60 * 24 * 2,
      maya,
      "Shortlists live in their topics; channel-wide notes here.",
    );
    // SCREENS-NEEDS-YOU: Sam hit LinkedIn's login wall while shortlisting; the system posts the
    // ask's card where the work came from (the screens twin serves `screens.ask`).
    say(shortlist, 3, null, "Sam needs you on a screen.", {
      card_refs: [`screen_ask:${SCREEN_ASK_ID}`],
    });
    return s;
  }

  /**
   * CHAT-TOPICS-SEPARATE (the migration's backfill): each topic of a channel (not a DM's, not the
   * channel's own) gets as members its opener, everyone who posted in it and every person or
   * agent mentioned in it, each starting at its read position in the channel.
   */
  function backfillTopicMembers(s: Store) {
    for (const t of s.topics) {
      const ch = channelOfTopic(s, t);
      if (!ch || channelScoped(t, ch)) continue;
      const th = threadOfTopic(s, t.id);
      const said = th ? s.messages.filter((m) => m.thread_id === th.id) : [];
      const ids = [
        ...(t.actor_membership_id ? [t.actor_membership_id] : []),
        ...said.flatMap((m) => (m.author_membership_id ? [m.author_membership_id] : [])),
        ...said.flatMap((m) => (m.deleted_at ? [] : mentionedIds(m.mentions))),
      ];
      for (const id of new Set(ids)) {
        if (s.topicMembers.some((r) => r.topic_id === t.id && r.membership_id === id)) continue;
        s.topicMembers.push({
          company_id: t.company_id,
          topic_id: t.id,
          membership_id: id,
          via: id === t.actor_membership_id ? "creator" : "backfill",
          last_read_message_id: null,
          last_read_at: s.reads.get(`${id}|${ch.id}`) ?? null,
          ...SYSTEM,
          created_at: t.created_at,
          updated_at: t.created_at,
        });
      }
    }
  }

  /**
   * CHAT-SWITCH-A: each person's reminders, `${company}|${member}` → rows (the API's `reminder`
   * rows with a `membership_id`). None to start with (no sample data).
   */
  const reminders = new Map<
    string,
    {
      id: string;
      at: string;
      text: string;
      thread_id: string | null;
      message_id: string | null;
      dismissed: boolean;
      created_at: string;
    }[]
  >();
  type ReminderRow = NonNullable<ReturnType<typeof reminders.get>>[number];

  /** CHAT-SWITCH-B: a scheduled message by `${member}|${client_id}` (a resent Schedule). */
  const scheduledByClient = new Map<string, unknown>();
  /** CHAT-SWITCH-B: each person's scheduled messages, `${company}|${member}` → rows (none seeded). */
  const scheduled = new Map<
    string,
    {
      id: string;
      thread_id: string;
      channel_id: string;
      topic_id: string | null;
      parent_message_id: string | null;
      body: Record<string, unknown>[];
      body_text: string;
      send_at: string;
      state: "scheduled" | "sent" | "cancelled" | "failed";
      message_id: string | null;
      failure: string | null;
      created_at: string;
    }[]
  >();
  /** CHAT-SWITCH-B: incoming webhooks (none seeded); ids are unique across companies. */
  const hooks: {
    id: string;
    target: "channel" | "de";
    channel_id: string | null;
    de_membership_id: string | null;
    name: string;
    token_hint: string;
    created_by_membership_id: string;
    post_count: number;
    last_used_at: string | null;
    rotated_at: string | null;
    revoked_at: string | null;
    created_at: string;
  }[] = [];
  /** Who changes a hook: its channel's managers, or (a DE's) company admins; else the refusal. */
  function hookRefused(
    s: Store,
    w: { p: Principal; who: string },
    row: (typeof hooks)[number],
  ): Response | null {
    if (row.de_membership_id)
      return w.p.manager ? null : forbidden("Only admins manage a DE's webhooks.");
    const ch = s.channels.find((c) => c.id === row.channel_id);
    if (!ch) return deps.notFound();
    return managesChannel(s, w, ch)
      ? null
      : forbidden("Only the channel's admins manage webhooks.");
  }
  const fakeToken = () =>
    Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) =>
      b.toString(16).padStart(2, "0"),
    ).join("");

  /** A reminder as the caller sees it: its message's place only while the caller can read it. */
  function reminderView(s: Store, ctx: Ctx, r: ReminderRow) {
    const m = r.message_id ? s.messages.find((x) => x.id === r.message_id) : undefined;
    const t = m ? topicOfThread(s, m.thread_id) : undefined;
    const ch = t ? channelOfTopic(s, t) : undefined;
    const companyId = ctx.principal?.company.id ?? "";
    const readable = !!(m && !m.deleted_at && t && ch && canSee(s, ctx.me, companyId, ch));
    return {
      id: r.id,
      at: r.at,
      text: r.message_id && !readable ? "A message you can no longer open" : r.text,
      message_id: readable && m ? m.id : null,
      thread_id: readable && m ? m.thread_id : null,
      channel_id: readable && ch ? ch.id : null,
      topic_id: readable && t ? t.id : null,
      parent_message_id: readable && m ? m.parent_message_id : null,
      fired_at: null,
      created_at: r.created_at,
    };
  }

  function storeOf(companyId: string): Store {
    let s = stores.get(companyId);
    if (!s) {
      s = seed(companyId);
      backfillTopicMembers(s);
      stores.set(companyId, s);
    }
    return s;
  }

  // Row helpers --------------------------------------------------------------------------------

  function member(
    company: string,
    channel: string,
    membership: string,
    when: string,
  ): ChannelMember {
    return {
      company_id: company,
      channel_id: channel,
      membership_id: membership,
      role: "member",
      muted: false,
      notify_level: "all",
      last_read_message_id: null,
      last_read_at: null,
      ...SYSTEM,
      created_at: when,
      updated_at: when,
    };
  }

  function participant(
    room: Room,
    membership: string,
    kind: Participant["kind"],
    order: number,
    when: string,
  ): Participant {
    return {
      company_id: room.company_id,
      room_id: room.id,
      membership_id: membership,
      kind,
      turn_order: order,
      ...SYSTEM,
      created_at: when,
      updated_at: when,
    };
  }

  function reaction(
    company: string,
    message: string,
    membership: string,
    emoji: string,
    when: string,
  ): Reaction {
    return {
      company_id: company,
      message_id: message,
      membership_id: membership,
      emoji,
      created_at: when,
    };
  }

  function follow(
    company: string,
    membership: string,
    kind: Follow["subject_kind"],
    subject: string,
    level: Follow["level"],
    when: string,
  ): Follow {
    return {
      company_id: company,
      membership_id: membership,
      subject_kind: kind,
      subject_id: subject,
      level,
      actor_kind: "person",
      actor_membership_id: membership,
      created_at: when,
      updated_at: when,
    };
  }

  function task(
    s: Store,
    board: Board,
    number: number,
    title: string,
    status: string,
    o: { seat?: string; due?: string; checklist?: [string, boolean][]; message?: string } = {},
  ): TaskDetail {
    const id = uid(0xc160, number + companyNo(board.company_id) * 0x100);
    const when = at(60 * 24);
    const checklistId = uid(0xc162, number + companyNo(board.company_id) * 0x100);
    const items = (o.checklist ?? []).map(([text, done], i) => ({
      company_id: board.company_id,
      id: uid(0xc161, i + 1 + number * 0x10),
      task_id: id,
      checklist_id: checklistId,
      text,
      done_at: done ? when : null,
      done_by_seat_id: done ? (o.seat ?? null) : null,
      assignee_seat_id: null,
      due_at: null,
      position: i,
      ...SYSTEM,
      created_at: when,
      updated_at: when,
    }));
    const row: TaskDetail = {
      tag_ids: [],
      company_id: board.company_id,
      id,
      board_id: board.id,
      number,
      title,
      status,
      priority: 2,
      parent_task_id: null,
      page_id: null,
      thread_id: null,
      due_at: o.due ?? null,
      start_at: null,
      estimate_minutes: null,
      created_by_seat_id: null,
      completed_at: null,
      origin: o.message ? "message" : "manual",
      origin_ref: o.message ? { message_id: o.message } : null,
      client_id: null,
      restricted: false,
      version: 1,
      ...SYSTEM,
      created_at: when,
      updated_at: when,
      assignee_seat_ids: o.seat ? [o.seat] : [],
      fields: {},
      checklist: { done: items.filter((i) => i.done_at).length, total: items.length },
      blocked: false,
      checklists: items.length
        ? [
            {
              company_id: board.company_id,
              id: checklistId,
              task_id: id,
              title: "Checklist",
              position: 0,
              ...SYSTEM,
              created_at: when,
              updated_at: when,
            },
          ]
        : [],
      checklist_items: items,
      dependencies: [],
      depends_on_task_ids: [],
      field_values: [],
      assignees: o.seat
        ? [
            {
              company_id: board.company_id,
              task_id: id,
              seat_id: o.seat,
              ...SYSTEM,
              created_at: when,
            },
          ]
        : [],
    };
    s.tasks.push(row);
    return row;
  }

  // Reads ------------------------------------------------------------------------------------

  function threadOfTopic(s: Store, topicId: string): Thread | undefined {
    return s.threads.find((t) => t.kind === "topic" && t.subject_id === topicId);
  }
  const topicOfThread = (s: Store, threadId: string) => {
    const th = s.threads.find((t) => t.id === threadId);
    return th ? s.topics.find((t) => t.id === th.subject_id) : undefined;
  };
  const channelOfTopic = (s: Store, t: Topic) => s.channels.find((c) => c.id === t.channel_id);

  function touchThread(s: Store, m: Message) {
    const th = s.threads.find((t) => t.id === m.thread_id);
    if (!th) return;
    th.last_message_at = m.created_at;
    th.message_count += 1;
    const t = s.topics.find((x) => x.id === th.subject_id);
    if (t) {
      t.last_message_at = m.created_at;
      t.message_count += 1;
    }
  }

  /** Channels the caller may see: public ones, and private ones and DMs they are in. */
  function canSee(s: Store, me: MeResponse, companyId: string, ch: Channel): boolean {
    if (!ch.is_private && ch.kind !== "dm" && ch.kind !== "group_dm") return true;
    const mine = membershipOf(me, companyId);
    return (
      mine !== null && s.members.some((m) => m.channel_id === ch.id && m.membership_id === mine)
    );
  }

  /**
   * WF-FIX (2026-10-26): whether `who` is one of the channel's members as `channels.members`
   * lists them: an explicit row, saved settings that say joined, or the caller in a channel it
   * is in by default.
   */
  function isChannelMember(s: Store, me: MeResponse, ch: Channel, who: string): boolean {
    if (s.members.some((m) => m.channel_id === ch.id && m.membership_id === who)) return true;
    if (s.prefs.get(`${who}|${ch.id}`)?.joined) return true;
    return who === membershipOf(me, ch.company_id) && prefsOf(s, who, ch).joined;
  }

  function visibleChannel(ctx: Ctx, id: string | undefined): { s: Store; ch: Channel } | null {
    const p = ctx.principal;
    if (!p || !id) return null;
    const s = storeOf(p.company.id);
    const ch = s.channels.find((c) => c.id === id);
    return ch && canSee(s, ctx.me, p.company.id, ch) ? { s, ch } : null;
  }

  /** M2-02's view of the caller's state in a channel (`channels.join` / `.subscription`). */
  function subscriptionOf(s: Store, who: string, ch: Channel) {
    const prefs = prefsOf(s, who, ch);
    const m = s.members.find((x) => x.channel_id === ch.id && x.membership_id === who);
    return {
      channel_id: ch.id,
      membership_id: who,
      joined: prefs.joined,
      role: m?.role ?? null,
      notify_level: prefs.notify,
      muted: prefs.muted,
      following: s.follows.some(
        (f) => f.membership_id === who && f.subject_kind === "channel" && f.subject_id === ch.id,
      ),
      last_read_message_id: m?.last_read_message_id ?? null,
    };
  }

  function setFollow(
    s: Store,
    w: { p: { company: { id: string } }; who: string },
    kind: Follow["subject_kind"],
    subjectId: string,
    on: boolean,
  ) {
    const has = (f: Follow) =>
      f.membership_id === w.who && f.subject_kind === kind && f.subject_id === subjectId;
    if (!on) s.follows = s.follows.filter((f) => !has(f));
    else if (!s.follows.some(has))
      s.follows.push(follow(w.p.company.id, w.who, kind, subjectId, "all", stamp()));
  }

  // CHAT-TOPICS-SEPARATE: places ---------------------------------------------------------------

  /** The channel's own conversation (its `(no topic)` topic). */
  const isMainTopic = (t: Pick<Topic, "name">) => t.name.toLowerCase() === MAIN_TOPIC;
  /** A place whose members are the channel's: the channel's own conversation, or a DM's topics. */
  const channelScoped = (t: Pick<Topic, "name">, ch: Pick<Channel, "kind">) =>
    isDmKind(ch.kind) || isMainTopic(t);
  const topicMemberOf = (s: Store, topicId: string, who: string | null) =>
    who === null
      ? undefined
      : s.topicMembers.find((r) => r.topic_id === topicId && r.membership_id === who);
  /** The topic's opener: its `creator` member row (the topic row's actor is its last editor). */
  const isOpenerOf = (s: Store, topicId: string, who: string | null) =>
    topicMemberOf(s, topicId, who)?.via === "creator";
  /** `person:{id}` / `de:{id}` mentions → membership ids. */
  const mentionedIds = (refs: readonly string[]) =>
    refs.flatMap((r) => {
      const m = /^(?:person|de):([0-9a-f-]{36})$/.exec(r);
      return m?.[1] ? [m[1]] : [];
    });
  /** Whether `who` is in the topic (the channel's own conversation: in the channel). */
  function inTopic(s: Store, me: MeResponse, ch: Channel, t: Topic, who: string | null): boolean {
    if (who === null) return false;
    if (channelScoped(t, ch)) return isChannelMember(s, me, ch, who) || prefsOf(s, who, ch).joined;
    return topicMemberOf(s, t.id, who) !== undefined;
  }
  /** A private topic is its members' alone (the channel's own conversation never is private). */
  const topicVisible = (s: Store, ch: Channel, t: Topic, who: string | null) =>
    !t.is_private || channelScoped(t, ch) || topicMemberOf(s, t.id, who) !== undefined;
  /** Adds members to a topic (none to the channel's own conversation); returns the new ones. */
  function addTopicMembers(
    s: Store,
    ch: Channel,
    t: Topic,
    ids: readonly string[],
    via: TopicMemberRow["via"],
    by: string | null,
  ): string[] {
    if (channelScoped(t, ch)) return [];
    const added: string[] = [];
    for (const id of new Set(ids)) {
      if (topicMemberOf(s, t.id, id)) continue;
      const when = stamp();
      s.topicMembers.push({
        company_id: t.company_id,
        topic_id: t.id,
        membership_id: id,
        via,
        last_read_message_id: null,
        last_read_at: null,
        actor_kind: by ? "person" : "system",
        actor_membership_id: by,
        created_at: when,
        updated_at: when,
      });
      added.push(id);
    }
    return added;
  }
  /** A topic as the caller reads it (`TopicDetail`). */
  const topicDetail = (s: Store, me: MeResponse, ch: Channel, t: Topic) => ({
    ...t,
    thread_id: threadOfTopic(s, t.id)?.id ?? "",
    is_main: isMainTopic(t),
    is_member: inTopic(s, me, ch, t, membershipOf(me, ch.company_id)),
    is_opener: !channelScoped(t, ch) && isOpenerOf(s, t.id, membershipOf(me, ch.company_id)),
  });

  function visibleTopic(ctx: Ctx, id: string | undefined) {
    const p = ctx.principal;
    if (!p || !id) return null;
    const s = storeOf(p.company.id);
    const t = s.topics.find((x) => x.id === id);
    const ch = t && channelOfTopic(s, t);
    return t &&
      ch &&
      canSee(s, ctx.me, p.company.id, ch) &&
      topicVisible(s, ch, t, membershipOf(ctx.me, p.company.id))
      ? { s, t, ch }
      : null;
  }

  function visibleThread(ctx: Ctx, id: string | undefined) {
    const p = ctx.principal;
    if (!p || !id) return null;
    const s = storeOf(p.company.id);
    const th = s.threads.find((x) => x.id === id);
    const t = th && topicOfThread(s, th.id);
    const ch = t && channelOfTopic(s, t);
    return th &&
      t &&
      ch &&
      canSee(s, ctx.me, p.company.id, ch) &&
      topicVisible(s, ch, t, membershipOf(ctx.me, p.company.id))
      ? { s, th, t, ch }
      : null;
  }

  /**
   * Unread for `who`: the channel's own conversation (a DM's every topic) from its read position
   * in the channel; a topic only for its members, from theirs (CHAT-TOPICS-SEPARATE). Without
   * `topicId`, the channel's total (archived topics left out).
   */
  const unreadIn = (s: Store, who: string, ch: Channel, topicId?: string) => {
    const chSince = s.reads.get(`${who}|${ch.id}`) ?? "";
    let n = 0;
    for (const t of s.topics) {
      if (t.channel_id !== ch.id || (topicId && t.id !== topicId)) continue;
      let since = chSince;
      if (!channelScoped(t, ch)) {
        const row = topicMemberOf(s, t.id, who);
        if (!row || (!topicId && t.archived_at)) continue;
        since = row.last_read_at ?? "";
      }
      const th = threadOfTopic(s, t.id);
      if (!th) continue;
      n += s.messages.filter(
        (m) =>
          m.thread_id === th.id &&
          !m.deleted_at &&
          m.author_membership_id !== who &&
          m.created_at > since,
      ).length;
    }
    return n;
  };

  /** The channel's last activity the caller sees: its own conversation and its topics. */
  const lastIn = (s: Store, ch: Channel, who?: string) =>
    s.topics
      .filter(
        (t) =>
          t.channel_id === ch.id &&
          (who === undefined || channelScoped(t, ch) || topicMemberOf(s, t.id, who) !== undefined),
      )
      .map((t) => t.last_message_at)
      .filter((x): x is string => x !== null)
      .sort()
      .at(-1) ?? null;

  const view = (s: Store, m: Message, who: string) => ({
    ...m,
    reactions: s.reactions.filter((r) => r.message_id === m.id),
    bookmarked: s.bookmarks.has(`${who}|${m.id}`),
    agent_wakes: s.wakes.get(m.id) ?? [],
    attachments: filesOfMessage(s, m),
  });

  /** CHAT-FILES: a message's live files, in the order sent (none once it is deleted). */
  const filesOfMessage = (s: Store, m: Pick<Message, "id" | "deleted_at">) =>
    m.deleted_at
      ? []
      : s.chatFiles.filter((f) => !f.gone && f.view.message_id === m.id).map((f) => f.view);

  // CHAT-SWITCH-A: the search page's filters (events/consumers/search.ts) ---------------------
  type SearchFilters = {
    from: string | null;
    in: string | null;
    has: "file" | null;
    is: "thread" | null;
    before: string | null;
    after: string | null;
  };
  const DATE = /^\d{4}-\d{2}-\d{2}$/;
  function searchFiltersOf(url: URL): SearchFilters | Response {
    const get = (k: string) => url.searchParams.get(k) || null;
    const f: SearchFilters = {
      from: get("from"),
      in: get("in"),
      has: get("has") as "file" | null,
      is: get("is") as "thread" | null,
      before: get("before"),
      after: get("after"),
    };
    if (f.has !== null && f.has !== "file") return deps.validation("has: must be file");
    if (f.is !== null && f.is !== "thread") return deps.validation("is: must be thread");
    for (const d of [f.before, f.after])
      if (d !== null && !DATE.test(d)) return deps.validation("dates are YYYY-MM-DD");
    return f;
  }
  const DAY_MS = 86_400_000;
  /** Whether a message passes the filters (`before` excludes its day, `after` starts the next). */
  function messageMatches(s: Store, m: Message, f: SearchFilters): boolean {
    if (f.from && m.author_membership_id !== f.from) return false;
    if (f.in) {
      const t = topicOfThread(s, m.thread_id);
      if (!t || t.channel_id !== f.in) return false;
    }
    if (f.has === "file" && filesOfMessage(s, m).length === 0) return false;
    if (
      f.is === "thread" &&
      !m.parent_message_id &&
      !s.messages.some((r) => r.parent_message_id === m.id && !r.deleted_at)
    )
      return false;
    const at = Date.parse(m.created_at);
    if (f.before && at >= Date.parse(`${f.before}T00:00:00Z`)) return false;
    if (f.after && at < Date.parse(`${f.after}T00:00:00Z`) + DAY_MS) return false;
    return true;
  }

  /** CHAT-SWITCH-A: the Files tab's kind (services/api/src/domain/chat/channel-files.ts). */
  const SPREADSHEET_MIMES = [
    "text/csv",
    "text/tab-separated-values",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet",
  ];
  const DOCUMENT_MIMES = [
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.oasis.opendocument.presentation",
    "application/rtf",
    "application/json",
  ];
  function fileKindOf(mime: string): ChannelFile["kind"] {
    const m = mime.toLowerCase();
    if (m.startsWith("image/")) return "image";
    if (m === "application/pdf") return "pdf";
    if (SPREADSHEET_MIMES.includes(m)) return "spreadsheet";
    if (m.startsWith("text/") || DOCUMENT_MIMES.includes(m)) return "document";
    if (m.startsWith("audio/") || m.startsWith("video/")) return "media";
    return "other";
  }

  // AGENT-MENTION ------------------------------------------------------------------------------
  // The API's readiness rule (domain/chat/wake.ts `skipOf`), as far as this twin knows it: the
  // DE's lifecycle, the add-on, the channel's `silent` rule, then whether the DE belongs to the
  // channel (an agent reads the channels it is a member of). A mention that cannot wake its DE
  // is recorded with why; one that can is `queued` (this twin starts no run).
  type Skip = "not_live" | "retired" | "paused" | "agents_off" | "silent" | "no_access";
  const SKIP_TEXT: Record<Skip, (n: string) => Pick<AgentWake, "state" | "reason">> = {
    not_live: (n) => ({ state: "not_live", reason: `${n} is still being set up.` }),
    retired: (n) => ({ state: "not_live", reason: `${n} is retired.` }),
    agents_off: () => ({ state: "not_live", reason: "Agents are off for this company." }),
    paused: (n) => ({ state: "paused", reason: `${n} is paused.` }),
    silent: () => ({
      state: "silent",
      reason: "Agents in this channel run on schedule only; mentions don't wake them.",
    }),
    no_access: (n) => ({ state: "no_access", reason: `${n} can't see this conversation.` }),
  };
  const READINESS: Record<Skip, Readiness["state"]> = {
    not_live: "not_live",
    retired: "not_live",
    agents_off: "not_live",
    paused: "paused",
    silent: "silent",
    no_access: "no_access",
  };
  const NOT_STARTED = new Set<AgentWake["state"]>([
    "not_live",
    "no_access",
    "paused",
    "over_budget",
    "silent",
    "blocked",
  ]);

  function skipOf(s: Store, ch: Channel, de: FakeDe): Skip | null {
    if (de.status === "retired") return "retired";
    if (de.status === "paused") return "paused";
    if (de.status === "onboarding" || de.status === "not_deployed") return "not_live";
    if (deps.agentsOn && !deps.agentsOn(ch.company_id)) return "agents_off";
    if (s.rules.get(ch.id)?.wake === "silent") return "silent";
    return deReads(s, ch, de) ? null : "no_access";
  }

  /**
   * Whether the DE reads the channel. As `canSee` (this twin models no space privacy, for anyone):
   * a public channel is open to the company's members; else a member row.
   */
  function deReads(s: Store, ch: Channel, de: FakeDe): boolean {
    const open = !ch.is_private && ch.kind !== "dm" && ch.kind !== "group_dm";
    return (
      open || s.members.some((m) => m.channel_id === ch.id && m.membership_id === de.membership_id)
    );
  }

  /** The DEs a message's mention refs name (`de:`, `person:` of a DE, `seat:` a DE holds). */
  function mentionedDes(companyId: string, refs: readonly string[]): FakeDe[] {
    const des = deps.desOf(companyId);
    const out = new Map<string, FakeDe>();
    for (const ref of refs) {
      const [kind, id] = ref.split(":");
      const de =
        kind === "seat"
          ? des.find((d) => d.seat_id === id && d.status !== "retired")
          : kind === "de" || kind === "person"
            ? des.find((d) => d.membership_id === id)
            : undefined;
      if (de) out.set(de.membership_id, de);
    }
    return [...out.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }

  function wakeRow(de: FakeDe, skip: Skip | null, requestedAt: string): AgentWake {
    const base = {
      membership_id: de.membership_id,
      name: de.display_name,
      agent_kind: "digital_employee" as const,
      run_id: null,
      requested_at: requestedAt,
    };
    return skip
      ? { ...base, ...SKIP_TEXT[skip](de.display_name) }
      : {
          ...base,
          state: "queued",
          reason: `Asked; ${de.display_name} starts when a slot is free.`,
        };
  }

  /** A new open topic of `ch` and its thread. */
  function insertTopic(s: Store, ch: Channel, name: string, who: string | null) {
    const when = stamp();
    const row: Topic = {
      company_id: ch.company_id,
      id: nextId(0xc120),
      channel_id: ch.id,
      name,
      status: "open",
      is_agent_room: ch.is_agent_room,
      taken_over_by_membership_id: null,
      taken_over_at: null,
      pinned_at: null,
      archived_at: null,
      last_message_at: null,
      message_count: 0,
      is_private: false,
      actor_kind: who ? "person" : "agency_user",
      actor_membership_id: who,
      created_at: when,
      updated_at: when,
    };
    const thread: Thread = {
      company_id: ch.company_id,
      id: nextId(0xc130),
      kind: "topic",
      subject_id: row.id,
      last_message_at: null,
      message_count: 0,
      created_at: when,
      updated_at: when,
    };
    s.topics.push(row);
    s.threads.push(thread);
    return { row, thread };
  }

  type DmMessage = Pick<Message, "company_id" | "author_membership_id"> & {
    parent_message_id?: string | null;
  };

  /** CHAT-THREAD-AGENTS: a DM's agents are asked at its top level only (a reply picks one). */
  const dmTopLevel = (m: DmMessage) => !m.parent_message_id;

  /** Is this membership an agent (a DE or a person's crew)? */
  const isAgent = (s: Store, companyId: string, id: string | null) =>
    id !== null &&
    (deps.desOf(companyId).some((d) => d.membership_id === id) || s.crewOwners.has(id));

  /**
   * CHAT-DE (chat/wake.ts `dmAgents`): a person's message in a DM names the DM's DEs as a
   * mention would; an agent's post (a DE's or a crew's) names none.
   */
  function dmRefs(s: Store, ch: Channel, m: DmMessage) {
    if (!isDmKind(ch.kind) || !dmTopLevel(m) || isAgent(s, m.company_id, m.author_membership_id))
      return [];
    const des = deps.desOf(m.company_id);
    return s.members
      .filter((x) => x.channel_id === ch.id && des.some((d) => d.membership_id === x.membership_id))
      .map((x) => `de:${x.membership_id}`);
  }

  /** The crews (`personal_agent`) of a DM a person's message asks, as `dmRefs` its DEs. */
  function dmCrews(s: Store, ch: Channel, m: DmMessage): string[] {
    if (!isDmKind(ch.kind) || !dmTopLevel(m) || isAgent(s, m.company_id, m.author_membership_id))
      return [];
    return s.members
      .filter((x) => x.channel_id === ch.id && s.crewOwners.has(x.membership_id))
      .map((x) => x.membership_id)
      .filter((id) => id !== m.author_membership_id);
  }

  /**
   * A crew's ask (wake.ts `skipOf`): only its owner wakes it (`owner_only`, shown as
   * no_access); the add-on off stops it; otherwise queued (it reads the DM it is in).
   */
  function crewWakeRow(
    s: Store,
    ch: Channel,
    crew: string,
    asker: string | null,
    name: string,
    requestedAt: string,
  ): AgentWake {
    const base = {
      membership_id: crew,
      name,
      agent_kind: "personal_agent" as const,
      run_id: null,
      requested_at: requestedAt,
    };
    if (s.crewOwners.get(crew) !== asker)
      return { ...base, state: "no_access", reason: `Only its owner can wake ${name}.` };
    if (deps.agentsOn && !deps.agentsOn(ch.company_id))
      return { ...base, ...SKIP_TEXT.agents_off(name) };
    return { ...base, state: "queued", reason: `Asked; ${name} starts when a slot is free.` };
  }

  /** The wake results of a new message (its author never wakes itself). */
  function wakeMentioned(
    s: Store,
    ch: Channel,
    m: Message,
    nameOf: (id: string) => string,
  ): AgentWake[] {
    const wakes = [
      ...mentionedDes(m.company_id, [...m.mentions, ...dmRefs(s, ch, m)])
        .filter((de) => de.membership_id !== m.author_membership_id)
        .map((de) => wakeRow(de, skipOf(s, ch, de), m.created_at)),
      ...dmCrews(s, ch, m).map((c) =>
        crewWakeRow(s, ch, c, m.author_membership_id, nameOf(c), m.created_at),
      ),
    ].sort((a, b) => a.name.localeCompare(b.name));
    if (wakes.length) s.wakes.set(m.id, wakes);
    return wakes;
  }

  /**
   * THREAD-WAKE, as the API's `addressingOf`: the conversation (`root` and its replies, else the
   * top level) read back from the newest: a message naming an agent (not its author) addresses
   * the first one named; else an agent's own message makes it the addressee. A person's message
   * naming no agent changes nothing. Also who took part (posted or was named).
   */
  function addressingIn(
    s: Store,
    companyId: string,
    threadId: string,
    root: string | null,
    except: string | null = null,
  ): { addressee: string | null; participating: Set<string> } {
    const des = deps.desOf(companyId);
    const isDe = (id: string | null) => id !== null && des.some((d) => d.membership_id === id);
    const said = s.messages
      .filter(
        (m) =>
          m.thread_id === threadId &&
          m.id !== except &&
          !m.deleted_at &&
          (root === null
            ? m.parent_message_id === null
            : m.id === root || m.parent_message_id === root),
      )
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
    if (root !== null) return replyThreadAddressing(s, companyId, threadId, root, said);
    const participating = new Set<string>();
    let addressee: string | null = null;
    for (const m of said) {
      const author = isDe(m.author_membership_id) ? m.author_membership_id : null;
      if (author) participating.add(author);
      const ids = [
        ...new Set(
          m.mentions.flatMap((r) => mentionedDes(companyId, [r]).map((d) => d.membership_id)),
        ),
      ].filter((id) => id !== m.author_membership_id);
      for (const id of ids) participating.add(id);
      addressee = ids[0] ?? author ?? addressee;
    }
    return { addressee, participating };
  }

  type ThreadRole = "started" | "target" | "posted" | "mentioned";
  type FakeParticipant = { role: ThreadRole; joined: string; posted: string | null };

  /**
   * CHAT-THREAD-AGENTS (a reply thread), as the API's `derivedParticipants` +
   * `pickThreadAddressee`: the root's agent author, the agents it names and its targets (a DM's
   * agents) started it at the root's time; an agent that replied posted (joined at its first
   * reply, its newest reply time kept); an agent a reply names was mentioned (at the first such
   * reply). A muted agent is out until named again. The pick: the only one; else the latest
   * replier; else the earliest starter; else the one that joined last — ties by membership id.
   */
  function replyThreadAddressing(
    s: Store,
    companyId: string,
    threadId: string,
    root: string,
    said: readonly Message[],
  ): { addressee: string | null; participating: Set<string> } {
    const agentIds = (refs: readonly string[]) => [
      ...new Set([
        ...mentionedDes(companyId, refs).map((d) => d.membership_id),
        ...refs.flatMap((r) => {
          const id = r.startsWith("person:") ? r.slice("person:".length) : null;
          return id && s.crewOwners.has(id) ? [id] : [];
        }),
      ]),
    ];
    const ps = new Map<string, FakeParticipant>();
    const add = (id: string, role: ThreadRole, at: string, postedAt: string | null = null) => {
      const prior = ps.get(id);
      const earlier = !prior || at < prior.joined;
      ps.set(id, {
        role: earlier ? role : (prior?.role ?? role),
        joined: earlier ? at : (prior?.joined ?? at),
        posted:
          postedAt && (!prior?.posted || postedAt > prior.posted)
            ? postedAt
            : (prior?.posted ?? null),
      });
    };
    const named = new Map<string, string>();
    const rootRow = said.find((m) => m.id === root);
    if (rootRow) {
      const author = rootRow.author_membership_id;
      if (isAgent(s, companyId, author) && author) add(author, "started", rootRow.created_at);
      for (const id of agentIds(rootRow.mentions))
        if (id !== author && !ps.has(id)) add(id, "started", rootRow.created_at);
      // A DM's agents are the root's targets from its first message.
      const topic = topicOfThread(s, threadId);
      const ch = topic ? channelOfTopic(s, topic) : undefined;
      if (ch && isDmKind(ch.kind))
        for (const mem of s.members)
          if (
            mem.channel_id === ch.id &&
            mem.membership_id !== author &&
            isAgent(s, companyId, mem.membership_id) &&
            !ps.has(mem.membership_id)
          )
            add(mem.membership_id, "target", rootRow.created_at);
      for (const id of agentIds(rootRow.mentions)) named.set(id, rootRow.created_at);
    }
    for (const m of said) {
      if (m.id === root) continue;
      for (const id of agentIds(m.mentions)) {
        if (id === m.author_membership_id) continue;
        add(id, "mentioned", m.created_at);
        named.set(id, m.created_at);
      }
      const author = m.author_membership_id;
      if (author && m.author_kind === "agent" && isAgent(s, companyId, author))
        add(author, "posted", m.created_at, m.created_at);
    }
    const live = [...ps.entries()]
      .filter(([id]) => {
        const muted = s.mutedAgents.get(`${root}|${id}`);
        return !muted || (named.get(id) ?? "") > muted;
      })
      .map(([id, p]) => ({ id, ...p }));
    const byId = (a: { id: string }, b: { id: string }) => a.id.localeCompare(b.id);
    const [only] = live;
    let picked: string | null = null;
    if (only && live.length === 1) picked = only.id;
    else if (only) {
      const [replied] = live
        .filter((p) => p.posted !== null)
        .sort((a, b) => (b.posted ?? "").localeCompare(a.posted ?? "") || byId(a, b));
      const [starter] = live
        .filter((p) => p.role === "started" || p.role === "target")
        .sort((a, b) => a.joined.localeCompare(b.joined) || byId(a, b));
      const [last] = [...live].sort((a, b) => b.joined.localeCompare(a.joined) || byId(a, b));
      picked = replied?.id ?? starter?.id ?? last?.id ?? null;
    }
    return { addressee: picked, participating: new Set(live.map((p) => p.id)) };
  }

  // CHAT-PRESENCE: the DE rings, as the API's `dePresence.list` decides them -------------------

  const isMember = (s: Store, channelId: string, who: string) =>
    s.members.some((m) => m.channel_id === channelId && m.membership_id === who);
  const ENDED = new Set<Run["state"]>(["completed", "failed", "cancelled"]);
  const WAITING = new Set<Run["state"]>(["waiting_input", "awaiting_approval", "waiting_children"]);
  const endedAt = (r: Run) => r.ended_at ?? r.updated_at;
  /** Waits that are the platform's (a take-over, a review), not a question for a person. */
  const SYSTEM_WAITS = /^(parked|taken_over|verdict_not_verified|premise_escalated)/;

  /** The DE's latest ended run when it failed and nobody acknowledged it. */
  function lastFailed(s: Store, de: string): Run | null {
    const [last] = s.runs
      .filter((r) => r.membership_id === de && ENDED.has(r.state))
      .sort((a, b) => endedAt(b).localeCompare(endedAt(a)));
    if (last?.state !== "failed" || s.acks.has(last.id)) return null;
    // A failure the platform retries (scheduled, or a successor started) is not stuck.
    return last.retry_state === "scheduled" || last.retry_state === "spawned" ? null : last;
  }

  /** A guest (client viewer only) sees no DE rings, as the API. */
  const guestOnly = (p: Principal) =>
    p.roles.length > 0 && p.roles.every((r) => r === "client_viewer");

  /** The caller may read this run's details (the API's `viewRun`): manager, holder, supervisor, owner. */
  function viewsRun(p: Principal, who: string | null, r: Run): boolean {
    if (p.operator || p.roles.includes("owner") || p.roles.includes("admin")) return true;
    if (!who) return false;
    if (r.owner_membership_id === who) return true;
    if (r.seat_id && seatHolder(p.company.id, r.seat_id) === who) return true;
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === r.membership_id);
    return de?.supervisor.membership_id === who;
  }

  /** What of a DE waits on the caller, whatever its lifecycle (the ring and "Clean up" read it). */
  function viewerAsks(s: Store, p: Principal, who: string | null, de: string) {
    if (!who) return { approval: undefined, input: undefined, screenAsk: undefined };
    const companyId = p.company.id;
    const admin = p.operator || p.roles.includes("owner") || p.roles.includes("admin");
    const approval = s.approvals.find(
      (a) =>
        a.state === "pending" &&
        a.requested_by_membership_id === de &&
        seatHolder(companyId, a.approver_seat_id) === who,
    );
    const input = s.runs
      .filter((r) => r.membership_id === de)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .find(
        (r) =>
          r.state === "waiting_input" &&
          !SYSTEM_WAITS.test(r.state_reason ?? "") &&
          (r.owner_membership_id === who || triggerMessage(s, r)?.author_membership_id === who),
      );
    const screenAsk = (deps.screens?.(companyId) ?? { asks: [], leases: [] }).asks.find(
      (a) =>
        a.agent_membership_id === de &&
        (a.owner_membership_id ? a.owner_membership_id === who : admin),
    );
    return { approval, input, screenAsk };
  }

  /** Who holds a seat in the demo (the people's seats; each DE holds its own). */
  function seatHolder(companyId: string, seatId: string): string | null {
    if (seatId === JORDAN_SEAT) return CHAT_PEOPLE.jordan;
    return deps.desOf(companyId).find((d) => d.seat_id === seatId)?.membership_id ?? null;
  }

  /** The message a run woke on (its trigger), when it was a message. */
  const triggerMessage = (s: Store, r: Run) =>
    r.trigger_ref?.kind === "message" ? s.messages.find((m) => m.id === r.trigger_ref?.id) : null;

  /** Where a run works: its trigger's topic and channel, when the caller can see the channel. */
  function placeOf(s: Store, ctx: Ctx, r: Run): DePresence["place"] {
    const ref = r.trigger_ref;
    const topic =
      ref?.kind === "topic"
        ? s.topics.find((t) => t.id === ref.id)
        : ref?.kind === "message"
          ? topicOfThread(s, triggerMessage(s, r)?.thread_id ?? "")
          : undefined;
    const ch = topic ? channelOfTopic(s, topic) : undefined;
    if (!topic || !ch || !ctx.principal || !canSee(s, ctx.me, ch.company_id, ch)) return null;
    // A private topic only to its members.
    const who = membershipOf(ctx.me, ch.company_id);
    if (topic.is_private && (!who || topicMemberOf(s, topic.id, who) === undefined)) return null;
    const dm = isDmKind(ch.kind);
    const main = topic.name === MAIN_TOPIC;
    return {
      channel_id: ch.id,
      channel_name: ch.name,
      is_dm: dm,
      topic_id: main ? null : topic.id,
      topic_name: main ? null : topic.name,
    };
  }

  function presenceOf(s: Store, ctx: Ctx, p: Principal): DePresence[] {
    const companyId = p.company.id;
    if (guestOnly(p)) return [];
    const who = membershipOf(ctx.me, companyId);
    const screens = deps.screens?.(companyId) ?? { asks: [], leases: [] };
    return deps.desOf(companyId).map((de): DePresence => {
      const id = de.membership_id;
      const runs = s.runs
        .filter((r) => r.membership_id === id)
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      const live = runs.find((r) => r.state === "queued" || r.state === "running");
      const { approval, input, screenAsk } = viewerAsks(s, p, who, id);
      const waiting = runs.find(
        (r) => WAITING.has(r.state) && r !== input && r.id !== approval?.run_id,
      );
      const failed = lastFailed(s, id);
      const state = deRingState({
        lifecycle: de.status as "onboarding" | "active" | "paused" | "retired",
        needsViewer: Boolean(approval || input || screenAsk),
        failedUnacked: failed !== null,
        live: live !== undefined,
        waiting: waiting !== undefined,
      });
      const run =
        state === "needs_you"
          ? (runs.find((r) => r.id === approval?.run_id) ?? input ?? null)
          : state === "stuck"
            ? failed
            : state === "working"
              ? (live ?? null)
              : state === "waiting"
                ? (waiting ?? null)
                : null;
      const ask: DePresence["ask"] =
        state !== "needs_you"
          ? null
          : approval
            ? {
                kind: "approval",
                text: oneSentence(approval.proposal.title) ?? "Approve the proposal",
                approval_id: approval.id,
                screen_lease_id: null,
                thread_id: approval.thread_id,
                parent_message_id: null,
              }
            : input
              ? {
                  kind: "input",
                  text: oneSentence(input.state_reason) ?? "Waiting for your reply.",
                  approval_id: null,
                  screen_lease_id: null,
                  thread_id: triggerMessage(s, input)?.thread_id ?? null,
                  parent_message_id: triggerMessage(s, input)?.parent_message_id ?? null,
                }
              : screenAsk
                ? {
                    kind: "screen",
                    text:
                      screenAsk.why === "login"
                        ? `Needs you to log in${screenAsk.reason ? ` to ${screenAsk.reason}` : ""}.`
                        : (oneSentence(screenAsk.reason) ?? "Needs you on a screen."),
                    approval_id: null,
                    screen_lease_id: screenAsk.lease_id,
                    thread_id: null,
                    parent_message_id: null,
                  }
                : null;
      const since =
        state === "needs_you"
          ? (approval?.created_at ?? input?.updated_at ?? screenAsk?.created_at ?? null)
          : state === "stuck"
            ? failed
              ? endedAt(failed)
              : null
            : state === "working"
              ? (live?.started_at ?? live?.created_at ?? null)
              : state === "waiting"
                ? (waiting?.updated_at ?? null)
                : null;
      // The run's details only to who may view the run (the API's `viewRun`).
      const shown = run && viewsRun(p, who, run) ? run : null;
      const task = shown?.task_id ? s.tasks.find((t) => t.id === shown.task_id) : undefined;
      return {
        membership_id: id,
        state,
        since,
        run_id: shown?.id ?? null,
        task: task ? { id: task.id, title: task.title } : null,
        place: run ? placeOf(s, ctx, run) : null,
        screen_lease_id: screens.leases.find((l) => l.holder_membership_id === id)?.id ?? null,
        ask,
        error: state === "stuck" && shown ? firstLine(shown.state_reason) : null,
      };
    });
  }

  const sidebarRow = (s: Store, who: string, ch: Channel, p: Principal) => ({
    ...ch,
    ...(({ joined, notify_level, muted, following }) => ({
      joined,
      notify_level: notify_level ?? "all",
      muted,
      following,
    }))(subscriptionOf(s, who, ch)),
    unread_count: unreadIn(s, who, ch),
    last_message_at: lastIn(s, ch),
    can_invite: canInvite(s, p, ch),
    can_manage: canManage(s, p, ch),
    closed: isDmKind(ch.kind) && dmClosed(s.closures.get(`${who}|${ch.id}`) ?? null, lastIn(s, ch)),
  });

  /**
   * WF-FIX (2026-10-26): `channelAccess(...).manage` as the fake models it: the company's
   * managers (the space's owners) and the channel's admins; never a DM or an archived channel.
   */
  function canManage(s: Store, p: Principal, ch: Channel): boolean {
    const who = membershipOf(p.me, ch.company_id);
    if (!who || ch.archived_at || isDmKind(ch.kind)) return false;
    return managesChannel(s, { p, who }, ch);
  }

  /**
   * WF-FIX (2026-10-26): `channelAccess(...).invite` as the fake models it: an author (a
   * membership, not an agency operator) who posts there — a private channel's member, a public
   * channel's company member (a client viewer has no role on a public space) — never in an
   * archived channel or a DM.
   */
  function canInvite(s: Store, p: Principal, ch: Channel): boolean {
    const who = membershipOf(p.me, ch.company_id);
    if (!who || ch.archived_at || isDmKind(ch.kind)) return false;
    if (ch.is_private)
      return s.members.some((m) => m.channel_id === ch.id && m.membership_id === who);
    return p.roles.some((r) => r !== "client_viewer");
  }

  /**
   * Without a saved setting: joined when a member row exists, or when the channel is a public
   * default one (the real API's `chat.setup` joins every person to those; CHAT-UX), with every
   * message notifying there. A saved leave stays a leave.
   */
  const prefsOf = (s: Store, who: string, ch: Channel): ChannelPrefs => {
    const saved = s.prefs.get(`${who}|${ch.id}`);
    if (saved) return saved;
    const byDefault = ch.is_default && !ch.is_private && !ch.archived_at;
    return {
      channel_id: ch.id,
      joined: byDefault || s.members.some((m) => m.channel_id === ch.id && m.membership_id === who),
      muted: false,
      notify: byDefault ? "all" : "mentions",
      updated_at: ch.created_at,
    };
  };

  /** A write needs a membership (agency operators read chat but do not post in it). */
  function writer(ctx: Ctx): { p: Principal; who: string } | Response {
    const p = ctx.principal;
    if (!p) return deps.notFound();
    const who = membershipOf(ctx.me, p.company.id);
    if (!who) return forbidden("Post as a member of the company (agency operators read chat).");
    return { p, who };
  }

  /** Archive or restore a topic (CHAT-UX): the channel's managers only. */
  function archiveTopic(ctx: Ctx, archived: boolean): Response {
    const w = writer(ctx);
    if (w instanceof Response) return w;
    const v = visibleTopic(ctx, ctx.params.id);
    if (!v) return deps.notFound();
    if (isMainTopic(v.t)) return conflict(MAIN_FIXED);
    if (!managesChannel(v.s, w, v.ch))
      return forbidden("Only the channel's admins archive a topic or restore it.");
    if (archived !== (v.t.archived_at !== null)) {
      v.t.archived_at = archived ? stamp() : null;
      v.t.updated_at = stamp();
    }
    return deps.json(topicDetail(v.s, ctx.me, v.ch, v.t));
  }

  /** The API's `access.manage`: a company owner or admin, or an admin row in the channel. */
  function managesChannel(s: Store, w: { p: Principal; who: string }, ch: Channel): boolean {
    return (
      w.p.manager ||
      // WF-FIX (2026-10-26): an owner of the channel's space (`spaces.addMember` relation owner).
      (ch.space_id !== null && s.spaceRelations.get(`${ch.space_id}|${w.who}`) === "owner") ||
      s.members.some(
        (m) => m.channel_id === ch.id && m.membership_id === w.who && m.role === "admin",
      )
    );
  }

  const bodyOf = (ctx: Ctx) => (ctx.body ?? {}) as Record<string, unknown>;

  // Handlers ---------------------------------------------------------------------------------

  const handlers: Partial<Record<RouteId, Handler>> = {
    "spaces.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const kind = ctx.url.searchParams.get("kind");
      const me = membershipOf(ctx.me, p.company.id);
      return deps.json(
        deps.page(
          s.spaces.filter(
            (x) =>
              (!kind || x.kind === kind) && (x.kind !== "personal" || x.owner_membership_id === me),
          ),
          ctx.url,
        ),
      );
    },
    "spaces.get": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).spaces.find((x) => x.id === ctx.params.id);
      return row ? deps.json(row) : deps.notFound();
    },
    "spaces.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"spaces.create">["body"];
      const name = String(b.name ?? "").trim();
      if (!name) return deps.validation("name must not be empty");
      const when = stamp();
      const row: Space = {
        company_id: w.p.company.id,
        id: nextId(0xc100),
        name,
        kind: b.kind,
        team_id: b.team_id ?? null,
        owner_membership_id: null,
        icon: b.icon ?? null,
        description: b.description ?? null,
        is_private: b.is_private ?? false,
        // MEMBER-UX, as the API: a shared space (no team, not private) lets members edit.
        settings:
          !b.team_id && !b.is_private && b.settings?.member_access === undefined
            ? { ...b.settings, member_access: "editor" }
            : (b.settings ?? {}),
        // BOARD-ORDER, as the API's default: the creation instant, so it lands last.
        position: Date.parse(when),
        archived_at: null,
        actor_kind: "person",
        actor_membership_id: w.who,
        created_at: when,
        updated_at: when,
      };
      s.spaces.push(row);
      // Zero setup (06): a space comes with its channel, the space's default one.
      s.channels.push({
        company_id: row.company_id,
        id: nextId(0xc110),
        space_id: row.id,
        kind: "space",
        is_default: true,
        name:
          name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "") || "general",
        description: null,
        board_id: null,
        team_id: row.team_id,
        posting_policy: "everyone",
        topic_policy: "everyone",
        broadcast_policy: "everyone",
        default_notify_level: "all",
        is_private: row.is_private,
        is_agent_room: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: null,
        archived_at: null,
        actor_kind: "person",
        actor_membership_id: w.who,
        created_at: when,
        updated_at: when,
      });
      return deps.json(row, 201);
    },
    "spaces.update": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).spaces.find((x) => x.id === ctx.params.id);
      if (!row) return deps.notFound();
      // The space's owners (company#admin in the fake): a member cannot change who edits.
      if (!p.manager) return forbidden("Only the space's owners change its settings.");
      const b = bodyOf(ctx) as OperationInput<"spaces.update">["body"];
      if (b.name !== undefined) row.name = b.name;
      if (b.description !== undefined) row.description = b.description;
      if (b.icon !== undefined) row.icon = b.icon;
      if (b.is_private !== undefined) row.is_private = b.is_private;
      if (b.settings !== undefined) row.settings = b.settings;
      row.updated_at = stamp();
      return deps.json(row);
    },
    "spaces.archive": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const row = s.spaces.find((x) => x.id === ctx.params.id);
      if (!row) return deps.notFound();
      if (row.archived_at) return conflict("already archived");
      row.archived_at = stamp();
      // Archiving a space archives its channels; nothing is deleted (06).
      for (const c of s.channels) if (c.space_id === row.id) c.archived_at ??= row.archived_at;
      return deps.json(row);
    },
    // ACTIONS: back with exactly what the archive took (archived at the same instant).
    "spaces.unarchive": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const row = s.spaces.find((x) => x.id === ctx.params.id);
      if (!row) return deps.notFound();
      const at = row.archived_at;
      if (!at) return conflict("the space is not archived");
      for (const c of s.channels)
        if (c.space_id === row.id && c.archived_at === at) c.archived_at = null;
      row.archived_at = null;
      row.updated_at = stamp();
      return deps.json(row);
    },
    "spaces.sidebar": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const row = s.spaces.find((x) => x.id === ctx.params.id);
      if (!row) return deps.notFound();
      const who = whoKey(ctx.me, p.company.id);
      const work = deps.work?.()?.sidebarOf(p.company.id, row.id) ?? null;
      const channels = [
        ...s.channels
          .filter((c) => c.space_id === row.id && canSee(s, ctx.me, p.company.id, c))
          .map((c) => sidebarRow(s, who, c, p)),
        // Work's channels (board homes, template channels), unless Chat holds one of that name.
        ...(work?.channels ?? []).filter(
          (c) =>
            !s.channels.some((x) => x.id === c.id || (x.space_id === row.id && x.name === c.name)),
        ),
      ];
      return deps.json({
        space: row,
        channels,
        boards: [
          ...(work?.boards ?? []),
          ...s.boards.filter(
            (b) => b.space_id === row.id && !work?.boards.some((x) => x.id === b.id),
          ),
        ],
        pages: (deps.pagesOf?.(p.company.id, row.id) ?? []).map((x) => ({
          id: x.id,
          title: x.title,
          parent_page_id: x.parent_page_id,
        })),
        unread_total: channels.reduce((n, c) => n + c.unread_count, 0),
      });
    },
    "spaces.addMember": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).spaces.find((x) => x.id === ctx.params.id);
      if (!row) return deps.notFound();
      if (row.team_id) return conflict("a team space follows its team's membership");
      // WF-FIX (2026-10-26): kept, so a space owner manages its channels (as the API's tuples).
      const b = bodyOf(ctx) as OperationInput<"spaces.addMember">["body"];
      storeOf(p.company.id).spaceRelations.set(`${row.id}|${b.membership_id}`, b.relation);
      return new Response(null, { status: 204 });
    },
    "spaces.removeMember": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const row = s.spaces.find((x) => x.id === ctx.params.id);
      if (row) s.spaceRelations.delete(`${row.id}|${ctx.params.membership_id}`);
      return row ? new Response(null, { status: 204 }) : deps.notFound();
    },

    // CHAT-FILES: stage one file (multipart `file`, optional `thread_id`), ✕, read, save a copy.
    "attachments.upload": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const b = bodyOf(ctx) as {
        __files?: Record<string, { bytes: Uint8Array; name: string; type: string }>;
        thread_id?: string;
      };
      const part = b.__files?.file;
      if (!part) return deps.validation("file: exactly one `file` part");
      if (b.thread_id && !visibleThread(ctx, b.thread_id)) return deps.notFound();
      if (part.bytes.byteLength === 0) return deps.validation(`file: ${part.name} is empty`);
      if (part.bytes.byteLength > 25 * 1024 * 1024)
        return deps.problem(
          413,
          "payload_too_large" as never,
          "Payload too large",
          `${part.name} is too large (the limit is 25 MB per file)`,
        );
      const s = storeOf(w.p.company.id);
      const name = cleanFileName(part.name.split("/").pop() || "file");
      const mime = sniffMime(part.bytes, name, part.type || null);
      const de = deps.desOf(w.p.company.id).some((d) => d.membership_id === w.who);
      const view: ChatFileView = {
        id: nextId(0xc191),
        file_name: name,
        mime,
        bytes: part.bytes.byteLength,
        preview: previewOf(mime),
        has_thumb: thumbable(mime) && previewOf(mime) === "image",
        preview_gap: /^image\/hei[cf]/.test(mime) ? "heic" : null,
        thread_id: b.thread_id ?? null,
        message_id: null,
        uploader: {
          membership_id: w.who,
          kind: de ? "digital_employee" : "person",
          name: memberInfo(s, ctx, w.p.company.id, w.who).display_name,
        },
        created_at: stamp(),
      };
      s.chatFiles.push({ view, bytes: part.bytes.slice(), gone: false });
      return deps.json(view, 201);
    },
    "attachments.discard": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const f = s.chatFiles.find((x) => x.view.id === ctx.params.id && !x.gone);
      if (!f || f.view.uploader.membership_id !== w.who) return deps.notFound();
      if (f.view.message_id) return conflict("the file was sent: it goes with its message");
      f.gone = true;
      return new Response(null, { status: 204 });
    },
    "attachments.get": (ctx) => {
      const f = readableChatFile(ctx);
      return f ? deps.json(f.view) : deps.notFound();
    },
    "attachments.content": (ctx) => {
      const f = readableChatFile(ctx);
      if (!f) return deps.notFound();
      const thumb = ctx.url.searchParams.get("thumb") === "true";
      if (thumb && f.view.preview !== "image") return deps.notFound();
      // `size=thumb|preview` (IMAGE-PREVIEWS): a small WebP copy (the twin sends the same bytes).
      const size = ctx.url.searchParams.get("size");
      const copy =
        (size === "thumb" || size === "preview") &&
        ctx.url.searchParams.get("download") !== "true" &&
        f.view.preview === "image";
      const inline = ctx.url.searchParams.get("download") !== "true" && f.view.preview !== "none";
      return new Response(f.bytes.slice(), {
        status: 200,
        headers: {
          "Content-Type": copy ? "image/webp" : thumb ? f.view.mime : servedType(f.view.mime),
          "Content-Disposition": contentDisposition(
            inline ? "inline" : "attachment",
            f.view.file_name,
          ),
          "X-Content-Type-Options": "nosniff",
        },
      });
    },
    "attachments.save": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const f = readableChatFile(ctx);
      if (!f) return deps.notFound();
      const s = storeOf(w.p.company.id);
      const m = s.messages.find((x) => x.id === f.view.message_id);
      if (!m || !f.view.thread_id) return conflict("an unsent file has nothing to save yet");
      const b = bodyOf(ctx) as { task_id?: string; folder_path?: string };
      if (!b.task_id) return deps.validation("task_id: required");
      if (!deps.saveChatCopy) return deps.notFound();
      const sender = m.author_membership_id ?? f.view.uploader.membership_id;
      return deps.saveChatCopy(
        w.p,
        b.task_id,
        {
          bytes: f.bytes,
          name: f.view.file_name,
          mime: f.view.mime,
          folderPath: b.folder_path ?? null,
        },
        {
          kind: "chat_message",
          source_attachment_id: f.view.id,
          message_id: m.id,
          thread_id: f.view.thread_id,
          sender_membership_id: sender,
          sender_name: memberInfo(s, ctx, w.p.company.id, sender).display_name,
          sent_at: m.created_at,
          saved_by_membership_id: w.who,
          saved_at: stamp(),
        },
      );
    },

    "inbox.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const filter = ctx.url.searchParams.get("filter");
      const now = stamp();
      const rows = inboxOf(ctx.me, p.company.id).filter((i) => inboxMatches(i, filter, now));
      return deps.json(deps.page(rows, ctx.url));
    },
    "inbox.read": (ctx) => inboxMark(ctx, "read_at", bodyOf(ctx).read !== false),
    "inbox.done": (ctx) => inboxMark(ctx, "done_at", bodyOf(ctx).done !== false),
    "inbox.readAll": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const filter = (bodyOf(ctx).filter as string | undefined) ?? null;
      const now = stamp();
      let count = 0;
      for (const i of inboxOf(ctx.me, p.company.id))
        if (!i.read_at && inboxMatches(i, filter, now)) {
          s.inbox.set(i.id, { read_at: now, done_at: i.done_at, snoozed_until: i.snoozed_until });
          count += 1;
        }
      return deps.json({ count });
    },
    "inbox.snooze": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const item = inboxOf(ctx.me, p.company.id).find((i) => i.id === ctx.params.id);
      if (!item) return deps.notFound();
      const until = (bodyOf(ctx).until as string | null | undefined) ?? null;
      if (until !== null && until <= stamp()) return deps.validation("until must be in the future");
      const next = { read_at: item.read_at, done_at: item.done_at, snoozed_until: until };
      storeOf(p.company.id).inbox.set(item.id, next);
      return deps.json({ ...item, ...next });
    },

    "search.query": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const q = ctx.url.searchParams.get("q") ?? "";
      const kind = ctx.url.searchParams.get("kind");
      // CHAT-SWITCH-A: Slack's filters; any filter limits the search to messages.
      const f = searchFiltersOf(ctx.url);
      if (f instanceof Response) return f;
      const filtered = Object.values(f).some((v) => v !== null);
      if (!q.trim() && !filtered) return deps.validation("a search needs words or a filter");
      const hits: SearchHit[] = [];
      const chatState = ctx.me.entitlements[p.company.id]?.chat;
      const chatOn = chatState === "active" || chatState === "trial" || chatState === "grace";
      if (kind !== "task" && chatOn)
        for (const m of s.messages) {
          if (m.deleted_at || (q.trim() && !textMatches(q, m.body_text))) continue;
          if (!messageMatches(s, m, f)) continue;
          const t = topicOfThread(s, m.thread_id);
          const ch = t && channelOfTopic(s, t);
          if (!t || !ch || !canSee(s, ctx.me, p.company.id, ch)) continue;
          // CHAT-TOPICS-SEPARATE: never a private topic the caller is not in; a message of the
          // channel's own conversation names (and opens) the channel.
          // No excerpts of a topic the caller has not joined (`topicJoinedSql`).
          const mine = membershipOf(ctx.me, p.company.id);
          if (!topicVisible(s, ch, t, mine)) continue;
          // (The agency operator, with no membership, reads every open topic: `readsWithoutJoining`.)
          if (mine !== null && !channelScoped(t, ch) && topicMemberOf(s, t.id, mine) === undefined)
            continue;
          const main = isMainTopic(t);
          hits.push({
            kind: "message",
            id: m.id,
            title: isDmKind(ch.kind) ? ch.name : main ? `#${ch.name}` : `#${ch.name} › ${t.name}`,
            snippet: m.body_text.slice(0, 200),
            created_at: m.created_at,
            board_id: null,
            task_number: null,
            thread_id: m.thread_id,
            channel_id: ch.id,
            topic_id: main ? null : t.id,
            author_membership_id: m.author_membership_id,
            parent_message_id: m.parent_message_id,
          });
        }
      if (kind !== "message" && !filtered)
        for (const t of s.tasks)
          if (textMatches(q, t.title))
            hits.push({
              kind: "task",
              id: t.id,
              title: t.title,
              snippet: t.title.slice(0, 200),
              created_at: t.created_at,
              board_id: t.board_id,
              task_number: t.number,
              thread_id: null,
              channel_id: null,
              topic_id: null,
              author_membership_id: null,
            });
      hits.sort((a, b) => b.created_at.localeCompare(a.created_at));
      return deps.json(deps.page(hits, ctx.url));
    },
    "channels.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const who = whoKey(ctx.me, p.company.id);
      const space = ctx.url.searchParams.get("space_id");
      const archived = ctx.url.searchParams.get("include_archived") === "true";
      // WF-FIX (2026-10-26): `member_id`: only the channels that membership is in (a DE's
      // profile), among the caller's visible ones — the real API's EXISTS on channel_member.
      const memberId = ctx.url.searchParams.get("member_id");
      if (memberId !== null && !asUuid(memberId))
        return deps.validation("member_id must be a membership id");
      const rows = s.channels
        .filter(
          (c) =>
            canSee(s, ctx.me, p.company.id, c) &&
            (!space || c.space_id === space) &&
            (archived || !c.archived_at) &&
            (!memberId || isChannelMember(s, ctx.me, c, memberId)),
        )
        .map((c) => sidebarRow(s, who, c, p));
      return deps.json(deps.page(rows, ctx.url));
    },
    "channels.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"channels.create">["body"];
      const space = s.spaces.find((x) => x.id === b.space_id);
      if (!space) return deps.validation("space_id must name a space you can see");
      const name = String(b.name).trim().toLowerCase().replace(/\s+/g, "-");
      if (s.channels.some((c) => c.space_id === space.id && c.name === name && !c.archived_at))
        return conflict(`#${name} already exists in ${space.name}`);
      if (b.shares?.length && b.is_private)
        return deps.validation("shares: a private channel is shared by adding members");
      const badShare = b.shares ? shareProblem(b.shares, space.id, s.spaces) : null;
      if (badShare) return deps.validation(badShare);
      const when = stamp();
      const row: Channel = {
        company_id: w.p.company.id,
        id: nextId(0xc110),
        space_id: space.id,
        kind: b.kind ?? "space",
        name,
        description: b.description ?? null,
        board_id: null,
        team_id: null,
        posting_policy: b.posting_policy ?? "everyone",
        topic_policy: b.topic_policy ?? "everyone",
        broadcast_policy: "everyone",
        default_notify_level: b.default_notify_level ?? "all",
        is_private: b.is_private ?? false,
        is_agent_room: b.kind === "agent_room",
        is_default: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: null,
        archived_at: null,
        actor_kind: "person",
        actor_membership_id: w.who,
        created_at: when,
        updated_at: when,
      };
      s.channels.push(row);
      if (row.is_private)
        for (const m of new Set([w.who, ...(b.member_ids ?? [])]))
          s.members.push(member(row.company_id, row.id, m, when));
      s.prefs.set(`${w.who}|${row.id}`, {
        channel_id: row.id,
        joined: true,
        muted: false,
        notify: "all",
        updated_at: when,
      });
      if (b.shares?.length) applyShares(s.shares, "channel", row.id, b.shares, when);
      return deps.json(row, 201);
    },
    // MULTI-SPACE-ACCESS: every share of a channel the caller sees (the demo sees every space).
    "channels.shares.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const only = ctx.url.searchParams.get("object_id");
      const data = s.shares.filter(
        (x) =>
          (!only || x.object_id === only) &&
          visibleChannel(ctx, x.object_id) !== null &&
          s.spaces.some((sp) => sp.id === x.space_id && !sp.archived_at),
      );
      return deps.json({ data });
    },
    "channels.shares.set": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.ch.is_private || v.ch.kind === "dm" || v.ch.kind === "group_dm")
        return deps.validation("id: a private channel is shared by adding members");
      if (v.ch.archived_at) return conflict("The channel is archived.");
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"channels.shares.set">["body"];
      const bad = shareProblem(b?.shares, v.ch.space_id, s.spaces);
      if (bad) return deps.validation(bad);
      return deps.json({ data: applyShares(s.shares, "channel", v.ch.id, b.shares, stamp()) });
    },
    "channels.get": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      return v ? deps.json(v.ch) : deps.notFound();
    },
    "channels.files": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const kind = ctx.url.searchParams.get("kind");
      const from = ctx.url.searchParams.get("from");
      const rows: ChannelFile[] = [];
      for (const f of v.s.chatFiles) {
        if (f.gone || !f.view.message_id) continue;
        const m = v.s.messages.find((x) => x.id === f.view.message_id);
        if (!m || m.deleted_at) continue;
        const t = topicOfThread(v.s, m.thread_id);
        if (!t || t.channel_id !== v.ch.id) continue;
        // CHAT-TOPICS-SEPARATE: the channel's own files and its joined topics' (`topicJoinedSql`).
        const mine = ctx.principal ? membershipOf(ctx.me, ctx.principal.company.id) : null;
        if (
          mine !== null &&
          !channelScoped(t, v.ch) &&
          topicMemberOf(v.s, t.id, mine) === undefined
        )
          continue;
        const k = fileKindOf(f.view.mime);
        if (kind && k !== kind) continue;
        if (from && f.view.uploader.membership_id !== from) continue;
        rows.push({ ...f.view, kind: k, topic_id: t.id, parent_message_id: m.parent_message_id });
      }
      rows.sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
      return deps.json(deps.page(rows, ctx.url));
    },
    "channels.update": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.ch.archived_at) return conflict("the channel is archived");
      if (!managesChannel(v.s, w, v.ch))
        return forbidden("Only the channel's admins change its settings.");
      const b = bodyOf(ctx) as OperationInput<"channels.update">["body"];
      if (b.name !== undefined) v.ch.name = b.name;
      if (b.description !== undefined) v.ch.description = b.description;
      if (b.is_private !== undefined) v.ch.is_private = b.is_private;
      if (b.is_agent_room !== undefined) v.ch.is_agent_room = b.is_agent_room;
      if (b.posting_policy !== undefined) v.ch.posting_policy = b.posting_policy;
      if (b.topic_policy !== undefined) v.ch.topic_policy = b.topic_policy;
      if (b.broadcast_policy !== undefined) v.ch.broadcast_policy = b.broadcast_policy;
      if (b.default_notify_level !== undefined) v.ch.default_notify_level = b.default_notify_level;
      v.ch.updated_at = stamp();
      return deps.json(v.ch);
    },
    "channels.archive": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.ch.archived_at) return conflict("already archived");
      if (!managesChannel(v.s, w, v.ch)) return forbidden("Only the channel's admins archive it.");
      v.ch.archived_at = stamp();
      return deps.json(v.ch);
    },
    // CHAT-CONTROLS: restore, delete (a tombstone: the channel leaves the store, its messages
    // stay) and a member's channel role; the channel's managers only.
    "channels.unarchive": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (!v.ch.archived_at) return conflict("not archived");
      if (!managesChannel(v.s, w, v.ch)) return forbidden("Only the channel's admins restore it.");
      v.ch.archived_at = null;
      v.ch.updated_at = stamp();
      return deps.json(v.ch);
    },
    "channels.delete": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.ch.is_default || v.ch.kind === "board_home" || isDmKind(v.ch.kind))
        return conflict("Default channels, board channels and DMs cannot be deleted.");
      if (!managesChannel(v.s, w, v.ch)) return forbidden("Only the channel's admins delete it.");
      const confirm = (ctx.url.searchParams.get("confirm") ?? "").trim().toLowerCase();
      if (confirm !== v.ch.name.toLowerCase())
        return deps.validation("confirm must be the channel's name");
      v.s.channels = v.s.channels.filter((c) => c.id !== v.ch.id);
      v.s.members = v.s.members.filter((m) => m.channel_id !== v.ch.id);
      return new Response(null, { status: 204 });
    },
    "channels.updateMember": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (isDmKind(v.ch.kind)) return forbidden("DMs have no admins.");
      if (!managesChannel(v.s, w, v.ch))
        return forbidden("Only the channel's admins change a member's role.");
      const b = bodyOf(ctx) as OperationInput<"channels.updateMember">["body"];
      // A public channel's subscriber has no explicit row until a role is set on it.
      let row = v.s.members.find(
        (m) => m.channel_id === v.ch.id && m.membership_id === ctx.params.membership_id,
      );
      if (!row) {
        const joined =
          ctx.params.membership_id !== undefined &&
          prefsOf(v.s, ctx.params.membership_id, v.ch).joined;
        if (!joined) return deps.notFound();
        row = member(v.ch.company_id, v.ch.id, ctx.params.membership_id ?? "", stamp());
        v.s.members.push(row);
      }
      row.role = b.role;
      row.updated_at = stamp();
      return deps.json(row);
    },
    "channels.topics": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v || !ctx.principal) return deps.notFound();
      const who = whoKey(ctx.me, ctx.principal.company.id);
      // Archived topics stay out unless one of the channel's managers asks (CHAT-UX).
      const w = writer(ctx);
      const withArchived =
        ctx.url.searchParams.get("include_archived") === "true" &&
        !(w instanceof Response) &&
        managesChannel(v.s, w, v.ch);
      // CHAT-TOPICS-SEPARATE: a private topic only for its members; unread only for members.
      const mine = membershipOf(ctx.me, ctx.principal.company.id);
      const rows = v.s.topics
        .filter(
          (t) =>
            t.channel_id === v.ch.id &&
            (withArchived || t.archived_at === null) &&
            topicVisible(v.s, v.ch, t, mine),
        )
        .sort((a, b) => (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""))
        .map((t) => ({
          ...topicDetail(v.s, ctx.me, v.ch, t),
          unread_count: unreadIn(v.s, who, v.ch, t.id),
          following: v.s.follows.some(
            (f) => f.membership_id === who && f.subject_kind === "topic" && f.subject_id === t.id,
          ),
          member_count: channelScoped(t, v.ch)
            ? 0
            : v.s.topicMembers.filter((r) => r.topic_id === t.id).length,
        }));
      return deps.json(deps.page(rows, ctx.url));
    },
    "channels.createTopic": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v || !ctx.principal) return deps.notFound();
      const name = String(bodyOf(ctx).name ?? "").trim();
      if (!name) return deps.validation("name must not be empty");
      const who = membershipOf(ctx.me, ctx.principal.company.id);
      const existing = v.s.topics.find(
        (t) =>
          t.channel_id === v.ch.id &&
          t.name.toLowerCase() === name.toLowerCase() &&
          topicVisible(v.s, v.ch, t, who),
      );
      if (existing) return deps.json(topicDetail(v.s, ctx.me, v.ch, existing), 201);
      // CHAT-TOPICS-SEPARATE: "(no topic)" makes the channel's own conversation (posting is
      // enough); another name a topic whose opener is its first member.
      const { row } = insertTopic(v.s, v.ch, isMainTopic({ name }) ? MAIN_TOPIC : name, who);
      if (who) addTopicMembers(v.s, v.ch, row, [who], "creator", who);
      return deps.json(topicDetail(v.s, ctx.me, v.ch, row), 201);
    },
    "channels.members": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      // A public channel's members are its subscribers (the real API's `channel_member`): the
      // explicit rows, everyone whose saved settings say joined, and (a default channel) the
      // caller, who is in it without browsing.
      const rows = v.s.members.filter((m) => m.channel_id === v.ch.id);
      const seen = new Set(rows.map((m) => m.membership_id));
      const add = (who: string) => {
        if (seen.has(who)) return;
        seen.add(who);
        rows.push(member(v.ch.company_id, v.ch.id, who, v.ch.created_at));
      };
      for (const [key, p] of v.s.prefs)
        if (p.channel_id === v.ch.id && p.joined) add(key.split("|")[0] ?? "");
      const me = membershipOf(ctx.me, v.ch.company_id);
      if (me && prefsOf(v.s, me, v.ch).joined) add(me);
      return deps.json(deps.page(rows, ctx.url));
    },
    "channels.addMember": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const id = String(bodyOf(ctx).membership_id ?? "");
      if (!asUuid(id)) return deps.validation("membership_id must be a membership id");
      // WF-FIX (2026-10-26): the real API's rules — `access.invite` (403), and only an active
      // membership joins (a paused or retired DE's is not: 404, `activeMembers`).
      if (!ctx.principal || !canInvite(v.s, ctx.principal, v.ch))
        return forbidden("Only those who post in this channel add members to it.");
      const de = deps.desOf(v.ch.company_id).find((d) => d.membership_id === id);
      if (de && (de.status === "paused" || de.status === "retired" || de.status === "not_deployed"))
        return deps.notFound();
      if (v.s.members.some((m) => m.channel_id === v.ch.id && m.membership_id === id))
        return conflict("already a member");
      const row = member(v.ch.company_id, v.ch.id, id, stamp());
      v.s.members.push(row);
      return deps.json(row, 201);
    },
    "channels.removeMember": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      // WF-FIX (2026-10-26): the real API's rule — never a DM; its managers, or the member itself.
      if (isDmKind(v.ch.kind)) return forbidden("A direct message keeps its members.");
      const self = membershipOf(ctx.me, v.ch.company_id);
      if (
        !ctx.principal ||
        !(canManage(v.s, ctx.principal, v.ch) || (self && self === ctx.params.membership_id))
      )
        return forbidden("Only the channel's admins remove others.");
      v.s.members = v.s.members.filter(
        (m) => !(m.channel_id === v.ch.id && m.membership_id === ctx.params.membership_id),
      );
      return new Response(null, { status: 204 });
    },
    "channels.takeOver": (ctx) => takeOver(ctx, "channel", true),
    "channels.release": (ctx) => takeOver(ctx, "channel", false),
    "channels.read": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v || !ctx.principal) return deps.notFound();
      const m = v.s.messages.find((x) => x.id === bodyOf(ctx).message_id);
      if (!m) return deps.validation("message_id must name a message of this channel");
      // CHAT-TOPICS-SEPARATE: a topic's message moves the caller's position in that topic (a
      // member's only), the channel's own conversation its channel position.
      const t = topicOfThread(v.s, m.thread_id);
      if (t && !channelScoped(t, v.ch)) {
        const row = topicMemberOf(v.s, t.id, membershipOf(ctx.me, ctx.principal.company.id));
        if (row && (row.last_read_at ?? "") < m.created_at) {
          row.last_read_at = m.created_at;
          row.last_read_message_id = m.id;
        }
        return new Response(null, { status: 204 });
      }
      const key = `${whoKey(ctx.me, ctx.principal.company.id)}|${v.ch.id}`;
      // Read positions never move backwards.
      if ((v.s.reads.get(key) ?? "") < m.created_at) v.s.reads.set(key, m.created_at);
      return new Response(null, { status: 204 });
    },
    "channels.myPrefs": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const who = whoKey(ctx.me, p.company.id);
      const rows = s.channels
        .filter((c) => canSee(s, ctx.me, p.company.id, c))
        .map((c) => prefsOf(s, who, c))
        .filter((x) => s.prefs.has(`${who}|${x.channel_id}`) || x.joined);
      return deps.json(deps.page(rows, ctx.url));
    },
    "channels.setPrefs": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"channels.setPrefs">["body"];
      const prior = prefsOf(v.s, w.who, v.ch);
      const row: ChannelPrefs = {
        channel_id: v.ch.id,
        joined: b.joined ?? prior.joined,
        muted: b.muted ?? prior.muted,
        notify: b.notify ?? prior.notify,
        updated_at: stamp(),
      };
      if (b.joined === false && v.ch.is_private)
        v.s.members = v.s.members.filter(
          (m) => !(m.channel_id === v.ch.id && m.membership_id === w.who),
        );
      v.s.prefs.set(`${w.who}|${v.ch.id}`, row);
      return deps.json(row);
    },
    // M2-02's subscription routes, over the same per-person state as M2-06's prefs.
    "channels.join": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const prior = prefsOf(v.s, w.who, v.ch);
      v.s.prefs.set(`${w.who}|${v.ch.id}`, { ...prior, joined: true, updated_at: stamp() });
      return deps.json(subscriptionOf(v.s, w.who, v.ch));
    },
    "channels.leave": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.ch.kind === "dm" || v.ch.kind === "group_dm")
        return conflict("a direct message cannot be left");
      const prior = prefsOf(v.s, w.who, v.ch);
      v.s.prefs.set(`${w.who}|${v.ch.id}`, { ...prior, joined: false, updated_at: stamp() });
      if (v.ch.is_private)
        v.s.members = v.s.members.filter(
          (m) => !(m.channel_id === v.ch.id && m.membership_id === w.who),
        );
      return new Response(null, { status: 204 });
    },
    "channels.subscription": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"channels.subscription">["body"];
      const prior = prefsOf(v.s, w.who, v.ch);
      v.s.prefs.set(`${w.who}|${v.ch.id}`, {
        ...prior,
        muted: b.muted ?? prior.muted,
        notify: b.notify_level ?? prior.notify,
        updated_at: stamp(),
      });
      if (b.following !== undefined) setFollow(v.s, w, "channel", v.ch.id, b.following);
      return deps.json(subscriptionOf(v.s, w.who, v.ch));
    },
    "topics.subscription": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"topics.subscription">["body"];
      setFollow(v.s, w, "topic", v.t.id, b.following);
      return deps.json({ topic_id: v.t.id, membership_id: w.who, following: b.following });
    },
    "channels.agentRules": (ctx) => {
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      return deps.json(
        v.s.rules.get(v.ch.id) ?? {
          channel_id: v.ch.id,
          wake: "mentions",
          initiate: "allowed",
          instructions: null,
          updated_at: null,
        },
      );
    },
    "channels.setAgentRules": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleChannel(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      // WF-FIX (2026-10-26): the channel's managers only, as the API (`access.manage`).
      if (!canManage(v.s, w.p, v.ch))
        return forbidden("Only the channel's admins change the agents' rules.");
      const b = bodyOf(ctx) as OperationInput<"channels.setAgentRules">["body"];
      if (!["mentions", "topic", "silent"].includes(String(b.wake)))
        return deps.validation("wake must be mentions, topic or silent");
      if (b.initiate !== undefined && !["allowed", "routines", "off"].includes(String(b.initiate)))
        return deps.validation("initiate must be allowed, routines or off");
      const row: AgentRules = {
        channel_id: v.ch.id,
        wake: b.wake,
        // DE-INITIATE: absent keeps the stored rule.
        initiate: b.initiate ?? v.s.rules.get(v.ch.id)?.initiate ?? "allowed",
        instructions: b.instructions?.trim() || null,
        updated_at: stamp(),
      };
      v.s.rules.set(v.ch.id, row);
      return deps.json(row);
    },

    "topics.get": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      return v ? deps.json(topicDetail(v.s, ctx.me, v.ch, v.t)) : deps.notFound();
    },
    "topics.rename": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (isMainTopic(v.t)) return conflict(MAIN_FIXED);
      const name = String(bodyOf(ctx).name ?? "").trim();
      if (!name) return deps.validation("name must not be empty");
      if (isMainTopic({ name })) return conflict("that name is the channel's own conversation");
      v.t.name = name;
      v.t.updated_at = stamp();
      return deps.json(topicDetail(v.s, ctx.me, v.ch, v.t));
    },
    "topics.resolve": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      v.t.status = bodyOf(ctx).resolved ? "resolved" : "open";
      v.t.updated_at = stamp();
      return deps.json(topicDetail(v.s, ctx.me, v.ch, v.t));
    },
    // CHAT-UX lifecycle: any poster pins; the channel's managers unpin and retire (the API's
    // `access.manage`: a channel admin, or a company owner or admin).
    "topics.pin": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (isMainTopic(v.t)) return conflict(MAIN_FIXED);
      const pinned = bodyOf(ctx).pinned === true;
      if (!pinned && !managesChannel(v.s, w, v.ch))
        return forbidden("Only the channel's admins unpin a topic.");
      if (pinned !== (v.t.pinned_at !== null)) {
        v.t.pinned_at = pinned ? stamp() : null;
        v.t.updated_at = stamp();
      }
      return deps.json(topicDetail(v.s, ctx.me, v.ch, v.t));
    },
    "topics.archive": (ctx) => archiveTopic(ctx, true),
    "topics.restore": (ctx) => archiveTopic(ctx, false),
    // Delete (managers): the messages become tombstones; the topic leaves the store.
    "topics.delete": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (isMainTopic(v.t)) return conflict(MAIN_FIXED);
      if (!managesChannel(v.s, w, v.ch))
        return forbidden("Only the channel's admins delete a topic.");
      const th = threadOfTopic(v.s, v.t.id);
      if (th)
        for (const m of v.s.messages)
          if (m.thread_id === th.id && !m.deleted_at) {
            m.deleted_at = stamp();
            m.body = [];
            m.body_text = "";
          }
      v.s.topics = v.s.topics.filter((t) => t !== v.t);
      return new Response(null, { status: 204 });
    },
    "topics.move": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (isMainTopic(v.t)) return conflict(MAIN_FIXED);
      const to = visibleChannel(ctx, String(bodyOf(ctx).channel_id ?? ""));
      if (!to) return deps.validation("channel_id must name a channel you can see");
      v.t.channel_id = to.ch.id;
      return deps.json(topicDetail(v.s, ctx.me, to.ch, v.t));
    },
    "topics.split": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"topics.split">["body"];
      const name = String(b.name ?? "").trim();
      if (!name) return deps.validation("name must not be empty");
      if (v.s.topics.some((t) => t.channel_id === v.ch.id && t.name === name))
        return conflict(`a topic "${name}" already exists`);
      const from = threadOfTopic(v.s, v.t.id);
      const moving = v.s.messages.filter(
        (m) => b.message_ids.includes(m.id) && m.thread_id === from?.id,
      );
      if (moving.length !== b.message_ids.length)
        return deps.validation("every message must belong to this topic");
      const when = stamp();
      const row: Topic = {
        ...v.t,
        id: nextId(0xc120),
        name,
        message_count: 0,
        created_at: when,
        updated_at: when,
      };
      const thread: Thread = {
        company_id: v.ch.company_id,
        id: nextId(0xc130),
        kind: "topic",
        subject_id: row.id,
        last_message_at: null,
        message_count: 0,
        created_at: when,
        updated_at: when,
      };
      v.s.topics.push(row);
      v.s.threads.push(thread);
      for (const m of moving) {
        m.thread_id = thread.id;
        touchThread(v.s, m);
      }
      if (from) {
        from.message_count -= moving.length;
        v.t.message_count -= moving.length;
      }
      // CHAT-TOPICS-SEPARATE: the split's opener is its first member, with the movers' authors.
      const w = writer(ctx);
      const by = w instanceof Response ? null : w.who;
      row.is_private = false;
      if (by) addTopicMembers(v.s, v.ch, row, [by], "creator", by);
      addTopicMembers(
        v.s,
        v.ch,
        row,
        moving.flatMap((m) => (m.author_membership_id ? [m.author_membership_id] : [])),
        "posted",
        by,
      );
      return deps.json(topicDetail(v.s, ctx.me, v.ch, row), 201);
    },
    "topics.members": (ctx) => {
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (channelScoped(v.t, v.ch))
        return conflict("the channel's own conversation has the channel's members");
      return deps.json({ data: v.s.topicMembers.filter((r) => r.topic_id === v.t.id) });
    },
    "topics.updateMembers": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (channelScoped(v.t, v.ch))
        return conflict("the channel's own conversation has the channel's members");
      const b = bodyOf(ctx) as OperationInput<"topics.updateMembers">["body"];
      const add = [...new Set(b.add ?? [])];
      const remove = [...new Set(b.remove ?? [])];
      if (add.length + remove.length === 0)
        return deps.validation("add or remove at least one member");
      if (![...add, ...remove].every(asUuid)) return deps.validation("ids must be membership ids");
      const member = topicMemberOf(v.s, v.t.id, w.who) !== undefined;
      const manages = managesChannel(v.s, w, v.ch);
      const opener = isOpenerOf(v.s, v.t.id, w.who);
      // Joining is adding yourself (an open topic); adding others needs membership or manage.
      if (add.some((id) => id !== w.who) && !member && !manages)
        return forbidden("Join the topic to add people to it.");
      if (remove.some((id) => id !== w.who) && !opener && !manages)
        return forbidden("Only the topic's opener or the channel's admins remove others.");
      // A private channel's topic takes only the channel's members.
      if (v.ch.is_private && add.some((id) => !isChannelMember(v.s, ctx.me, v.ch, id)))
        return deps.validation("only the channel's members can join its topics");
      addTopicMembers(
        v.s,
        v.ch,
        v.t,
        add,
        add.length === 1 && add[0] === w.who ? "joined" : "added",
        w.who,
      );
      v.s.topicMembers = v.s.topicMembers.filter(
        (r) => r.topic_id !== v.t.id || !remove.includes(r.membership_id),
      );
      return deps.json({ data: v.s.topicMembers.filter((r) => r.topic_id === v.t.id) });
    },
    "topics.privacy": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleTopic(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (channelScoped(v.t, v.ch))
        return conflict("the channel's own conversation is never private");
      if (!isOpenerOf(v.s, v.t.id, w.who) && !managesChannel(v.s, w, v.ch))
        return forbidden("Only the topic's opener or the channel's admins make it private.");
      const on = bodyOf(ctx).private === true;
      if (on !== v.t.is_private) {
        v.t.is_private = on;
        v.t.updated_at = stamp();
        // Whoever makes it private stays in it.
        if (on) addTopicMembers(v.s, v.ch, v.t, [w.who], "added", w.who);
      }
      return deps.json(topicDetail(v.s, ctx.me, v.ch, v.t));
    },
    "topics.takeOver": (ctx) => takeOver(ctx, "topic", true),
    "topics.release": (ctx) => takeOver(ctx, "topic", false),

    "threads.get": (ctx) => {
      const v = visibleThread(ctx, ctx.params.id);
      return v ? deps.json(v.th) : deps.notFound();
    },
    // CHAT-CONTROLS + THREAD-READS: the threads the caller follows, with unread counts.
    "threads.mine": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const rows = myThreads(ctx, s).map((r) => r.row);
      return deps.json(deps.page(rows, ctx.url));
    },
    "threads.readAll": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      let moved = 0;
      for (const r of myThreads(ctx, s))
        if (r.row.unread_count > 0 && r.lastReply) {
          moveThreadRead(s, w.who, r.row.root.id, r.lastReply);
          moved++;
        }
      return deps.json({ threads: moved });
    },
    "threads.read": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found || found.m.parent_message_id || found.m.deleted_at) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"threads.read">["body"];
      const replies = found.s.messages
        .filter((m) => m.parent_message_id === found.m.id)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      const upTo = b.reply_id ? replies.find((r) => r.id === b.reply_id) : replies.at(-1);
      if (b.reply_id && !upTo) return deps.notFound();
      moveThreadRead(found.s, w.who, found.m.id, upTo ?? found.m);
      return deps.json(threadState(found.s, w.who, found.m));
    },
    "threads.follow": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found || found.m.parent_message_id || found.m.deleted_at) return deps.notFound();
      const key = `${w.who}|${found.m.id}`;
      const row = found.s.threadReads.get(key) ?? { at: null, reply: null, following: null };
      found.s.threadReads.set(key, {
        ...row,
        following: Boolean((bodyOf(ctx) as { following: boolean }).following),
      });
      return deps.json(threadState(found.s, w.who, found.m));
    },
    "messages.list": (ctx) => {
      const v = visibleThread(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const all = v.s.messages
        .filter((m) => m.thread_id === v.th.id)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      const limit = Math.min(200, Number(ctx.url.searchParams.get("limit") ?? 50) || 50);
      const before = ctx.url.searchParams.get("before");
      const after = ctx.url.searchParams.get("after");
      let end = all.length;
      let start = Math.max(0, end - limit);
      if (before) {
        end = all.findIndex((m) => m.id === before);
        if (end < 0) return deps.problem(400, "invalid_cursor" as never, "Invalid cursor");
        start = Math.max(0, end - limit);
      } else if (after) {
        const i = all.findIndex((m) => m.id === after);
        if (i < 0) return deps.problem(400, "invalid_cursor" as never, "Invalid cursor");
        start = i + 1;
        end = Math.min(all.length, start + limit);
      }
      const who = ctx.principal ? whoKey(ctx.me, ctx.principal.company.id) : "";
      const data = all.slice(start, end).map((m) => view(v.s, m, who));
      return deps.json({
        data,
        next_cursor: start > 0 ? (data[0]?.id ?? null) : null,
        prev_cursor: end < all.length ? (data.at(-1)?.id ?? null) : null,
      });
    },
    "messages.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleThread(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"messages.create">["body"];
      const fileIds = [...new Set(b.attachment_ids ?? [])];
      const filesOnly = b.body === undefined && b.body_markdown === undefined && fileIds.length > 0;
      if (!filesOnly && (b.body === undefined) === (b.body_markdown === undefined))
        return deps.validation("exactly one of body and body_markdown (or files only)");
      const key = `${w.p.company.id}|${b.client_id}`;
      const replay = v.s.clientIds.get(key);
      const prior = replay && v.s.messages.find((m) => m.id === replay);
      if (prior)
        return deps.json(
          {
            ...prior,
            agent_wakes: v.s.wakes.get(prior.id) ?? [],
            attachments: filesOfMessage(v.s, prior),
          },
          201,
        );
      // CHAT-FILES: the caller's staged files, staged here or for a new topic, not sent yet.
      if (fileIds.length > 20) return deps.validation("attachment_ids: at most 20 files");
      const staged = fileIds.map((id) =>
        v.s.chatFiles.find(
          (f) =>
            f.view.id === id &&
            !f.gone &&
            f.view.message_id === null &&
            f.view.uploader.membership_id === w.who &&
            (f.view.thread_id === null || f.view.thread_id === v.th.id),
        ),
      );
      if (staged.some((f) => !f))
        return deps.validation(
          "attachment_ids: a file is not one you staged for this thread, was already sent, or is gone",
        );
      // A taken-over room: agents park; people keep talking.
      const text = filesOnly
        ? ""
        : b.body !== undefined
          ? blocksText(b.body)
          : String(b.body_markdown).trim();
      if (!text && !filesOnly) return deps.validation("a message needs text");
      // CHAT-SWITCH-A: `@channel` / `@here`, as the API: a channel's (never a DM's), and only as
      // its broadcast policy allows.
      if (b.broadcast !== undefined) {
        const t = topicOfThread(v.s, v.th.id);
        const ch = t ? channelOfTopic(v.s, t) : undefined;
        if (!ch || ch.kind === "dm" || ch.kind === "group_dm")
          return deps.validation("@channel and @here are for channels");
        if (ch.broadcast_policy === "admins" && !managesChannel(v.s, w, ch))
          return forbidden("Only this channel's admins can use @channel and @here.");
      }
      // THREAD-WAKE: the composer's pick: an agent (woken as a mention) or "none" (a note).
      const addressee = b.addressee === undefined ? undefined : String(b.addressee).toLowerCase();
      if (
        addressee !== undefined &&
        addressee !== "none" &&
        !deps.desOf(w.p.company.id).some((d) => d.membership_id === addressee)
      )
        return deps.validation("addressee must be an agent's membership id or none");
      // As the API: the pick is kept as a mention of it, unless the text names an agent.
      const asked = b.mentions ?? [];
      const namesAgent = mentionedDes(w.p.company.id, asked).length > 0;
      const mentions =
        addressee && addressee !== "none" && !namesAgent && !asked.includes(`de:${addressee}`)
          ? [...asked, `de:${addressee}`]
          : asked;
      const row: Message = {
        company_id: w.p.company.id,
        created_at: stamp(),
        id: nextId(0xc140),
        thread_id: v.th.id,
        author_membership_id: w.who,
        body: b.body ?? markdownToBlocks(text),
        body_text: b.body !== undefined ? text : bodyTextOf(markdownToBlocks(text)),
        author_kind: "person",
        mentions,
        card_refs: b.card_refs ?? [],
        parent_message_id: b.parent_message_id ?? null,
        client_id: b.client_id,
        edited_at: null,
        deleted_at: null,
        pinned_at: null,
        ...NO_ACTOR,
        actor_kind: "person",
        actor_membership_id: w.who,
      };
      v.s.messages.push(row);
      v.s.clientIds.set(key, row.id);
      for (const f of staged) if (f) f.view = { ...f.view, message_id: row.id, thread_id: v.th.id };
      touchThread(v.s, row);
      noteThreadPost(v.s, row);
      // A person's post in an agent room restarts the agent-turn count and wakes the holder.
      const topic = v.s.topics.find((t) => t.id === v.th.subject_id);
      const room = topic && roomOfTopic(v.s, topic);
      if (room) onHumanPost(v.s, room);
      // CHAT-TOPICS-SEPARATE: posting in a topic makes the author a member, mentioning makes the
      // mentioned members; posting reads the place up to one's own message.
      addTopicMembers(v.s, v.ch, v.t, [w.who], "posted", w.who);
      addTopicMembers(
        v.s,
        v.ch,
        v.t,
        mentionedIds(row.mentions).filter((id) => id !== w.who),
        "mentioned",
        w.who,
      );
      const mineIn = channelScoped(v.t, v.ch) ? undefined : topicMemberOf(v.s, v.t.id, w.who);
      if (mineIn) {
        if ((mineIn.last_read_at ?? "") < row.created_at) {
          mineIn.last_read_at = row.created_at;
          mineIn.last_read_message_id = row.id;
        }
      } else {
        const readKey = `${w.who}|${v.ch.id}`;
        if ((v.s.reads.get(readKey) ?? "") < row.created_at) v.s.reads.set(readKey, row.created_at);
      }
      // AGENT-MENTION: what became of each DE it mentioned, at once.
      const nameOf = (id: string) => memberInfo(v.s, ctx, row.company_id, id).display_name;
      // THREAD-WAKE: a follow-up naming no agent asks the conversation's one addressee (not a
      // note, not in a DM or a silent/topic channel; the fake keeps no pending-wake collapse).
      const wakeRule = v.s.rules.get(v.ch.id)?.wake ?? "mentions";
      const target =
        addressee !== "none" &&
        // CHAT-THREAD-AGENTS: a reply naming anyone (people only too) asks only those named.
        row.mentions.length === 0 &&
        // A reply thread picks one participant on any channel that is not silent (DM, topic).
        (row.parent_message_id
          ? wakeRule !== "silent"
          : !isDmKind(v.ch.kind) && wakeRule === "mentions") &&
        v.th.kind === "topic"
          ? addressingIn(v.s, w.p.company.id, v.th.id, row.parent_message_id, row.id).addressee
          : null;
      const asking =
        target && target !== w.who ? { ...row, mentions: [...row.mentions, `de:${target}`] } : row;
      const agent_wakes = wakeMentioned(v.s, v.ch, asking, nameOf);
      // CHAT-THREAD-AGENTS: "Also send to #channel" — as the API, the server posts the copy on
      // the main line, quoting the root (a note: it wakes nobody).
      if (b.also_to_channel && row.parent_message_id && !isDmKind(v.ch.kind)) {
        const rootRow = v.s.messages.find((m) => m.id === row.parent_message_id);
        const excerpt = rootRow?.body_text.split("\n")[0]?.slice(0, 120) || "(a thread)";
        const n = staged.length;
        const said =
          row.body_text.trim() || (n ? "Sent files in the thread." : "Replied in the thread.");
        const note = n ? `\n\n_${n} file${n === 1 ? "" : "s"} in the thread_` : "";
        const copyText = `${said}${note}\n\n> ${excerpt}`;
        const copy: Message = {
          ...row,
          id: nextId(0xc140),
          created_at: stamp(),
          body: markdownToBlocks(copyText),
          body_text: bodyTextOf(markdownToBlocks(copyText)),
          mentions: [],
          card_refs: [],
          parent_message_id: null,
          client_id: `channel-copy:${row.id}`,
        };
        v.s.messages.push(copy);
        touchThread(v.s, copy);
      }
      return deps.json({ ...row, agent_wakes, attachments: filesOfMessage(v.s, row) }, 201);
    },
    "messages.wake": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found || found.m.deleted_at) return deps.notFound();
      const { s, ch, m } = found;
      if (m.author_membership_id !== w.who && !managesChannel(s, w, ch))
        return forbidden("the message's author or the channel's managers ask again");
      const id = String(bodyOf(ctx).membership_id ?? "").toLowerCase();
      if (!asUuid(id)) return deps.validation("membership_id must be a membership id");
      // THREAD-WAKE: or the addressee the message was sent to (its ask is on record), as the API.
      const recorded = (s.wakes.get(m.id) ?? []).map((x) => `de:${x.membership_id}`);
      const de = mentionedDes(w.p.company.id, [
        ...m.mentions,
        ...dmRefs(s, ch, m),
        ...recorded,
      ]).find((d) => d.membership_id === id && d.membership_id !== m.author_membership_id);
      // CHAT-DE: a DM's crew, asked by its owner's message (the readiness rule is the post's).
      const crew = de ? undefined : dmCrews(s, ch, m).find((c) => c === id);
      if (!de && !crew) return deps.notFound();
      const list = s.wakes.get(m.id) ?? [];
      const current = list.find((x) => x.membership_id === id);
      if (current && !NOT_STARTED.has(current.state)) return deps.json(current);
      const next = de
        ? wakeRow(de, skipOf(s, ch, de), stamp())
        : // The requester's ask now (wake.ts `wakeAgain` → `skipOf`): only the crew's owner.
          crewWakeRow(
            s,
            ch,
            id,
            w.who,
            memberInfo(s, ctx, w.p.company.id, id).display_name,
            stamp(),
          );
      if (current && current.state === next.state && current.reason === next.reason)
        return deps.json(current);
      s.wakes.set(m.id, [...list.filter((x) => x.membership_id !== id), next]);
      return deps.json(next);
    },
    "threads.agents": (ctx) => {
      const v = visibleThread(ctx, ctx.params.id);
      if (!v || !ctx.principal) return deps.notFound();
      const p = ctx.principal;
      const who = membershipOf(ctx.me, p.company.id);
      const dm = isDmKind(v.ch.kind);
      const adds = !dm && who !== null && managesChannel(v.s, { p, who }, v.ch);
      // THREAD-WAKE: the conversation (`root` and its replies, else the top level): who took
      // part (posted or was named) and whom a follow-up without a mention wakes.
      const root = ctx.url.searchParams.get("root")?.toLowerCase() ?? null;
      if (root !== null && !asUuid(root)) return deps.validation("root must be a message id");
      const des = deps.desOf(p.company.id);
      const { addressee: latest, participating } = addressingIn(v.s, p.company.id, v.th.id, root);
      const wakeRule = v.s.rules.get(v.ch.id)?.wake ?? "mentions";
      const picks =
        v.th.kind === "topic" &&
        (root !== null ? wakeRule !== "silent" : !dm && wakeRule === "mentions");
      const addressee = picks ? latest : null;
      const data = des
        .filter((d) => d.status !== "retired" && d.membership_id !== who)
        .sort((a, b) => a.display_name.localeCompare(b.display_name))
        .map((de): Readiness => {
          const skip = skipOf(v.s, v.ch, de);
          return {
            membership_id: de.membership_id,
            name: de.display_name,
            agent_kind: "digital_employee",
            seat_title: de.seat_title,
            state: skip ? READINESS[skip] : "ready",
            reason: skip
              ? SKIP_TEXT[skip](de.display_name).reason
              : `A mention wakes ${de.display_name}.`,
            can_add: adds && skip === "no_access",
            can_read: deReads(v.s, v.ch, de),
            participating: participating.has(de.membership_id),
          };
        });
      return deps.json({
        channel_id: dm ? null : v.ch.id,
        channel_name: dm ? null : v.ch.name,
        data,
        // (The caller itself, a DE, is never its own addressee.)
        addressee_membership_id: addressee !== null && addressee !== who ? addressee : null,
      });
    },
    "threads.leaveAgent": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleThread(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const root = String(ctx.params.mid).toLowerCase();
      const agent = String(ctx.params.membership_id).toLowerCase();
      const r = v.s.messages.find((m) => m.id === root && m.thread_id === v.th.id);
      if (!r || r.parent_message_id !== null) return deps.notFound();
      const { participating } = addressingIn(v.s, w.p.company.id, v.th.id, root);
      const key = `${root}|${agent}`;
      if (!participating.has(agent) && !v.s.mutedAgents.has(key)) return deps.notFound();
      if (participating.has(agent)) v.s.mutedAgents.set(key, stamp());
      return new Response(null, { status: 204 });
    },
    "messages.update": (ctx) => {
      const found = ownMessage(ctx);
      if (found instanceof Response) return found;
      const { m } = found;
      if (deps.now().getTime() - new Date(m.created_at).getTime() > EDIT_WINDOW_MS)
        return deps.problem(
          409,
          "edit_window_closed" as never,
          "Edit window closed",
          "Messages can be edited for 15 minutes after posting.",
        );
      const b = bodyOf(ctx) as OperationInput<"messages.update">["body"];
      const text = b.body !== undefined ? blocksText(b.body) : String(b.body_markdown ?? "").trim();
      if (!text) return deps.validation("a message needs text");
      m.body = b.body ?? markdownToBlocks(text);
      m.body_text = b.body !== undefined ? text : bodyTextOf(markdownToBlocks(text));
      if (b.mentions) m.mentions = b.mentions;
      m.edited_at = stamp();
      return deps.json(m);
    },
    "messages.delete": (ctx) => {
      const found = ownMessage(ctx);
      if (found instanceof Response) return found;
      const { m } = found;
      m.deleted_at = stamp();
      m.body = [];
      m.body_text = "";
      // CHAT-FILES: its files go with it.
      const s = storeOf(m.company_id);
      for (const f of s.chatFiles) if (f.view.message_id === m.id) f.gone = true;
      return new Response(null, { status: 204 });
    },
    "messages.react": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found) return deps.notFound();
      const emoji = String(bodyOf(ctx).emoji ?? "");
      if (!emoji) return deps.validation("emoji must not be empty");
      if (
        found.s.reactions.some(
          (r) => r.message_id === found.m.id && r.membership_id === w.who && r.emoji === emoji,
        )
      )
        return conflict("already reacted with that emoji");
      const row = reaction(w.p.company.id, found.m.id, w.who, emoji, stamp());
      found.s.reactions.push(row);
      return deps.json(row, 201);
    },
    "messages.unreact": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found) return deps.notFound();
      const emoji = decodeURIComponent(ctx.params.emoji ?? "");
      found.s.reactions = found.s.reactions.filter(
        (r) => !(r.message_id === found.m.id && r.membership_id === w.who && r.emoji === emoji),
      );
      return new Response(null, { status: 204 });
    },
    "messages.pin": (ctx) => {
      const found = messageIn(ctx);
      if (!found) return deps.notFound();
      found.m.pinned_at = bodyOf(ctx).pinned ? stamp() : null;
      return deps.json(found.m);
    },
    "messages.makeTask": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"messages.makeTask">["body"];
      // The board may be the Work twin's (its boards list answers first): file the task here.
      const board =
        found.s.boards.find((x) => x.id === b.board_id) ??
        deps.work?.()?.boardOf(w.p.company.id, b.board_id);
      if (!board) return deps.validation("board_id must name a board you can see");
      const key = `${w.p.company.id}|task|${b.client_id}`;
      const replay = found.s.clientIds.get(key);
      const prior = replay && found.s.tasks.find((t) => t.id === replay);
      if (prior) return deps.json(stripDetail(prior), 201);
      const title = (b.title ?? found.m.body_text.split("\n")[0] ?? "").slice(0, 500) || "Untitled";
      const number = Math.max(0, ...found.s.tasks.map((t) => t.number)) + 1;
      const row = task(found.s, board, number, title, board.status_set[0]?.key ?? "todo", {
        message: found.m.id,
        ...(b.assignee_seat_ids?.[0] ? { seat: b.assignee_seat_ids[0] } : {}),
      });
      found.s.clientIds.set(key, row.id);
      // The message shows the task: its card refs gain it (06 "→ task #123").
      found.m.card_refs = [...found.m.card_refs, `task:${row.id}`];
      return deps.json(stripDetail(row), 201);
    },
    "messages.bookmark": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const found = messageIn(ctx);
      if (!found) return deps.notFound();
      const key = `${w.who}|${found.m.id}`;
      if (bodyOf(ctx).bookmarked) {
        if (!found.s.bookmarks.has(key)) found.s.bookmarks.set(key, stamp());
      } else found.s.bookmarks.delete(key);
      return new Response(null, { status: 204 });
    },
    "bookmarks.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const who = whoKey(ctx.me, p.company.id);
      const rows: Bookmark[] = [];
      for (const [key, when] of s.bookmarks) {
        const [owner, messageId] = key.split("|");
        if (owner !== who) continue;
        const m = s.messages.find((x) => x.id === messageId);
        const t = m && topicOfThread(s, m.thread_id);
        const ch = t && channelOfTopic(s, t);
        if (!m || !t || !ch || m.deleted_at || !canSee(s, ctx.me, p.company.id, ch)) continue;
        // CHAT-TOPICS-SEPARATE: no words of a topic the caller is not (or no longer) in.
        const mine = membershipOf(ctx.me, p.company.id);
        if (!channelScoped(t, ch) && topicMemberOf(s, t.id, mine) === undefined) continue;
        rows.push({
          bookmark: {
            company_id: p.company.id,
            membership_id: who,
            message_id: m.id,
            message_created_at: m.created_at,
            thread_id: m.thread_id,
            created_at: when,
          },
          message: m,
          channel_id: ch.id,
          topic_id: t.id,
        });
      }
      rows.sort((a, b) => b.bookmark.created_at.localeCompare(a.bookmark.created_at));
      return deps.json(deps.page(rows, ctx.url));
    },
    // CHAT-SWITCH-A: "Remind me about this" and `/remind` (a person's own; never fired here).
    "chatReminders.list": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return deps.notFound();
      const s = storeOf(w.p.company.id);
      const rows = (reminders.get(`${w.p.company.id}|${w.who}`) ?? [])
        .filter((r) => !r.dismissed)
        .sort((a, b) => a.at.localeCompare(b.at));
      return deps.json({ data: rows.map((r) => reminderView(s, ctx, r)) });
    },
    "chatReminders.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"chatReminders.create">["body"];
      const at = new Date(b.at).getTime();
      if (!(at > Date.now())) return deps.validation("at: must be in the future");
      if (at > Date.now() + 366 * 86_400_000) return deps.validation("at: must be within a year");
      let text = b.text?.trim() || null;
      if (b.thread_id && b.message_id) {
        const v = visibleThread(ctx, b.thread_id);
        const m = v?.s.messages.find(
          (x) => x.id === b.message_id && x.thread_id === b.thread_id && !x.deleted_at,
        );
        if (!v || !m) return deps.notFound();
        text ??= m.body_text.replace(/\s+/g, " ").trim().slice(0, 200) || "A message with files";
      }
      if (!text) return deps.validation("a reminder needs a message or a text");
      const row = {
        id: uid(0x5a, Math.floor(Math.random() * 0xffffff)),
        at: new Date(at).toISOString(),
        text,
        thread_id: b.thread_id ?? null,
        message_id: b.message_id ?? null,
        dismissed: false,
        created_at: stamp(),
      };
      const key = `${w.p.company.id}|${w.who}`;
      reminders.set(key, [...(reminders.get(key) ?? []), row]);
      return deps.json(reminderView(s, ctx, row), 201);
    },
    "chatReminders.dismiss": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const row = (reminders.get(`${w.p.company.id}|${w.who}`) ?? []).find(
        (r) => r.id === ctx.params.id,
      );
      if (!row) return deps.notFound();
      row.dismissed = true;
      return deps.json(reminderView(s, ctx, row));
    },
    // CHAT-SWITCH-B: a message link, wherever the message is now (the caller's access).
    "messages.locate": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const m = s.messages.find((x) => x.id === ctx.params.id && !x.deleted_at);
      const v = m ? visibleThread(ctx, m.thread_id) : null;
      if (!m || !v) return deps.notFound();
      const who = whoKey(ctx.me, p.company.id);
      const rootId = m.parent_message_id ?? m.id;
      const root = s.messages.find((x) => x.id === rootId);
      if (!root) return deps.notFound();
      const replies = s.messages
        .filter((x) => x.parent_message_id === rootId)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      return deps.json({
        message_id: m.id,
        thread_id: m.thread_id,
        channel_id: v.ch.id,
        topic_id: v.t.id,
        root_id: rootId,
        root: view(s, root, who),
        replies: replies.slice(0, 500).map((r) => view(s, r, who)),
        replies_truncated: replies.length > 500,
      });
    },
    // CHAT-SWITCH-B: no link cards here (the demo fetches nothing from the web).
    "messages.previews": (ctx) => {
      const v = visibleThread(ctx, ctx.params.id);
      const m = v?.s.messages.find((x) => x.id === ctx.params.mid && x.thread_id === v.th.id);
      if (!v || !m) return deps.notFound();
      const who = ctx.principal ? whoKey(ctx.me, ctx.principal.company.id) : "";
      return deps.json({ data: [], removable: m.author_membership_id === who, pending: false });
    },
    // CHAT-SWITCH-B: Send later — the person's waiting messages (none are sent in the demo).
    "scheduledMessages.list": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return deps.json({ data: [] });
      const rows = (scheduled.get(`${w.p.company.id}|${w.who}`) ?? [])
        .filter((r) => r.state === "scheduled")
        .sort((a, b) => a.send_at.localeCompare(b.send_at));
      return deps.json({ data: rows });
    },
    "scheduledMessages.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const b = bodyOf(ctx) as OperationInput<"scheduledMessages.create">["body"];
      const v = visibleThread(ctx, b.thread_id);
      if (!v) return deps.notFound();
      // As the API: a resent Schedule (same client id) answers the one already made.
      const prior = (b.client_id ? scheduledByClient.get(`${w.who}|${b.client_id}`) : undefined) as
        | { state?: string }
        | undefined;
      if (prior) return deps.json(prior, 201);
      const at = new Date(b.send_at).getTime();
      if (!(at > Date.now() + 30_000)) return deps.validation("send_at: must be in the future");
      const text = b.body !== undefined ? blocksText(b.body) : String(b.body_markdown ?? "").trim();
      if (!text) return deps.validation("the message is empty");
      const row = {
        id: nextId(0x5b),
        thread_id: v.th.id,
        channel_id: v.ch.id,
        topic_id: v.t.id,
        parent_message_id: b.parent_message_id ?? null,
        body: (b.body ?? markdownToBlocks(text)) as Record<string, unknown>[],
        body_text: text,
        send_at: new Date(at).toISOString(),
        state: "scheduled" as "scheduled" | "sent" | "cancelled" | "failed",
        message_id: null,
        failure: null,
        created_at: stamp(),
      };
      const key = `${w.p.company.id}|${w.who}`;
      scheduled.set(key, [...(scheduled.get(key) ?? []), row]);
      if (b.client_id) scheduledByClient.set(`${w.who}|${b.client_id}`, row);
      return deps.json(row, 201);
    },
    "scheduledMessages.update": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const row = (scheduled.get(`${w.p.company.id}|${w.who}`) ?? []).find(
        (r) => r.id === ctx.params.id,
      );
      if (!row) return deps.notFound();
      if (row.state !== "scheduled") return conflict("it is no longer waiting");
      const b = bodyOf(ctx) as OperationInput<"scheduledMessages.update">["body"];
      if (b.send_at !== undefined) {
        const at = new Date(b.send_at).getTime();
        if (!(at > Date.now() + 30_000)) return deps.validation("send_at: must be in the future");
        row.send_at = new Date(at).toISOString();
      }
      if (b.body !== undefined) {
        // As the API: the blocks as written.
        row.body = b.body as Record<string, unknown>[];
        row.body_text = blocksText(b.body);
        if (!row.body_text) return deps.validation("the message is empty");
      }
      if (b.body_markdown !== undefined) {
        row.body_text = b.body_markdown.trim();
        row.body = markdownToBlocks(row.body_text) as Record<string, unknown>[];
      }
      return deps.json(row);
    },
    "scheduledMessages.cancel": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const row = (scheduled.get(`${w.p.company.id}|${w.who}`) ?? []).find(
        (r) => r.id === ctx.params.id,
      );
      if (!row) return deps.notFound();
      if (row.state === "scheduled") row.state = "cancelled";
      return deps.json(row);
    },
    // CHAT-SWITCH-B: Ask needs a DE run, which the demo has not got: it says so.
    "chatAsks.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      return conflict("No digital employee can answer in this demo.");
    },
    "chatAsks.get": () => deps.notFound(),
    // CHAT-SWITCH-B: a channel's incoming webhooks (its managers); the demo never receives posts.
    "chatWebhooks.list": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const ch = s.channels.find((c) => c.id === ctx.params.id);
      if (!ch || !canSee(s, ctx.me, w.p.company.id, ch)) return deps.notFound();
      if (!managesChannel(s, w, ch)) return forbidden("Only the channel's admins manage webhooks.");
      return deps.json({ data: hooks.filter((h) => h.channel_id === ch.id) });
    },
    "chatWebhooks.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const ch = s.channels.find((c) => c.id === ctx.params.id);
      if (!ch || !canSee(s, ctx.me, w.p.company.id, ch)) return deps.notFound();
      if (!managesChannel(s, w, ch)) return forbidden("Only the channel's admins manage webhooks.");
      const name = String(bodyOf(ctx).name ?? "").trim();
      if (!name || name.length > 80) return deps.validation("name: 1 to 80 characters");
      const token = fakeToken();
      const row = {
        id: nextId(0x5c),
        target: "channel" as const,
        channel_id: ch.id as string | null,
        de_membership_id: null as string | null,
        name,
        token_hint: token.slice(-4),
        created_by_membership_id: w.who,
        post_count: 0,
        last_used_at: null,
        rotated_at: null as string | null,
        revoked_at: null as string | null,
        created_at: stamp(),
      };
      hooks.push(row);
      return deps.json(
        { webhook: row, path: `/v1/companies/${w.p.company.id}/hooks/chat/${row.id}/${token}` },
        201,
      );
    },
    "chatWebhooks.rotate": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const row = hooks.find((h) => h.id === ctx.params.id && h.revoked_at === null);
      const refused = row ? hookRefused(s, w, row) : deps.notFound();
      if (refused || !row) return refused ?? deps.notFound();
      const token = fakeToken();
      row.token_hint = token.slice(-4);
      row.rotated_at = stamp();
      return deps.json({
        webhook: row,
        path: `/v1/companies/${w.p.company.id}/hooks/chat/${row.id}/${token}`,
      });
    },
    "chatWebhooks.revoke": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const row = hooks.find((h) => h.id === ctx.params.id);
      const refused = row ? hookRefused(s, w, row) : deps.notFound();
      if (refused || !row) return refused ?? deps.notFound();
      row.revoked_at ??= stamp();
      return deps.json(row);
    },
    // CHAT-SWITCH-B: a DE's incoming webhooks (company admins; the demo never receives posts).
    "chatWebhooks.listForDe": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      if (!w.p.manager) return forbidden("Only admins manage a DE's webhooks.");
      return deps.json({ data: hooks.filter((h) => h.de_membership_id === ctx.params.id) });
    },
    "chatWebhooks.createForDe": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      if (!w.p.manager) return forbidden("Only admins manage a DE's webhooks.");
      const deId = ctx.params.id ?? "";
      if (!deps.desOf(w.p.company.id).some((d) => d.membership_id === deId)) return deps.notFound();
      const name = String(bodyOf(ctx).name ?? "").trim();
      if (!name || name.length > 80) return deps.validation("name: 1 to 80 characters");
      const token = fakeToken();
      const row = {
        id: nextId(0x5c),
        target: "de" as const,
        channel_id: null as string | null,
        de_membership_id: deId as string | null,
        name,
        token_hint: token.slice(-4),
        created_by_membership_id: w.who,
        post_count: 0,
        last_used_at: null,
        rotated_at: null as string | null,
        revoked_at: null as string | null,
        created_at: stamp(),
      };
      hooks.push(row);
      return deps.json(
        { webhook: row, path: `/v1/companies/${w.p.company.id}/hooks/chat/${row.id}/${token}` },
        201,
      );
    },
    "dms.open": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const ids = (bodyOf(ctx).membership_ids as unknown[] | undefined) ?? [];
      if (!ids.length || !ids.every(asUuid)) return deps.validation("membership_ids must be ids");
      const set = [...new Set([w.who, ...(ids as string[])])].sort();
      const dmKey = set.join(","); // dmKeyOf: sorted ids, comma-joined
      const existing = s.channels.find((c) => c.dm_key === dmKey);
      // CHAT-PRESENCE: opening a DM the caller closed lists it again.
      if (existing) {
        s.closures.delete(`${w.who}|${existing.id}`);
        return deps.json(existing);
      }
      const when = stamp();
      // As the API: the stored name is every member's name; each viewer reads the others' (channelLabel).
      const names = set.map((x) => nameOf(w.p.company.id, x)).join(", ");
      const row: Channel = {
        company_id: w.p.company.id,
        id: nextId(0xc110),
        space_id: null,
        kind: set.length > 2 ? "group_dm" : "dm",
        name: names || "Note to self",
        description: null,
        board_id: null,
        team_id: null,
        posting_policy: "everyone",
        topic_policy: "everyone",
        broadcast_policy: "everyone",
        default_notify_level: "all",
        is_private: true,
        is_agent_room: false,
        is_default: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: dmKey,
        archived_at: null,
        actor_kind: "person",
        actor_membership_id: w.who,
        created_at: when,
        updated_at: when,
      };
      s.channels.push(row);
      for (const m of set) s.members.push(member(row.company_id, row.id, m, when));
      // As the API (`defaultTopic`): a new DM's messages go to its "(no topic)" topic.
      insertTopic(s, row, "(no topic)", w.who);
      return deps.json(row);
    },

    // CHAT-PRESENCE ------------------------------------------------------------------------------
    "dePresence.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      return deps.json({ data: presenceOf(s, ctx, p) });
    },
    "dePresence.ack": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const who = membershipOf(ctx.me, p.company.id);
      if (!who) return forbidden("Acknowledge as a member of the company.");
      if (guestOnly(p)) return deps.notFound();
      const s = storeOf(p.company.id);
      const de = ctx.params.id ?? "";
      if (!deps.desOf(p.company.id).some((d) => d.membership_id === de)) return deps.notFound();
      // Only a DE whose ring says stuck is acknowledged (else nothing changes).
      const before = presenceOf(s, ctx, p).find((x) => x.membership_id === de);
      const failed = lastFailed(s, de);
      if (failed && before?.state === "stuck") s.acks.add(failed.id);
      const row = presenceOf(s, ctx, p).find((x) => x.membership_id === de);
      return row ? deps.json(row) : deps.notFound();
    },
    "dms.close": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const ch = s.channels.find((c) => c.id === ctx.params.id);
      if (!ch || !isDmKind(ch.kind) || !isMember(s, ch.id, w.who)) return deps.notFound();
      const key = `${w.who}|${ch.id}`;
      if (!dmClosed(s.closures.get(key) ?? null, lastIn(s, ch))) s.closures.set(key, stamp());
      return deps.json({ channel_id: ch.id, closed: true, closed_at: s.closures.get(key) ?? null });
    },
    "dms.cleanup": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const days = Number((bodyOf(ctx) as { quiet_days?: unknown }).quiet_days);
      if (![7, 14, 30].includes(days)) return deps.validation("quiet_days must be 7, 14 or 30");
      // What waits on the caller protects the DM, whatever the DE's ring (a paused DE is off).
      const needs = new Set(
        deps
          .desOf(w.p.company.id)
          .map((d) => d.membership_id)
          .filter((id) => {
            const a = viewerAsks(s, w.p, w.who, id);
            return Boolean(a.approval || a.input || a.screenAsk);
          }),
      );
      const closed: string[] = [];
      for (const ch of s.channels) {
        if (!isDmKind(ch.kind) || !isMember(s, ch.id, w.who)) continue;
        const key = `${w.who}|${ch.id}`;
        const last = lastIn(s, ch);
        if (dmClosed(s.closures.get(key) ?? null, last)) continue;
        if (!dmQuiet(last, ch.created_at, days, deps.now())) continue;
        if (s.members.some((m) => m.channel_id === ch.id && needs.has(m.membership_id))) continue;
        s.closures.set(key, stamp());
        closed.push(ch.id);
      }
      return deps.json({ closed_channel_ids: closed });
    },

    "follows.put": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"follows.put">["body"];
      const prior = s.follows.find(
        (f) =>
          f.membership_id === w.who &&
          f.subject_kind === b.subject_kind &&
          f.subject_id === b.subject_id,
      );
      if (prior) {
        prior.level = b.level ?? prior.level;
        prior.updated_at = stamp();
        return deps.json(prior);
      }
      const row = follow(
        w.p.company.id,
        w.who,
        b.subject_kind,
        b.subject_id,
        b.level ?? "all",
        stamp(),
      );
      s.follows.push(row);
      return deps.json(row);
    },
    "follows.delete": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const kind = ctx.url.searchParams.get("subject_kind");
      const subject = ctx.url.searchParams.get("subject_id");
      s.follows = s.follows.filter(
        (f) => !(f.membership_id === w.who && f.subject_kind === kind && f.subject_id === subject),
      );
      return new Response(null, { status: 204 });
    },
    "follows.mine": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const who = whoKey(ctx.me, p.company.id);
      const kind = ctx.url.searchParams.get("subject_kind");
      const rows = storeOf(p.company.id).follows.filter(
        (f) => f.membership_id === who && (!kind || f.subject_kind === kind),
      );
      return deps.json(deps.page(rows, ctx.url));
    },

    "boards.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const space = ctx.url.searchParams.get("space_id");
      const rows = storeOf(p.company.id).boards.filter((b) => !space || b.space_id === space);
      return deps.json(deps.page(rows, ctx.url));
    },
    "tasks.get": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).tasks.find((t) => t.id === ctx.params.id);
      return row ? deps.json(row) : deps.notFound();
    },
    "tasks.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"tasks.create">["body"];
      const board = s.boards.find((x) => x.id === b.board_id);
      if (!board) return deps.validation("board_id must name a board you can see");
      const title = String(b.title ?? "").trim();
      if (!title) return deps.validation("title must not be empty");
      const number = Math.max(0, ...s.tasks.map((t) => t.number)) + 1;
      const row = task(s, board, number, title, b.status ?? board.status_set[0]?.key ?? "todo", {
        ...(b.assignee_seat_ids?.[0] ? { seat: b.assignee_seat_ids[0] } : {}),
        ...(b.due_at ? { due: b.due_at } : {}),
      });
      return deps.json(row, 201);
    },
    "tasks.setStatus": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const row = s.tasks.find((t) => t.id === ctx.params.id);
      if (!row) return deps.notFound();
      const board =
        s.boards.find((b) => b.id === row.board_id) ??
        deps.work?.()?.boardOf(p.company.id, row.board_id);
      const status = String(bodyOf(ctx).status ?? "");
      const def = board?.status_set.find((x) => x.key === status);
      if (!def) return deps.validation(`status must be one of the board's statuses`);
      row.status = status;
      row.completed_at = def.done ? stamp() : null;
      row.version += 1;
      row.updated_at = stamp();
      // Parity P1: this store keeps no undo records (the work fake does)
      return deps.json({ ...row, undo_id: null });
    },

    "approvals.get": (ctx) => {
      const found = approvalOf(ctx);
      if (!found) return deps.notFound();
      // Parity P6a: what the caller may do; here, as `decide` below, any member decides
      const expired = found.expires_at !== null && Date.parse(found.expires_at) <= Date.now();
      const member = ctx.principal ? membershipOf(ctx.me, ctx.principal.company.id) : null;
      return deps.json({
        ...found,
        can_view: true,
        can_decide: found.state === "pending" && !expired && member !== null,
      });
    },
    "approvals.approve": (ctx) => decide(ctx, "approved"),
    "approvals.reject": (ctx) => decide(ctx, "rejected"),
    "approvals.edit": (ctx) => decide(ctx, "edited"),
    "runs.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const q = ctx.url.searchParams;
      const rows = storeOf(p.company.id)
        .runs.filter(
          (r) =>
            (!q.get("state") || r.state === q.get("state")) &&
            (!q.get("membership_id") || r.membership_id === q.get("membership_id")) &&
            (!q.get("seat_id") || r.seat_id === q.get("seat_id")),
        )
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      return deps.json(deps.page(rows, ctx.url));
    },
    "runs.get": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).runs.find((r) => r.id === ctx.params.id);
      return row ? deps.json({ ...row, steps: [] }) : deps.notFound();
    },
    "runs.cancel": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const row = storeOf(p.company.id).runs.find((r) => r.id === ctx.params.id);
      if (!row) return deps.notFound();
      if (["completed", "failed", "cancelled"].includes(row.state))
        return conflict(
          `the run has already ${row.state === "cancelled" ? "been cancelled" : "ended"}`,
        );
      row.state = "cancelled";
      row.state_reason = "stopped from chat";
      row.ended_at = stamp();
      row.updated_at = stamp();
      return deps.json(row);
    },

    // Agent rooms (M4-05) ---------------------------------------------------------------------
    "agentRooms.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const channelId = ctx.url.searchParams.get("channel_id");
      const rows = s.rooms
        .filter((r) => !channelId || r.channel_id === channelId)
        .filter(
          (r) =>
            roomPlace(s, r).ch && canSee(s, ctx.me, p.company.id, roomPlace(s, r).ch as Channel),
        )
        .map((r) => roomView(s, ctx, r));
      return deps.json(deps.page(rows, ctx.url));
    },
    "agentRooms.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const s = storeOf(w.p.company.id);
      const b = bodyOf(ctx) as OperationInput<"agentRooms.create">["body"];
      const modes = [b.space_id ?? b.name, b.channel_id, b.topic_id].filter((x) => x !== undefined);
      if (modes.length !== 1 || (b.space_id === undefined) !== (b.name === undefined))
        return deps.validation("exactly one of space_id + name, channel_id and topic_id");
      let channelId: string;
      let topicId: string | null = null;
      if (b.space_id !== undefined) {
        const made = handlers["channels.create"]?.({
          ...ctx,
          body: {
            space_id: b.space_id,
            name: b.name,
            is_private: b.is_private ?? false,
            kind: "agent_room",
          },
        });
        if (made?.status !== 201) return made ?? deps.notFound();
        const ch = s.channels.at(-1);
        if (!ch) return deps.notFound();
        channelId = ch.id;
      } else if (b.channel_id !== undefined) {
        const v = visibleChannel(ctx, b.channel_id);
        if (!v || v.ch.kind === "dm" || v.ch.kind === "group_dm") return deps.notFound();
        if (s.rooms.some((r) => r.channel_id === v.ch.id && r.topic_id === null))
          return conflict("this channel is already an agent room");
        v.ch.is_agent_room = true;
        v.ch.updated_at = stamp();
        channelId = v.ch.id;
      } else {
        const v = visibleTopic(ctx, b.topic_id);
        if (!v) return deps.notFound();
        if (s.rooms.some((r) => r.topic_id === v.t.id))
          return conflict("this topic is already an agent room");
        v.t.is_agent_room = true;
        channelId = v.ch.id;
        topicId = v.t.id;
      }
      const when = stamp();
      const room: Room = {
        company_id: w.p.company.id,
        id: nextId(0xc1b0),
        channel_id: channelId,
        topic_id: topicId,
        turn_policy: "round_robin",
        window_size: b.window_size ?? 20,
        max_agent_turns: b.max_agent_turns ?? 8,
        current_turn_membership_id: null,
        turn_seq: 0,
        turn_started_at: null,
        agent_turns_since_human: 0,
        stalled_at: null,
        actor_kind: "person",
        actor_membership_id: w.who,
        created_at: when,
        updated_at: when,
      };
      s.rooms.push(room);
      for (const m of new Set(b.participant_ids ?? [])) {
        const refused = addTo(s, w, room, m);
        if (refused) return refused;
      }
      return deps.json(roomView(s, ctx, room), 201);
    },
    "agentRooms.get": (ctx) => {
      const v = visibleRoom(ctx, ctx.params.id);
      return v ? deps.json(roomView(v.s, ctx, v.room)) : deps.notFound();
    },
    "agentRooms.update": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"agentRooms.update">["body"];
      if (b.window_size === undefined && b.max_agent_turns === undefined)
        return deps.validation("at least one field");
      if (b.window_size !== undefined && (b.window_size < 1 || b.window_size > 200))
        return deps.validation("window_size must be 1–200");
      if (b.max_agent_turns !== undefined && (b.max_agent_turns < 1 || b.max_agent_turns > 50))
        return deps.validation("max_agent_turns must be 1–50");
      if (b.window_size !== undefined) v.room.window_size = b.window_size;
      if (b.max_agent_turns !== undefined) v.room.max_agent_turns = b.max_agent_turns;
      v.room.updated_at = stamp();
      return deps.json(roomView(v.s, ctx, v.room));
    },
    // ACTIONS: as the API — refused while taken over or with status asks; the messages stay
    // (a room channel of its own is archived, a flagged channel is a plain one again).
    "agentRooms.delete": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const ch = v.s.channels.find((c) => c.id === v.room.channel_id);
      if (ch?.taken_over_by_membership_id)
        return conflict("the room is taken over; release it first");
      if (v.s.asks.some((a) => a.room_id === v.room.id))
        return conflict("the room has status asks, its record; archive its channel instead");
      v.s.rooms = v.s.rooms.filter((r) => r.id !== v.room.id);
      v.s.participants = v.s.participants.filter((x) => x.room_id !== v.room.id);
      if (ch && !v.room.topic_id && !ch.archived_at) {
        if (ch.kind === "agent_room") ch.archived_at = stamp();
        else if (!v.s.rooms.some((r) => r.channel_id === ch.id)) ch.is_agent_room = false;
      }
      return new Response(null, { status: 204 });
    },
    "agentRooms.addParticipant": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const b = bodyOf(ctx) as OperationInput<"agentRooms.addParticipant">["body"];
      if (!asUuid(b.membership_id)) return deps.validation("membership_id must be a membership");
      const refused = addTo(v.s, w, v.room, b.membership_id);
      return refused ?? deps.json(roomView(v.s, ctx, v.room));
    },
    "agentRooms.removeParticipant": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const id = ctx.params.membership_id;
      const row = v.s.participants.find((x) => x.room_id === v.room.id && x.membership_id === id);
      if (!row) return deps.notFound();
      const owner = v.s.crewOwners.get(id ?? "");
      if (owner && owner !== w.who && !w.p.manager)
        return forbidden("Only its owner takes a crew out of a room.");
      v.s.participants = v.s.participants.filter((x) => x !== row);
      onParticipantsChanged(v.s, v.room);
      return deps.json(roomView(v.s, ctx, v.room));
    },
    "agentRooms.join": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (!v.s.participants.some((x) => x.room_id === v.room.id && x.membership_id === w.who))
        v.s.participants.push(
          participant(v.room, w.who, "person", nextOrder(v.s, v.room), stamp()),
        );
      return deps.json(roomView(v.s, ctx, v.room));
    },
    "agentRooms.leave": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const before = v.s.participants.length;
      v.s.participants = v.s.participants.filter(
        (x) => !(x.room_id === v.room.id && x.membership_id === w.who),
      );
      if (v.s.participants.length === before) return conflict("you are not in this room");
      return deps.json(roomView(v.s, ctx, v.room));
    },
    "agentRooms.turn": (ctx) => {
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      return deps.json(turnOf(v.s, ctx, v.room));
    },
    "agentRooms.pass": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      if (v.room.current_turn_membership_id !== w.who) return conflict("it is not your turn");
      advance(v.s, v.room, w.who);
      return deps.json(turnOf(v.s, ctx, v.room));
    },
    "statusAsks.list": (ctx) => {
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      const state = ctx.url.searchParams.get("state");
      const rows = v.s.asks
        .filter((a) => a.room_id === v.room.id && (!state || a.state === state))
        .sort((a, b) => b.created_at.localeCompare(a.created_at));
      return deps.json(deps.page(rows, ctx.url));
    },
    "statusAsks.create": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const v = visibleRoom(ctx, ctx.params.id);
      if (!v) return deps.notFound();
      // People speak in the room; status asks are agent to agent (the fake has no agent callers).
      return forbidden("Status asks are made by agents in the room; ask in the room yourself.");
    },
    "statusAsks.answer": (ctx) => askDecision(ctx, "answer"),
    "statusAsks.decline": (ctx) => askDecision(ctx, "decline"),

    "presence.get": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const peers: Peer[] =
        p.company.id === COMPANY_IDS.acme
          ? [
              {
                connection_id: "fake-jordan",
                membership_id: CHAT_PEOPLE.jordan,
                user_id: null,
                name: "Jordan Lee",
                screen: "chat",
                subject: null,
                typing: null,
                since: at(40),
              },
              {
                connection_id: "fake-riley",
                membership_id: DE_IDS.riley,
                user_id: null,
                name: "Riley",
                screen: "chat",
                subject: null,
                typing: null,
                since: at(3),
              },
            ]
          : [];
      return deps.json({ peers });
    },

    // CHAT-SWITCH-A: the people the caller sees (in a channel the caller can see, and the
    // caller), each with presence (the caller active, everyone else away: the fake has no other
    // sessions), the custom status they set (none to start) and their time zone.
    "chatStatus.list": (ctx) => {
      const p = ctx.principal;
      if (!p) return deps.notFound();
      const s = storeOf(p.company.id);
      const me = membershipOf(ctx.me, p.company.id);
      const des = new Set(deps.desOf(p.company.id).map((d) => d.membership_id));
      const seen = new Set<string>(me ? [me] : []);
      for (const c of s.channels)
        if (canSee(s, ctx.me, p.company.id, c))
          for (const m of s.members)
            if (m.channel_id === c.id && !des.has(m.membership_id)) seen.add(m.membership_id);
      return deps.json({ data: [...seen].map((id) => statusOf(p.company.id, id, id === me)) });
    },
    "chatStatus.set": (ctx) => {
      const w = writer(ctx);
      if (w instanceof Response) return w;
      const b = bodyOf(ctx);
      const known = new Set(["emoji", "text", "expires_at", "time_zone"]);
      if (Object.keys(b).some((k) => !known.has(k))) return deps.validation("Unknown field.");
      const str = (k: string, max: number): string | null | undefined | Response => {
        const v = b[k];
        if (v === undefined || v === null) return v as undefined | null;
        if (typeof v !== "string" || !v.trim() || v.trim().length > max)
          return deps.validation(`${k}: 1 to ${max} characters.`);
        return v.trim();
      };
      const emoji = str("emoji", 32);
      const text = str("text", 100);
      const expires = b.expires_at;
      const zone = b.time_zone;
      if (emoji instanceof Response) return emoji;
      if (text instanceof Response) return text;
      if (expires !== undefined && expires !== null && Number.isNaN(Date.parse(String(expires))))
        return deps.validation("expires_at: a timestamp.");
      if (zone !== undefined && (typeof zone !== "string" || !zone || zone.length > 64))
        return deps.validation("time_zone: an IANA zone.");
      const key = `${w.p.company.id}|${w.who}`;
      const cur = statuses.get(key) ?? {
        emoji: null,
        text: null,
        expires_at: null,
        time_zone: null,
      };
      statuses.set(key, {
        emoji: emoji === undefined ? cur.emoji : emoji,
        text: text === undefined ? cur.text : text,
        expires_at: expires === undefined ? cur.expires_at : (expires as string | null),
        time_zone: zone === undefined ? cur.time_zone : (zone as string),
      });
      return deps.json(statusOf(w.p.company.id, w.who, true));
    },
  };

  /** CHAT-SWITCH-A: one person's `ChatStatus` (an expired custom status reads as none). */
  function statusOf(companyId: string, id: string, active: boolean) {
    const row = statuses.get(`${companyId}|${id}`);
    const live =
      row &&
      (row.emoji || row.text) &&
      !(row.expires_at && Date.parse(row.expires_at) <= +deps.now());
    return {
      membership_id: id,
      presence: active ? ("active" as const) : ("away" as const),
      emoji: live ? row.emoji : null,
      text: live ? row.text : null,
      expires_at: live ? row.expires_at : null,
      time_zone: row?.time_zone ?? null,
      dnd: false,
    };
  }

  // Shared handler parts ---------------------------------------------------------------------

  // THREAD-READS: the API's thread-reads.ts, over the store. Following is by participation (the
  // root's author, a replier, a follower of the topic) until the row says otherwise; a reply or
  // a mention sets it; unread counts replies by others after the member's position.
  function liveReplies(s: Store, rootId: string): Message[] {
    return s.messages
      .filter((m) => m.parent_message_id === rootId && !m.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  function threadState(s: Store, who: string, root: Message) {
    const row = s.threadReads.get(`${who}|${root.id}`);
    const replies = liveReplies(s, root.id);
    const th = s.threads.find((t) => t.id === root.thread_id);
    const topicFollowed =
      th?.kind === "topic" &&
      s.follows.some(
        (f) =>
          f.membership_id === who && f.subject_kind === "topic" && f.subject_id === th.subject_id,
      );
    const following =
      row?.following ??
      (root.author_membership_id === who ||
        replies.some((r) => r.author_membership_id === who) ||
        topicFollowed);
    return {
      root_message_id: root.id,
      following,
      unread_count: replies.filter(
        (r) => r.author_membership_id !== who && (!row?.at || r.created_at > row.at),
      ).length,
      last_read_reply_id: row?.reply ?? null,
      last_read_at: row?.at ?? null,
    };
  }

  function moveThreadRead(s: Store, who: string, rootId: string, upTo: Message) {
    const key = `${who}|${rootId}`;
    const row = s.threadReads.get(key) ?? { at: null, reply: null, following: null };
    if (row.at && row.at >= upTo.created_at) return;
    s.threadReads.set(key, {
      ...row,
      at: upTo.created_at,
      reply: upTo.parent_message_id ? upTo.id : null,
    });
  }

  /** After a post: its author has read up to its reply and follows; the mentioned follow. */
  function noteThreadPost(s: Store, m: Message) {
    const rootId = m.parent_message_id ?? m.id;
    if (m.parent_message_id && m.author_membership_id) {
      moveThreadRead(s, m.author_membership_id, rootId, m);
      const key = `${m.author_membership_id}|${rootId}`;
      const row = s.threadReads.get(key) ?? { at: null, reply: null, following: null };
      s.threadReads.set(key, { ...row, following: true });
    }
    for (const ref of m.mentions) {
      const [kind, id] = ref.split(":");
      if ((kind !== "person" && kind !== "de") || !id || id === m.author_membership_id) continue;
      const key = `${id}|${rootId}`;
      const row = s.threadReads.get(key) ?? { at: null, reply: null, following: null };
      s.threadReads.set(key, { ...row, following: true });
    }
  }

  /** The caller's followed reply threads in channels it may see, latest reply first. */
  function myThreads(ctx: Ctx, s: Store) {
    const p = ctx.principal;
    const me = p ? membershipOf(ctx.me, p.company.id) : null;
    if (!p || me === null) return [];
    const who = whoKey(ctx.me, p.company.id);
    const roots = new Set<string>();
    for (const m of s.messages)
      if (m.parent_message_id && !m.deleted_at) roots.add(m.parent_message_id);
    const out: { row: OperationOutput<"threads.mine">["data"][number]; lastReply?: Message }[] = [];
    for (const rootId of roots) {
      const root = s.messages.find((m) => m.id === rootId);
      if (!root || root.deleted_at) continue;
      const th = s.threads.find((t) => t.id === root.thread_id);
      const t = th?.kind === "topic" ? s.topics.find((x) => x.id === th.subject_id) : undefined;
      const ch = t ? s.channels.find((c) => c.id === t.channel_id) : undefined;
      if (!t || !ch || !canSee(s, ctx.me, p.company.id, ch)) continue;
      const state = threadState(s, me, root);
      if (!state.following) continue;
      const replies = liveReplies(s, rootId);
      const last = replies.at(-1);
      out.push({
        row: {
          root: view(s, root, who),
          channel_id: ch.id,
          channel_name: ch.name,
          channel_kind: ch.kind,
          topic_id: t.id,
          topic_name: t.name,
          reply_count: replies.length,
          last_reply_at: last?.created_at ?? root.created_at,
          participant_ids: [
            ...new Set(
              replies
                .map((r) => r.author_membership_id)
                .filter((a) => a !== root.author_membership_id),
            ),
          ],
          unread: state.unread_count > 0,
          unread_count: state.unread_count,
          following: true,
          last_read_at: state.last_read_at,
        },
        ...(last ? { lastReply: last } : {}),
      });
    }
    out.sort((a, b) => b.row.last_reply_at.localeCompare(a.row.last_reply_at));
    return out;
  }

  /**
   * CHAT-FILES: a chat file whoever asks may read — staged: its uploader; sent: whoever sees
   * its message's thread while the message is live.
   */
  function readableChatFile(ctx: Ctx): ChatFileRow | null {
    const p = ctx.principal;
    if (!p) return null;
    const s = storeOf(p.company.id);
    const f = s.chatFiles.find((x) => x.view.id === ctx.params.id && !x.gone);
    if (!f) return null;
    if (!f.view.message_id)
      return f.view.uploader.membership_id === membershipOf(ctx.me, p.company.id) ? f : null;
    const m = s.messages.find((x) => x.id === f.view.message_id);
    if (!m || m.deleted_at || !visibleThread(ctx, m.thread_id)) return null;
    return f;
  }

  function messageIn(ctx: Ctx) {
    const v = visibleThread(ctx, ctx.params.id);
    const m = v?.s.messages.find((x) => x.id === ctx.params.mid && x.thread_id === v.th.id);
    return v && m ? { ...v, m } : null;
  }

  function ownMessage(ctx: Ctx): { m: Message } | Response {
    const w = writer(ctx);
    if (w instanceof Response) return w;
    const found = messageIn(ctx);
    if (!found || found.m.deleted_at) return deps.notFound();
    if (found.m.author_membership_id !== w.who)
      return forbidden("only the author edits or deletes");
    return { m: found.m };
  }

  function takeOver(ctx: Ctx, level: "channel" | "topic", on: boolean): Response {
    const w = writer(ctx);
    if (w instanceof Response) return w;
    const target =
      level === "channel"
        ? visibleChannel(ctx, ctx.params.id)?.ch
        : visibleTopic(ctx, ctx.params.id)?.t;
    if (!target) return deps.notFound();
    const s = storeOf(w.p.company.id);
    const isRoom =
      level === "channel"
        ? (target as Channel).is_agent_room || (target as Channel).kind === "agent_room"
        : (target as Topic).is_agent_room;
    if (!isRoom) return conflict("only agent rooms can be taken over");
    if (on && target.taken_over_by_membership_id) return conflict("already taken over");
    if (!on && !target.taken_over_by_membership_id) return conflict("not taken over");
    target.taken_over_by_membership_id = on ? w.who : null;
    target.taken_over_at = on ? stamp() : null;
    target.updated_at = stamp();
    // Runs targeting the room park while a person has it, and resume on release.
    const topics =
      level === "channel"
        ? new Set(s.topics.filter((t) => t.channel_id === target.id).map((t) => t.id))
        : new Set([target.id]);
    for (const r of s.runs)
      if (r.trigger_ref?.kind === "topic" && r.trigger_ref.id && topics.has(r.trigger_ref.id)) {
        if (on && r.state === "running") {
          r.state = "waiting_input";
          r.state_reason = "parked: the room was taken over";
        } else if (!on && r.state === "waiting_input") {
          r.state = "running";
          r.state_reason = null;
        }
      }
    return deps.json(
      level === "channel"
        ? target
        : (() => {
            const t = target as Topic;
            const ch = channelOfTopic(s, t);
            return ch
              ? topicDetail(s, ctx.me, ch, t)
              : { ...t, thread_id: "", is_main: false, is_member: false, is_opener: false };
          })(),
    );
  }

  // Agent rooms (M4-05; services/api/src/domain/agent-rooms/rules.ts) -------------------------

  const LIVE = new Set([
    "queued",
    "running",
    "waiting_input",
    "awaiting_approval",
    "waiting_children",
  ]);
  const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

  function roomPlace(s: Store, room: Room) {
    const ch = s.channels.find((c) => c.id === room.channel_id);
    const t = room.topic_id ? s.topics.find((x) => x.id === room.topic_id) : undefined;
    return { ch, t };
  }

  function roomTopicIds(s: Store, room: Room): Set<string> {
    return room.topic_id
      ? new Set([room.topic_id])
      : new Set(s.topics.filter((t) => t.channel_id === room.channel_id).map((t) => t.id));
  }

  /** The room of a topic: its own, or its channel's. */
  function roomOfTopic(s: Store, t: Topic): Room | undefined {
    return (
      s.rooms.find((r) => r.topic_id === t.id) ??
      s.rooms.find((r) => r.topic_id === null && r.channel_id === t.channel_id)
    );
  }

  function visibleRoom(ctx: Ctx, id: string | undefined) {
    const p = ctx.principal;
    if (!p || !id) return null;
    const s = storeOf(p.company.id);
    const room = s.rooms.find((r) => r.id === id);
    const ch = room && roomPlace(s, room).ch;
    return room && ch && canSee(s, ctx.me, p.company.id, ch) ? { s, room, ch } : null;
  }

  /** Who a membership is, with the badge its messages carry (06; research rec. 1). */
  function memberInfo(s: Store, ctx: Ctx, companyId: string, membership: string) {
    const de = deps.desOf(companyId).find((d) => d.membership_id === membership);
    if (de)
      return {
        display_name: de.display_name,
        member_kind: "digital_employee" as const,
        badge: {
          kind: "de" as const,
          label: `DE · ${de.seat_title}`,
          seat_title: de.seat_title,
          owner_membership_id: null,
        },
      };
    const owner = s.crewOwners.get(membership);
    if (owner) {
      const ownerName = personName(ctx, companyId, owner);
      return {
        display_name: `${firstName(ownerName)}'s agent`,
        member_kind: "personal_agent" as const,
        badge: {
          kind: "crew" as const,
          label: `${firstName(ownerName)}'s agent`,
          seat_title: null,
          owner_membership_id: owner,
        },
      };
    }
    const name = personName(ctx, companyId, membership);
    return {
      display_name: name,
      member_kind: "person" as const,
      badge: { kind: "person" as const, label: name, seat_title: null, owner_membership_id: null },
    };
  }

  const personName = (ctx: Ctx, companyId: string, membership: string) =>
    membership === membershipOf(ctx.me, companyId)
      ? ctx.me.user.name
      : nameOf(companyId, membership);

  const isParked = (r: Run) =>
    r.state === "waiting_input" && (r.state_reason ?? "").startsWith("parked");

  function roomView(s: Store, ctx: Ctx, room: Room): RoomView {
    const { ch, t } = roomPlace(s, room);
    const topics = roomTopicIds(s, room);
    const runs = s.runs
      .filter(
        (r) => r.trigger_ref?.kind === "topic" && r.trigger_ref.id && topics.has(r.trigger_ref.id),
      )
      .filter((r) => LIVE.has(r.state))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const target = t ?? ch;
    return {
      room,
      name: t?.name ?? ch?.name ?? "",
      space_id: ch?.space_id ?? null,
      taken_over_by_membership_id: target?.taken_over_by_membership_id ?? null,
      taken_over_at: target?.taken_over_at ?? null,
      participants: s.participants
        .filter((x) => x.room_id === room.id)
        .sort((a, b) => a.turn_order - b.turn_order)
        .map((x): ParticipantView => {
          const run = runs.find((r) => r.membership_id === x.membership_id);
          return {
            ...x,
            ...memberInfo(s, ctx, room.company_id, x.membership_id),
            is_turn: room.current_turn_membership_id === x.membership_id,
            run: run ? { id: run.id, state: run.state, parked: isParked(run) } : null,
          };
        }),
      parked_runs: runs.filter(isParked).length,
      open_status_asks: s.asks.filter(
        (a) => a.room_id === room.id && (a.state === "open" || a.state === "escalated"),
      ).length,
    };
  }

  const agentOrder = (s: Store, room: Room) =>
    s.participants
      .filter((x) => x.room_id === room.id && x.kind === "agent")
      .sort((a, b) => a.turn_order - b.turn_order)
      .map((x) => x.membership_id);

  const nextOrder = (s: Store, room: Room) =>
    Math.max(-1, ...s.participants.filter((x) => x.room_id === room.id).map((x) => x.turn_order)) +
    1;

  function moveTurn(room: Room, holder: string | null) {
    if (holder === room.current_turn_membership_id) return;
    room.current_turn_membership_id = holder;
    room.turn_seq += 1;
    room.turn_started_at = holder ? stamp() : null;
    room.updated_at = stamp();
  }

  /** A room that got its first agent starts; a holder who left hands to the first agent. */
  function onParticipantsChanged(s: Store, room: Room) {
    if (room.stalled_at) return;
    const agents = agentOrder(s, room);
    const cur = room.current_turn_membership_id;
    if (cur && agents.includes(cur)) return;
    moveTurn(room, agents[0] ?? null);
  }

  /** A person posted: the count restarts, a stalled room resumes with its holder. */
  function onHumanPost(s: Store, room: Room) {
    const agents = agentOrder(s, room);
    const cur = room.current_turn_membership_id;
    const holder = cur && agents.includes(cur) ? cur : (agents[0] ?? null);
    room.agent_turns_since_human = 0;
    if (room.stalled_at) {
      room.stalled_at = null;
      room.turn_seq += 1;
      room.current_turn_membership_id = holder;
      room.turn_started_at = holder ? stamp() : null;
      room.updated_at = stamp();
      return;
    }
    moveTurn(room, holder);
  }

  /** The holder posted or passed: the next agent, or a stall after `max_agent_turns`. */
  function advance(s: Store, room: Room, by: string) {
    room.agent_turns_since_human += 1;
    if (room.agent_turns_since_human >= room.max_agent_turns) {
      room.current_turn_membership_id = null;
      room.turn_seq += 1;
      room.turn_started_at = null;
      room.stalled_at = stamp();
      room.updated_at = stamp();
      return;
    }
    const agents = agentOrder(s, room);
    const i = agents.indexOf(by);
    room.current_turn_membership_id = agents[(i + 1) % Math.max(1, agents.length)] ?? null;
    room.turn_seq += 1;
    room.turn_started_at = stamp();
    room.updated_at = stamp();
  }

  /** Adds a participant: a DE, the caller's own crew (never another person's), or a person. */
  function addTo(
    s: Store,
    w: { p: Principal; who: string },
    room: Room,
    membership: string,
  ): Response | null {
    if (s.participants.some((x) => x.room_id === room.id && x.membership_id === membership))
      return null;
    const companyId = w.p.company.id;
    const de = deps.desOf(companyId).some((d) => d.membership_id === membership);
    const ownCrew = crewMembershipIdOf(companyId, w.who) === membership;
    if (ownCrew) s.crewOwners.set(membership, w.who);
    const owner = s.crewOwners.get(membership);
    if (owner && owner !== w.who) return forbidden("Only its owner brings a crew into a room.");
    const kind = de || owner ? "agent" : "person";
    s.participants.push(participant(room, membership, kind, nextOrder(s, room), stamp()));
    const ch = roomPlace(s, room).ch;
    if (
      kind === "agent" &&
      ch?.is_private &&
      !s.members.some((m) => m.channel_id === ch.id && m.membership_id === membership)
    )
      s.members.push(member(companyId, ch.id, membership, stamp()));
    onParticipantsChanged(s, room);
    return null;
  }

  /** The thread a turn replies in: the room topic's, or the latest active topic's. */
  function replyThread(s: Store, room: Room): Thread | undefined {
    if (room.topic_id) return threadOfTopic(s, room.topic_id);
    const latest = s.topics
      .filter((t) => t.channel_id === room.channel_id)
      .sort((a, b) => (b.last_message_at ?? "").localeCompare(a.last_message_at ?? ""))[0];
    return latest ? threadOfTopic(s, latest.id) : undefined;
  }

  function turnOf(s: Store, ctx: Ctx, room: Room): RoomTurn {
    const topics = roomTopicIds(s, room);
    const threads = new Set(s.threads.filter((t) => topics.has(t.subject_id)).map((t) => t.id));
    const window = s.messages
      .filter((m) => threads.has(m.thread_id) && !m.deleted_at)
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .slice(-room.window_size);
    const { ch, t } = roomPlace(s, room);
    const me = membershipOf(ctx.me, room.company_id);
    return {
      room_id: room.id,
      membership_id: room.current_turn_membership_id,
      seq: room.turn_seq,
      started_at: room.turn_started_at,
      stalled: room.stalled_at !== null,
      agent_turns_since_human: room.agent_turns_since_human,
      is_mine: me !== null && room.current_turn_membership_id === me,
      taken_over: Boolean((t ?? ch)?.taken_over_by_membership_id),
      reply_thread_id: replyThread(s, room)?.id ?? null,
      window_size: room.window_size,
      window,
    };
  }

  /** A person answers or declines a status ask escalated to them (or an open one to their crew). */
  function askDecision(ctx: Ctx, action: "answer" | "decline"): Response {
    const w = writer(ctx);
    if (w instanceof Response) return w;
    const s = storeOf(w.p.company.id);
    const ask = s.asks.find((a) => a.id === ctx.params.id);
    const room = ask && s.rooms.find((r) => r.id === ask.room_id);
    const v = room ? visibleRoom(ctx, room.id) : null;
    if (!ask || !room || !v) return deps.notFound();
    const mine =
      (ask.state === "escalated" && ask.escalated_to_membership_id === w.who) ||
      (ask.state === "open" && s.crewOwners.get(ask.target_membership_id) === w.who);
    if (action === "decline" && ask.state !== "escalated")
      return conflict("only an escalated ask can be declined");
    if (ask.state !== "open" && ask.state !== "escalated")
      return conflict(`the ask was already ${ask.state}`);
    if (!mine) return forbidden("Only the person the ask went to answers it.");
    const b = bodyOf(ctx);
    const when = stamp();
    if (action === "answer") {
      const status = String(b.status ?? "").trim();
      if (!status || status.length > 60) return deps.validation("status must be 1–60 characters");
      ask.state = "answered";
      ask.answer = {
        status,
        note: typeof b.note === "string" && b.note.trim() ? b.note.trim() : null,
      };
    } else ask.state = "declined";
    ask.answered_by_membership_id = w.who;
    ask.answered_at = when;
    ask.updated_at = when;
    const th = replyThread(s, room);
    if (th) {
      const task = s.tasks.find((x) => x.id === ask.subject_id);
      const what = task ? `"${task.title}"` : "the task";
      const text =
        action === "answer"
          ? `${personName(ctx, w.p.company.id, w.who)} answered the status ask about ${what}: ${ask.answer?.status}.`
          : `${personName(ctx, w.p.company.id, w.who)} declined the status ask about ${what}.`;
      const row: Message = {
        company_id: w.p.company.id,
        created_at: when,
        id: nextId(0xc140),
        thread_id: th.id,
        author_membership_id: null,
        body: textBlocks(text),
        body_text: text,
        author_kind: "system",
        mentions: [],
        card_refs: [`status_ask:${ask.id}`],
        parent_message_id: null,
        client_id: null,
        edited_at: null,
        deleted_at: null,
        pinned_at: null,
        ...NO_ACTOR,
        ...SYSTEM,
      };
      s.messages.push(row);
      touchThread(s, row);
    }
    return deps.json(ask);
  }

  /**
   * The caller's inbox: a mention of them, or a reply in a topic they follow, is an item (the
   * M2-04 projection's rules, coalescing aside); read and done live on the item.
   */
  function inboxOf(me: MeResponse, companyId: string): InboxItem[] {
    const who = membershipOf(me, companyId);
    if (!who) return [];
    const s = storeOf(companyId);
    const followed = new Set(
      s.follows
        .filter((f) => f.membership_id === who && f.subject_kind === "topic")
        .map((f) => f.subject_id),
    );
    const items: InboxItem[] = [];
    for (const m of s.messages) {
      if (m.deleted_at || m.author_membership_id === who) continue;
      const t = topicOfThread(s, m.thread_id);
      const ch = t && channelOfTopic(s, t);
      if (!t || !ch || !canSee(s, me, companyId, ch)) continue;
      const mention = m.mentions.includes(`person:${who}`);
      // INBOX-PREFS: a message in a DM the person is in reaches them (the DMs row).
      const inDm =
        isDmKind(ch.kind) &&
        s.members.some((r) => r.channel_id === ch.id && r.membership_id === who);
      if (!mention && !followed.has(t.id) && !inDm) continue;
      const id = uid(0xc1a0, Number.parseInt(m.id.slice(-6), 16));
      const state = s.inbox.get(id) ?? { read_at: null, done_at: null, snoozed_until: null };
      items.push({
        company_id: companyId,
        id,
        membership_id: who,
        kind: mention ? "mention" : "reply",
        subject_kind: "topic",
        subject_id: t.id,
        message_id: m.id,
        source_event_id: uid(0xc1a1, Number.parseInt(m.id.slice(-6), 16)),
        coalesce_key: null,
        count: 1,
        summary: `${ch.kind === "dm" ? ch.name : `#${ch.name} › ${t.name}`}: ${m.body_text.slice(0, 120)}`,
        read_at: state.read_at,
        done_at: state.done_at,
        snoozed_until: state.snoozed_until,
        // SEARCH-NOTIF: stamped from the person's preferences by fake.ts's merged inbox.
        desktop: false,
        // INBOX-PREFS: a DM's items are the DMs row; the author is who it is from.
        category: categoryOf(mention ? "mention" : "reply", "topic", isDmKind(ch.kind)),
        from_membership_id: m.author_membership_id,
        nudged_at: null,
        escalated_at: null,
        created_at: m.created_at,
        updated_at: m.created_at,
      });
    }
    return items.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  function inboxMark(ctx: Ctx, field: "read_at" | "done_at", on: boolean): Response {
    const p = ctx.principal;
    if (!p) return deps.notFound();
    const item = inboxOf(ctx.me, p.company.id).find((i) => i.id === ctx.params.id);
    if (!item) return deps.notFound();
    const s = storeOf(p.company.id);
    const next = {
      read_at: item.read_at,
      done_at: item.done_at,
      snoozed_until: item.snoozed_until,
      [field]: on ? stamp() : null,
    };
    s.inbox.set(item.id, next);
    return deps.json({ ...item, ...next });
  }

  function approvalOf(ctx: Ctx): Approval | null {
    const p = ctx.principal;
    if (!p) return null;
    return storeOf(p.company.id).approvals.find((a) => a.id === ctx.params.id) ?? null;
  }

  function decide(ctx: Ctx, state: "approved" | "rejected" | "edited"): Response {
    const w = writer(ctx);
    if (w instanceof Response) return w;
    const a = approvalOf(ctx);
    if (!a) return deps.notFound();
    // Idempotent: a second decision returns the first.
    if (a.state !== "pending") return deps.json(a);
    const b = bodyOf(ctx);
    if (state === "rejected" && !String(b.reason ?? "").trim())
      return deps.validation("reason must not be empty");
    a.state = state;
    a.decided_at = stamp();
    a.decided_by_membership_id = w.who;
    a.reason = typeof b.reason === "string" ? b.reason : null;
    a.approved_payload =
      state === "approved"
        ? a.proposal
        : state === "edited"
          ? ((b.proposal as Approval["proposal"]) ?? a.proposal)
          : null;
    a.updated_at = stamp();
    const run = storeOf(w.p.company.id).runs.find((r) => r.id === a.run_id);
    if (run && run.state === "awaiting_approval") {
      run.state = state === "rejected" ? "completed" : "running";
      run.state_reason = null;
    }
    return deps.json(a);
  }

  function nameOf(companyId: string, membership: string): string {
    if (membership === CHAT_PEOPLE.maya) return "Maya Owner";
    if (membership === CHAT_PEOPLE.jordan) return "Jordan Lee";
    return (
      deps.desOf(companyId).find((d) => d.membership_id === membership)?.display_name ?? "Member"
    );
  }

  // M4-03: what a person's crew writes in chat (fake-crews.ts, composed in fake.ts) ----------

  /** The owner's personal space and its `#brief` (setup.ts `ensureCrew`), made once. */
  function ensureBrief(c: CrewChatRef): { s: Store; ch: Channel } {
    const s = storeOf(c.companyId);
    const when = stamp();
    if (!s.spaces.some((x) => x.id === c.spaceId))
      s.spaces.push({
        company_id: c.companyId,
        id: c.spaceId,
        name: c.ownerName,
        kind: "personal",
        team_id: null,
        owner_membership_id: c.ownerId,
        icon: null,
        description: null,
        is_private: true,
        settings: {},
        position: Date.parse(when),
        archived_at: null,
        ...SYSTEM,
        created_at: when,
        updated_at: when,
      });
    let ch = s.channels.find((x) => x.id === c.channelId);
    if (!ch) {
      ch = {
        company_id: c.companyId,
        id: c.channelId,
        space_id: c.spaceId,
        kind: "space",
        name: "brief",
        description: `${c.crewName}'s daily brief and notes for you`,
        board_id: null,
        team_id: null,
        posting_policy: "everyone",
        topic_policy: "everyone",
        broadcast_policy: "everyone",
        default_notify_level: "all",
        // The personal space is private: only its members (the owner and the crew) see it.
        is_private: true,
        is_agent_room: false,
        is_default: false,
        taken_over_by_membership_id: null,
        taken_over_at: null,
        dm_key: null,
        archived_at: null,
        ...SYSTEM,
        created_at: when,
        updated_at: when,
      };
      s.channels.push(ch);
      for (const m of [c.ownerId, c.crewId]) s.members.push(member(c.companyId, ch.id, m, when));
    }
    return { s, ch };
  }

  /** A message as the crew (`personal_agent`, as its agent), its blocks from the markdown. */
  function crewMessage(c: CrewChatRef, s: Store, threadId: string, md: string, clientId: string) {
    const prior = s.messages.find((m) => m.id === s.clientIds.get(`${c.companyId}|${clientId}`));
    if (prior) return prior;
    const body = markdownToBlocks(md);
    const row: Message = {
      company_id: c.companyId,
      created_at: stamp(),
      id: nextId(0xc140),
      thread_id: threadId,
      author_membership_id: c.crewId,
      body,
      body_text: bodyTextOf(body),
      author_kind: "agent",
      mentions: [],
      card_refs: [],
      parent_message_id: null,
      client_id: clientId,
      edited_at: null,
      deleted_at: null,
      pinned_at: null,
      ...NO_ACTOR,
      actor_kind: "personal_agent",
      actor_membership_id: c.crewId,
      actor_crew_agent_id: c.crewAgentId,
      actor_identity_mode: "as_agent",
      actor_acting_for_membership_id: c.ownerId,
    };
    s.messages.push(row);
    s.clientIds.set(`${c.companyId}|${clientId}`, row.id);
    touchThread(s, row);
    return row;
  }

  const crew: CrewChatBridge = {
    ensureBrief: (c) => void ensureBrief(c),
    postBrief(c, topicName, md, clientId) {
      const { s, ch } = ensureBrief(c);
      let t = s.topics.find((x) => x.channel_id === ch.id && x.name === topicName);
      if (!t) {
        const when = stamp();
        t = {
          company_id: c.companyId,
          id: nextId(0xc120),
          channel_id: ch.id,
          name: topicName,
          status: "open",
          is_agent_room: false,
          taken_over_by_membership_id: null,
          taken_over_at: null,
          pinned_at: null,
          archived_at: null,
          last_message_at: null,
          message_count: 0,
          is_private: false,
          actor_kind: "personal_agent",
          actor_membership_id: c.crewId,
          created_at: when,
          updated_at: when,
        };
        s.topics.push(t);
        // CHAT-TOPICS-SEPARATE: the brief's topic holds its crew and the crew's owner.
        addTopicMembers(s, ch, t, [c.crewId], "creator", c.crewId);
        addTopicMembers(s, ch, t, [c.ownerId], "added", c.crewId);
        s.threads.push({
          company_id: c.companyId,
          id: nextId(0xc130),
          kind: "topic",
          subject_id: t.id,
          last_message_at: null,
          message_count: 0,
          created_at: when,
          updated_at: when,
        });
      }
      const threadId = threadOfTopic(s, t.id)?.id ?? "";
      const m = crewMessage(c, s, threadId, md, clientId);
      return { message_id: m.id, thread_id: threadId, channel_id: ch.id };
    },
    threadOf(companyId, subjectKind, subjectId) {
      if (subjectKind !== "topic") return null;
      return threadOfTopic(storeOf(companyId), subjectId)?.id ?? null;
    },
    postReply(c, threadId, md, clientId) {
      const s = storeOf(c.companyId);
      return s.threads.some((t) => t.id === threadId)
        ? crewMessage(c, s, threadId, md, clientId).id
        : null;
    },
  };

  /**
   * MEETINGS (fake-cadence.ts): a team meeting's channel (the company's team channel, else its
   * default `#general`), a message to point an issue at, and the meeting summary the end posts
   * as a system message into the topic "Meeting YYYY-MM-DD" (made on first use).
   */
  const meetings: MeetingsChatBridge = {
    teamChannel(companyId) {
      const s = storeOf(companyId);
      const live = s.channels.filter((c) => !c.archived_at && !c.is_private);
      const ch =
        live.find((c) => c.kind === "team") ??
        live.find((c) => c.is_default && c.name === "general") ??
        live[0];
      return ch ? { id: ch.id, name: ch.name } : null;
    },
    channel(companyId, channelId) {
      const ch = storeOf(companyId).channels.find((c) => c.id === channelId);
      return ch ? { id: ch.id, name: ch.name } : null;
    },
    firstMessage(companyId, channelId) {
      const s = storeOf(companyId);
      const topics = new Set(s.topics.filter((t) => t.channel_id === channelId).map((t) => t.id));
      const threads = new Set(
        s.threads.filter((t) => t.kind === "topic" && topics.has(t.subject_id)).map((t) => t.id),
      );
      const m = s.messages.find(
        (x) => threads.has(x.thread_id) && !x.deleted_at && x.author_kind === "person",
      );
      return m ? { id: m.id, text: m.body_text } : null;
    },
    postSummary(companyId, channelId, topicName, md, at) {
      const s = storeOf(companyId);
      const ch = s.channels.find((c) => c.id === channelId);
      if (!ch) return null;
      const when = at ?? stamp();
      let t = s.topics.find((x) => x.channel_id === ch.id && x.name === topicName);
      if (!t) {
        t = {
          company_id: companyId,
          id: nextId(0xc120),
          channel_id: ch.id,
          name: topicName,
          status: "open",
          is_agent_room: false,
          taken_over_by_membership_id: null,
          taken_over_at: null,
          pinned_at: null,
          archived_at: null,
          last_message_at: null,
          message_count: 0,
          is_private: false,
          ...SYSTEM,
          created_at: when,
          updated_at: when,
        };
        s.topics.push(t);
        s.threads.push({
          company_id: companyId,
          id: nextId(0xc130),
          kind: "topic",
          subject_id: t.id,
          last_message_at: null,
          message_count: 0,
          created_at: when,
          updated_at: when,
        });
      }
      const body = markdownToBlocks(md);
      const row: Message = {
        company_id: companyId,
        created_at: when,
        id: nextId(0xc140),
        thread_id: threadOfTopic(s, t.id)?.id ?? "",
        author_membership_id: null,
        body,
        body_text: bodyTextOf(body),
        author_kind: "system",
        mentions: [],
        card_refs: [],
        parent_message_id: null,
        client_id: null,
        edited_at: null,
        deleted_at: null,
        pinned_at: null,
        ...NO_ACTOR,
        actor_kind: "system",
        actor_membership_id: null,
      };
      s.messages.push(row);
      touchThread(s, row);
      return { message_id: row.id, topic_id: t.id, channel_id: ch.id };
    },
  };

  /** Seeds a company's chat (its spaces join the shared array) before Work answers a list. */
  const prime = (companyId: string) => void storeOf(companyId);
  /** The demo's chat runs (cards, agent rooms): Agents mode's run list shows them too. */
  const runsOf = (companyId: string): readonly Run[] => storeOf(companyId).runs;
  /** SHELL-DECISIONS: the memberships in a channel or DM with this one (its chat mates). */
  const matesOf = (companyId: string, membershipId: string): Set<string> => {
    const s = storeOf(companyId);
    const mine = new Set(
      s.members.filter((m) => m.membership_id === membershipId).map((m) => m.channel_id),
    );
    return new Set(s.members.filter((m) => mine.has(m.channel_id)).map((m) => m.membership_id));
  };
  return { handlers, prime, runsOf, crew, meetings, matesOf };
}

/** `TaskDetail` → `Task` (what `make-task` and `POST tasks` answer). */
function stripDetail(t: TaskDetail) {
  const {
    assignee_seat_ids: _a,
    fields: _f,
    checklist: _c,
    blocked: _b,
    checklist_items: _i,
    checklists: _l,
    dependencies: _d,
    field_values: _v,
    assignees: _s,
    ...task
  } = t;
  return task;
}
